// ============================================================================
// Iter 85 — Modul Pengeluaran Gudang (untuk Rido / role gudang, owner, admin)
// 3 seksi:
//   1. Kebutuhan Real-time (GET /api/warehouse/needs)
//   2. Konfirmasi Pengeluaran (POST /api/warehouse/issue, partial support)
//   3. Riwayat Pengeluaran (GET /api/warehouse/history, PATCH cancel)
// Tidak mengubah backend/logic/permission — hanya UI.
// TAMFARIS hitam-gold-cream, responsive HP/tablet/PC, dark mode.
// ============================================================================
import { byShipDeadlineThenWait } from "./bordir_queue_sort";
import { ShopeeCancelTag } from "./shopee_cancel_tag";
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./press_hub.css";
import axios from "axios";
import {
  Warehouse, Search, Truck, Filter, X, CheckCircle2, Ban,
  RefreshCw, PackageSearch, ClipboardList, ChevronRight,
  AlertTriangle, TrendingUp, Calendar, Image as ImgIcon, Download,
  Layers, Sparkles, AlertCircle, FileText, CheckCircle
} from "lucide-react";
import { notify } from "./modal_center";
import { toast } from "sonner";
import { useAutoRefresh, RefreshIndicator } from "./use_auto_refresh";
import { SHOPEE_PROCESS_LABEL, SHOPEE_PROCESSES_WITH_EMBROIDERY } from "./shopee_module";
import { CetakMockupSizeModal, KonfirmasiCetakModal, cetakMockupShopee, unduhMockupShopee, cetakPdfA4P } from "./cetak_mockup";
import { ShopeeBatchPrintBar, BatchSelectBox, batchEligible, PaperBadge, finalCancelled } from "./shopee_batch_print";
import { RidoMoreMenu } from "./rido_desk";
import { RidoCardInfo } from "./rido_card_info";
import { Printer, Camera } from "lucide-react";
// Iter 511 — Reuse scanner existing + modal laporkan kendala konsisten dengan Offline.
import ScanMockupModal from "./scan_mockup";
import { WhNeedsPanel } from "./warehouse_needs_panel";
import { StockCheckBoard } from "./stock_check_board";
import { ShopeeDirectWarehouseSection } from "./shopee_direct_warehouse";
import { TitikInputSheet } from "./bordir_titik_form";
import { fmtPrintedWib, sortPrinted } from "./shopee_printed_sort";
import { ScanFoundBanner, MSG_REVISED, isStaleSnapshot, StaleWarning, STALE_FILE_LABEL } from "./shopee_scan_revision";
import { TokopediaAttachments, useChannel } from "./channel";
import { BordirCancelDialog, priorProgressText } from "./bordir_cancel_dialog";
import { TokopediaDeadline } from "./tokopedia_deadline";
import { MarketplaceBrand } from "./marketplace_brand";
import { BordirSplitPanel, MyBordirTasks } from "./bordir_tasks";
import { DesignPointsDialog } from "./bordir_design_points";
import { ClaimLookup, useClaimSummary } from "./bordir_claims";
import { BordirTeamActions, FinishWithClaim, ResumeScanButton, TeamStatus, teamRole, needsFinishForm } from "./bordir_team";
import { BordirShipBy } from "./shopee_deadline_chip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Kanal dari data pekerjaan (shopee_jobs.channel), bukan tampilan/format nomor.
function ChannelBrand() { return <MarketplaceBrand channel={useChannel()} size="sm"/>; }
export const mpOrderLabel = (job) => (job?.channel === "tokopedia" ? "Pesanan Tokopedia" : "Pesanan Shopee");



// Iter 466 — ErrorBoundary minimal: cegah 1 komponen error menjatuhkan seluruh
//   halaman menjadi blank. Dipakai di tab Shopee Desain Bordir (section-level
//   + card-level) sesuai acceptance test P0.
export class ShopeeErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMsg: "" };
  }
  static getDerivedStateFromError(err) {
    return { hasError: true, errorMsg: String(err?.message || err || "Terjadi kesalahan tak terduga") };
  }
  componentDidCatch(err, info) {
    // eslint-disable-next-line no-console
    console.error("[ShopeeErrorBoundary]", err, info);
  }
  reset = () => this.setState({ hasError: false, errorMsg: "" });
  render() {
    if (!this.state.hasError) return this.props.children;
    const label = this.props.label || "bagian ini";
    return (
      <div className="warn-box small" data-testid={this.props.testId || "shopee-error-boundary"}
        style={{display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, flexWrap:"wrap"}}>
        <span>⚠️ Gagal menampilkan {label}. {this.state.errorMsg}</span>
        <button type="button" className="soft-btn small-btn" onClick={this.reset} data-testid={`${this.props.testId || "shopee-error-boundary"}-retry`}>
          <RefreshCw size={12}/> Coba lagi
        </button>
      </div>
    );
  }
}

// Iter 287l — Helper unduh Shopee terautentikasi: fetch via axios (cookie ikut),
// picu save-as di browser, lalu catat ke backend (mark-downloaded) sebagai bukti
// file diterima aplikasi. Browser TIDAK dapat memastikan file tersimpan ke disk;
// pencatatan ini adalah "diterima app + save dialog dipicu", bukan verifikasi disk.
// Iter 287m — Dipisah menjadi dua tahap: (1) fetch+trigger, (2) record. Kegagalan
// pencatatan TIDAK boleh membuat tombol kembali "Coba Lagi" karena unduh sudah dipicu
// (mis. di iPhone Safari, native Save prompt sering membatalkan request lanjutan).
async function fetchAndTriggerShopeeDownload(file) {
  const fid = file.file_id;
  const r = await axios.get(`${API}/shopee/files/${fid}/download`,
    { responseType: "blob", withCredentials: true });
  const objUrl = URL.createObjectURL(r.data);
  try {
    const a = document.createElement("a");
    a.href = objUrl; a.download = file.filename || `mockup-${fid}`;
    document.body.appendChild(a); a.click(); a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(objUrl), 60_000);
  }
}

async function recordShopeeDownload(jobId, fileId) {
  const r = await axios.post(`${API}/shopee/${jobId}/files/${fileId}/mark-downloaded`, null,
    { withCredentials: true });
  return r.data;
}

// Iter 332.1 — Thumbnail mockup bersama untuk semua kartu Shopee (Rido / Adit / Bordir).
// Iter 376 — Bedakan 3 kondisi: (1) memuat, (2) memang tidak ada mockup
// (thumbUrl=null → snapshot kosong / tidak ada file gambar), (3) ada URL tapi
// gagal dimuat (403/404/network) → tampilkan ikon alert + tombol "Coba Lagi"
// yang memaksa reload img tanpa mengubah status apa pun.
// Prop `onClickPreview` opsional — klik thumbnail (saat image OK) membuka
// preview besar (biasanya tab baru / modal) tanpa mengubah status transaksi.
// Iter 519 — Timeout 15 detik anti stuck "Memuat…" pada koneksi lambat.
//   Timer hanya aktif setelah elemen masuk viewport (fetch img lazy benar-
//   benar dimulai). Clear otomatis pada onLoad/onError. Timeout → state
//   "failed" + tombol "Coba Lagi" (hanya reload thumbnail terkait).
export function ShopeeMockupThumb({ thumbUrl, fallbackUrls, iconSize = 22, testId, onClickPreview }) {
  const [state, setState] = React.useState("loading"); // loading | ok | failed | empty
  // Iter 472 — Cache-buster nonce HANYA naik saat user klik "Coba Lagi".
  //   Sebelumnya nonce bump setiap thumbUrl berubah → URL berbeda per render →
  //   browser tidak reuse cache → thumbnail re-fetch terus menerus (termasuk
  //   saat silent auto-refresh). Sekarang URL stabil → browser cache (24 jam
  //   via Cache-Control thumbnail endpoint) bekerja optimal.
  const [retryNonce, setRetryNonce] = React.useState(0);
  // Iter 484 — Fallback ke snapshot alternatif. Index 0 = primary thumbUrl;
  //   index ≥1 = fallbackUrls[index-1]. Image gagal → coba kandidat berikutnya
  //   sebelum menampilkan "Gagal muat". Tombol Coba Lagi reset ke primary.
  const [altIndex, setAltIndex] = React.useState(0);
  const wrapRef = React.useRef(null);
  const timerRef = React.useRef(null);
  // Normalise fallbackUrls (prop defaults ke array kosong). Pakai join sebagai
  //   dependency key supaya reset state hanya terjadi ketika kandidat benar-
  //   benar berubah (bukan tiap render parent).
  const fbKey = React.useMemo(() => (fallbackUrls || []).join("|"), [fallbackUrls]);
  React.useEffect(() => {
    setState(thumbUrl ? "loading" : "empty");
    setAltIndex(0);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [thumbUrl, fbKey]);
  const activeUrl = altIndex === 0
    ? thumbUrl
    : (fallbackUrls && fallbackUrls[altIndex - 1]) || null;
  const effUrl = activeUrl
    ? (retryNonce > 0 ? `${activeUrl}${activeUrl.includes("?") ? "&" : "?"}_r=${retryNonce}` : activeUrl)
    : null;
  const clearTimer = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  };
  const onImgError = () => {
    clearTimer();
    // Iter 484 — Pindah ke kandidat berikutnya tanpa menandai failed dulu.
    const hasNext = altIndex < ((fallbackUrls || []).length);
    if (hasNext) {
      setAltIndex(altIndex + 1);
      setState("loading");
    } else {
      setState("failed");
    }
  };
  const retry = (e) => {
    if (e) e.stopPropagation();
    clearTimer();
    setState("loading");
    setAltIndex(0); // Mulai lagi dari primary → URL stabil → cache valid.
    setRetryNonce(n => n + 1);
  };
  // Iter 519 — IntersectionObserver menjalankan timer 15 detik HANYA bila
  //   wrapper sudah berada di viewport. Mencegah thumbnail yang masih
  //   menunggu lazy load (jauh di bawah) dianggap "gagal" prematurely.
  React.useEffect(() => {
    const el = wrapRef.current;
    if (state !== "loading" || !effUrl) { clearTimer(); return; }
    if (!el || typeof IntersectionObserver === "undefined") {
      clearTimer();
      timerRef.current = setTimeout(() => {
        setState(s => s === "loading" ? "failed" : s);
      }, 15000);
      return () => clearTimer();
    }
    const io = new IntersectionObserver((entries) => {
      const vis = entries.some(e => e.isIntersecting);
      if (vis && !timerRef.current) {
        timerRef.current = setTimeout(() => {
          setState(s => s === "loading" ? "failed" : s);
        }, 15000);
      } else if (!vis) {
        clearTimer();
      }
    }, { rootMargin: "100px" });
    io.observe(el);
    return () => { try { io.disconnect(); } catch {} clearTimer(); };
  }, [state, effUrl]);
  const clickable = state === "ok" && !!onClickPreview;
  const onThumbClick = clickable
    ? (e) => { e.stopPropagation(); onClickPreview(thumbUrl); }
    : undefined;
  return <div ref={wrapRef} className="shopee-thumb shopee-thumb-warm shopee-wh-thumb"
    data-testid={testId || undefined}
    onClick={onThumbClick}
    role={clickable ? "button" : undefined}
    tabIndex={clickable ? 0 : undefined}
    onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClickPreview(thumbUrl); } } : undefined}
    title={clickable ? "Klik untuk melihat mockup besar" : undefined}
    style={clickable ? { cursor: "zoom-in" } : undefined}>
    {effUrl && state !== "failed" && (
      <img key={effUrl} src={effUrl} alt="thumb" loading="lazy" decoding="async"
        onLoad={() => { clearTimer(); setState("ok"); }}
        onError={onImgError}
        style={{width:"100%", height:"100%", objectFit:"contain",
                 position: state === "loading" ? "absolute" : "static",
                 opacity: state === "loading" ? 0 : 1,
                 pointerEvents: state === "loading" ? "none" : "auto"}}/>
    )}
    {effUrl && state === "loading" && (
      <div className="shopee-wh-thumb-empty" data-testid={testId ? `${testId}-loading` : undefined}>
        <ImgIcon size={iconSize} className="shopee-thumb-icon" style={{opacity:.5}}/>
        <span className="shopee-wh-thumb-empty-label" style={{opacity:.7}}>Memuat…</span>
      </div>
    )}
    {state === "failed" && (
      <button type="button" className="shopee-wh-thumb-empty shopee-wh-thumb-failed"
        onClick={retry}
        title="Gagal memuat thumbnail — klik untuk coba lagi"
        data-testid={testId ? `${testId}-failed` : undefined}
        style={{border:"none", background:"transparent", cursor:"pointer", padding:0}}>
        <AlertCircle size={iconSize} style={{color:"#dc2626"}}/>
        <span className="shopee-wh-thumb-empty-label" style={{color:"#dc2626"}}>
          Gagal muat
        </span>
        <span className="shopee-wh-thumb-empty-label" style={{fontSize:10, textDecoration:"underline", opacity:.9}}>
          Coba Lagi
        </span>
      </button>
    )}
    {!effUrl && state === "empty" && (
      <div className="shopee-wh-thumb-empty">
        <ImgIcon size={iconSize} className="shopee-thumb-icon"/>
        <span className="shopee-wh-thumb-empty-label">Belum ada mockup</span>
      </div>
    )}
  </div>;
}

// Ulangi pencatatan saja (tanpa mengunduh ulang file) hingga sukses atau menyerah.
async function recordShopeeDownloadWithRetry(jobId, fileId, attempts = 4) {
  const delays = [1500, 3000, 6000, 12000];
  for (let i = 0; i < attempts; i++) {
    try { await recordShopeeDownload(jobId, fileId); return true; }
    catch { /* fallthrough → tidur, ulangi */ }
    if (i < attempts - 1) await new Promise(res => setTimeout(res, delays[i] || 5000));
  }
  return false;
}

async function parseAxiosErrMsg(e) {
  const status = e?.response?.status;
  let msg = "";
  try {
    if (e?.response?.data instanceof Blob) {
      const t = await e.response.data.text();
      try { msg = JSON.parse(t)?.detail || t; } catch { msg = t; }
    } else msg = e?.response?.data?.detail || "";
  } catch { /* ignore */ }
  if (status === 401) return "Sesi Anda kedaluwarsa. Silakan login ulang sebagai operator Gudang.";
  if (status === 423) return msg || "Klik 'Proses Pesanan' terlebih dahulu.";
  return msg || "Gagal mengunduh. Coba lagi.";
}

function downloadsMapOf(job) {
  const m = {};
  for (const d of (job?.warehouse_downloads || [])) m[d.file_id] = d;
  return m;
}
function isAllDownloaded(job, optimistic) {
  const snap = job?.warehouse_sent_snapshot || [];
  if (snap.length === 0) return false;
  const m = downloadsMapOf(job);
  return snap.every(s => !!m[s.file_id] || (optimistic && optimistic.has && optimistic.has(s.file_id)));
}

function currency(n) {
  return new Intl.NumberFormat("id-ID").format(n || 0);
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth()+1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return iso; }
}

export default function WarehousePage({ user, onBack }) {
  const [section, setSection] = useState("needs"); // needs | history | shopee
  const canCancel = ["owner", "admin", "admin_kantor"].includes(user?.role || "");

  return (
    <main className="content" data-testid="warehouse-page">
      <div className="welcome">
        <div>
          <p className="eyebrow">
            {new Date().toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", timeZone: "Asia/Jakarta" })}
          </p>
          <h1><Warehouse size={26} style={{verticalAlign:"-4px",marginRight:8,color:"#a5842f"}}/>Pengeluaran Gudang</h1>
          <p className="muted">Pantau kebutuhan real-time dari pesanan aktif, konfirmasi pengeluaran (parsial diperbolehkan), dan audit riwayat pengeluaran.</p>
        </div>
        <div className="welcome-actions">
          <button type="button" className="soft-btn" data-testid="warehouse-back-btn" onClick={onBack}>← Kembali</button>
        </div>
      </div>

      <nav className="wh-section-tabs" role="tablist" data-testid="wh-section-tabs">
        <button
          type="button"
          role="tab"
          className={`wh-section-tab ${section==="needs"?"active":""}`}
          data-testid="wh-tab-needs"
          onClick={()=>setSection("needs")}
        >
          <PackageSearch size={16}/>
          <span>Kebutuhan Real-time</span>
        </button>
        <button
          type="button"
          role="tab"
          className={`wh-section-tab ${section==="history"?"active":""}`}
          data-testid="wh-tab-history"
          onClick={()=>setSection("history")}
        >
          <ClipboardList size={16}/>
          <span>Riwayat Pengeluaran</span>
        </button>
        {/* Iter 287f — Tab Mockup Shopee: kartu-kartu yang dikirim dari modul Shopee.
            Tidak mengubah alur Kebutuhan/Riwayat pengeluaran offline. */}
        <button
          type="button"
          role="tab"
          className={`wh-section-tab ${section==="shopee"?"active":""}`}
          data-testid="wh-tab-shopee"
          onClick={()=>setSection("shopee")}
        >
          <ChannelBrand/>
          <span>Mockup</span>
        </button>
        <button
          type="button"
          role="tab"
          className={`wh-section-tab ${section==="direct"?"active":""}`}
          data-testid="wh-tab-direct"
          onClick={()=>setSection("direct")}
        >
          <Layers size={16}/>
          <span>Pesanan Tanpa Mockup</span>
        </button>
      </nav>

      {section === "needs" && <NeedsSection user={user}/>}
      {section === "history" && <HistorySection user={user} canCancel={canCancel}/>}
      {section === "shopee" && <ShopeeWarehouseSection user={user}/>}
      {section === "direct" && <ShopeeDirectWarehouseSection/>}
    </main>
  );
}

// ============================================================================
// 1. Kebutuhan Real-time + inline Konfirmasi Pengeluaran (partial)
// ============================================================================
function NeedsSection({ user }) {
  const [needs, setNeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [destFilter, setDestFilter] = useState("");
  const [issueTarget, setIssueTarget] = useState(null); // needs item saat form dispatch dibuka

  const loadErrRef = React.useRef(null);
  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/warehouse/needs`, { withCredentials: true });
      setNeeds(Array.isArray(r.data) ? r.data : []);
      loadErrRef.current = null;
    } catch (e) {
      notify.error({ title: "Gagal memuat kebutuhan", message: e?.response?.data?.detail || e.message });
      loadErrRef.current = e;
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  // Iter 141 — Auto-refresh 30 dtk, tombol manual + last-updated.
  const auto = useAutoRefresh({
    enabled: true,
    intervalMs: 30000,
    onRefresh: async () => {
      await load();
      if (loadErrRef.current) { const e = loadErrRef.current; loadErrRef.current = null; throw e; }
    },
  });

  const destinations = useMemo(() => Array.from(new Set(needs.map(n=>n.destination).filter(Boolean))), [needs]);
  const filtered = useMemo(() => {
    return needs.filter(n => {
      if (destFilter && n.destination !== destFilter) return false;
      if (!query.trim()) return true;
      const q = query.toLowerCase();
      return (
        (n.order_number||"").toLowerCase().includes(q) ||
        (n.customer_name||"").toLowerCase().includes(q) ||
        (n.product_name||"").toLowerCase().includes(q) ||
        (n.color||"").toLowerCase().includes(q)
      );
    });
  }, [needs, query, destFilter]);

  // Ringkasan kebutuhan
  const totalRemaining = filtered.reduce((s, n) => s + (n.remaining||0), 0);
  const totalLines = filtered.length;
  const uniqueOrders = new Set(filtered.map(n => n.order_number)).size;

  return (
    <div className="section" data-testid="wh-needs-section">
      {/* Ringkasan */}
      <div className="wh-summary-grid" data-testid="wh-needs-summary">
        <div className="wh-summary-tile">
          <span className="wh-summary-label">Baris Kebutuhan</span>
          <span className="wh-summary-value" data-testid="wh-summary-lines">{totalLines}</span>
        </div>
        <div className="wh-summary-tile wh-summary-primary">
          <span className="wh-summary-label">Total Sisa (pcs)</span>
          <span className="wh-summary-value" data-testid="wh-summary-remaining">{currency(totalRemaining)}</span>
        </div>
        <div className="wh-summary-tile">
          <span className="wh-summary-label">Nota Aktif</span>
          <span className="wh-summary-value" data-testid="wh-summary-orders">{uniqueOrders}</span>
        </div>
      </div>

      {/* Filter bar */}
      <div className="wh-filter-bar">
        <div className="wh-search-wrap">
          <Search size={16}/>
          <input
            type="text"
            data-testid="wh-search-input"
            value={query}
            onChange={e=>setQuery(e.target.value)}
            placeholder="Cari nota, pelanggan, produk, warna…"
            className="wh-search-input"
          />
        </div>
        <select
          data-testid="wh-dest-filter"
          value={destFilter}
          onChange={e=>setDestFilter(e.target.value)}
          className="wh-select"
        >
          <option value="">Semua Tujuan</option>
          {destinations.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <button type="button" className="soft-btn" data-testid="wh-refresh-btn" onClick={load} disabled={loading}>
          <RefreshCw size={14} className={loading?"spin":""}/> Refresh
        </button>
      </div>
      <div style={{margin:"6px 0 4px"}}>
        <RefreshIndicator
          testId="wh-refresh-ind"
          lastUpdated={auto.lastUpdated}
          isRefreshing={auto.isRefreshing}
          error={auto.error}
          onRefresh={auto.refresh}
          compact
        />
      </div>

      {loading ? (
        <p className="muted small">Memuat kebutuhan…</p>
      ) : filtered.length === 0 ? (
        <div className="op-empty" data-testid="wh-empty">
          <div className="op-empty-icon"><CheckCircle2 size={32}/></div>
          <h4 className="op-empty-title">Semua kebutuhan sudah dikeluarkan</h4>
          <p className="op-empty-msg">Tidak ada baris kebutuhan yang perlu dikeluarkan saat ini. Kebutuhan akan muncul otomatis ketika ada Nota aktif yang lewat Gudang.</p>
        </div>
      ) : (
        <div className="wh-needs-list" data-testid="wh-needs-list">
          {filtered.map((n, i) => (
            <NeedCard
              key={`${n.order_number}-${n.product_id}-${n.color}-${n.size}-${i}`}
              item={n}
              onIssue={() => setIssueTarget(n)}
              testId={`wh-need-${i}`}
            />
          ))}
        </div>
      )}

      {issueTarget && (
        <IssueDispatchModal
          item={issueTarget}
          onClose={() => setIssueTarget(null)}
          onDone={() => { setIssueTarget(null); load(); }}
        />
      )}
    </div>
  );
}

function NeedCard({ item, onIssue, testId }) {
  const pct = item.need_qty > 0 ? Math.min(100, Math.round((item.issued_qty / item.need_qty) * 100)) : 0;
  return (
    <article className="wh-need-card" data-testid={testId}>
      <div className="wh-need-head">
        <div className="wh-need-head-left">
          <span className="wh-need-nota">{item.order_number}</span>
          <strong className="wh-need-customer">{item.customer_name || "—"}</strong>
        </div>
        <div className="wh-need-dest">
          <Truck size={12}/> {item.destination}
        </div>
      </div>
      <div className="wh-need-body">
        <div className="wh-need-title">
          <strong>{item.product_name}</strong>
          <div className="wh-need-chips">
            {item.material && <span className="queue-chip queue-chip-material">Bahan: <strong>{item.material}</strong></span>}
            {item.color && <span className="queue-chip queue-chip-color">Warna: <strong>{item.color}</strong></span>}
            {item.size && <span className="queue-chip">Size: <strong>{item.size}</strong></span>}
          </div>
        </div>
        <div className="wh-need-progress">
          <div className="wh-need-progress-bar">
            <div className="wh-need-progress-fill" style={{width: `${pct}%`}}/>
          </div>
          <div className="wh-need-progress-labels">
            <span className="muted small">Sudah keluar: <strong>{item.issued_qty}</strong> / {item.need_qty} pcs</span>
            <span className="wh-need-remaining" data-testid={`${testId}-remaining`}>Sisa: <strong>{item.remaining} pcs</strong></span>
          </div>
        </div>
      </div>
      <div className="wh-need-foot">
        <span className="muted small">
          <Calendar size={11} style={{verticalAlign:"-1px",marginRight:2}}/>
          Deadline: <strong>{item.deadline || "—"}</strong> · Tahap: <strong>{item.stage || "—"}</strong>
        </span>
        <button
          type="button"
          className="primary small-btn"
          data-testid={`${testId}-issue-btn`}
          onClick={onIssue}
        >
          Keluarkan <ChevronRight size={14}/>
        </button>
      </div>
    </article>
  );
}

// ============================================================================
// Modal Konfirmasi Pengeluaran (support parsial via qty < remaining)
// ============================================================================
function IssueDispatchModal({ item, onClose, onDone }) {
  const [qty, setQty] = useState(item.remaining);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const q = parseInt(qty, 10);
    if (!q || q <= 0) {
      notify.warning({ title: "Qty tidak valid", message: "Masukkan qty > 0" });
      return;
    }
    if (q > item.remaining) {
      notify.warning({ title: "Melebihi sisa", message: `Sisa maksimum: ${item.remaining} pcs` });
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/warehouse/issue`, {
        order_number: item.order_number,
        product_id: item.product_id,
        color: item.color || "",
        size: item.size || "",
        qty: q,
        destination: item.destination || "",
        note: note.trim(),
      }, { withCredentials: true });
      notify.success({
        title: "Pengeluaran berhasil",
        message: `${q} pcs ${item.product_name} ${item.color?`(${item.color})`:""} telah dikeluarkan ke ${item.destination}.`,
      });
      onDone();
    } catch (e) {
      notify.error({ title: "Gagal keluarkan barang", message: e?.response?.data?.detail || e.message });
      setBusy(false);
    }
  };
  const isPartial = parseInt(qty, 10) < item.remaining;
  return (
    <div className="modal-overlay" data-testid="wh-issue-overlay" onClick={onClose}>
      <div className="modal wh-issue-modal" onClick={e=>e.stopPropagation()} data-testid="wh-issue-modal">
        <div className="modal-head">
          <h3><Truck size={18} style={{verticalAlign:"-3px",marginRight:6,color:"#a5842f"}}/>Konfirmasi Pengeluaran</h3>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Tutup"><X size={18}/></button>
        </div>
        <div className="wh-issue-info">
          <p style={{margin:"2px 0"}}>Nota: <strong>{item.order_number}</strong> · <strong>{item.customer_name}</strong></p>
          <p style={{margin:"2px 0"}}>Produk: <strong>{item.product_name}</strong>{item.material?` · ${item.material}`:""}{item.color?` · ${item.color}`:""}{item.size?` · ${item.size}`:""}</p>
          <p style={{margin:"2px 0"}}>Tujuan: <strong>{item.destination}</strong> · Sisa: <strong>{item.remaining} pcs</strong> · Deadline: <strong>{item.deadline || "—"}</strong></p>
        </div>
        <label className="field-label" style={{marginTop:14,display:"block"}}>Qty yang dikeluarkan <span className="required">*</span></label>
        <div style={{display:"flex",gap:8,alignItems:"center",marginTop:6}}>
          <input
            type="number"
            data-testid="wh-issue-qty"
            value={qty}
            onChange={e=>setQty(e.target.value)}
            min={1}
            max={item.remaining}
            style={{flex:1,fontFamily:"'Plus Jakarta Sans', monospace",fontSize:16,fontWeight:700,textAlign:"right",padding:"10px 12px"}}
            disabled={busy}
          />
          <span className="muted">/ {item.remaining} pcs</span>
        </div>
        <div style={{display:"flex",gap:6,marginTop:8}}>
          <button type="button" className="chip-btn" data-testid="wh-issue-qty-half" onClick={()=>setQty(Math.ceil(item.remaining/2))} disabled={busy}>½</button>
          <button type="button" className="chip-btn" data-testid="wh-issue-qty-full" onClick={()=>setQty(item.remaining)} disabled={busy}>Semua ({item.remaining})</button>
        </div>
        {isPartial && (
          <div className="wh-issue-partial-hint" data-testid="wh-issue-partial-hint">
            <AlertTriangle size={13}/> Pengeluaran parsial: masih ada <strong>{item.remaining - parseInt(qty||0,10)} pcs</strong> yang menyisa untuk dikeluarkan nanti.
          </div>
        )}
        <label className="field-label" style={{marginTop:14,display:"block"}}>Catatan (opsional)</label>
        <textarea
          data-testid="wh-issue-note"
          value={note}
          onChange={e=>setNote(e.target.value)}
          style={{width:"100%",marginTop:6,minHeight:60}}
          rows={2}
          placeholder="Mis. batch pertama, size L saja, dll."
          disabled={busy}
        />
        <div className="modal-foot" style={{marginTop:16}}>
          <button type="button" className="soft-btn" data-testid="wh-issue-cancel" onClick={onClose} disabled={busy}>Batal</button>
          <button type="button" className="primary" data-testid="wh-issue-submit" onClick={submit} disabled={busy}>
            {busy ? "Memproses…" : `Keluarkan ${qty || 0} pcs`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// 3. Riwayat Pengeluaran (filter periode + cancel)
// ============================================================================
function HistorySection({ user, canCancel }) {
  const [range, setRange] = useState("week");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [data, setData] = useState({ items: [], summary: null });
  const [loading, setLoading] = useState(true);
  const [cancelTarget, setCancelTarget] = useState(null);

  const loadErrRef = React.useRef(null);
  const load = async () => {
    setLoading(true);
    try {
      const params = { range };
      if (range === "custom" && customStart && customEnd) {
        params.start = customStart; params.end = customEnd;
      }
      const r = await axios.get(`${API}/warehouse/history`, { params, withCredentials: true });
      setData(r.data || { items: [], summary: null });
      loadErrRef.current = null;
    } catch (e) {
      notify.error({ title: "Gagal memuat riwayat", message: e?.response?.data?.detail || e.message });
      loadErrRef.current = e;
    }
    setLoading(false);
  };
  useEffect(() => {
    if (range !== "custom" || (customStart && customEnd)) load();
    // eslint-disable-next-line
  }, [range, customStart, customEnd]);
  // Iter 141 — Auto-refresh 30 dtk; hanya jika bukan custom-tanpa-tanggal.
  const autoEnabled = range !== "custom" || (!!customStart && !!customEnd);
  const auto = useAutoRefresh({
    enabled: autoEnabled,
    intervalMs: 30000,
    onRefresh: async () => {
      await load();
      if (loadErrRef.current) { const e = loadErrRef.current; loadErrRef.current = null; throw e; }
    },
    deps: [range, customStart, customEnd],
  });

  const s = data.summary || {};
  const items = data.items || [];

  return (
    <div className="section" data-testid="wh-history-section">
      {/* Range picker */}
      <div className="wh-range-bar" data-testid="wh-range-bar">
        {[
          {key:"today", label:"Hari Ini"},
          {key:"week", label:"Minggu Ini"},
          {key:"month", label:"Bulan Ini"},
          {key:"year", label:"Tahun Ini"},
          {key:"custom", label:"Kustom"},
        ].map(r => (
          <button
            key={r.key}
            type="button"
            className={`wh-range-btn ${range===r.key?"active":""}`}
            data-testid={`wh-range-${r.key}`}
            onClick={()=>setRange(r.key)}
          >{r.label}</button>
        ))}
        {range === "custom" && (
          <div className="wh-range-custom">
            <input type="date" data-testid="wh-range-start" value={customStart} onChange={e=>setCustomStart(e.target.value)}/>
            <span>—</span>
            <input type="date" data-testid="wh-range-end" value={customEnd} onChange={e=>setCustomEnd(e.target.value)}/>
          </div>
        )}
        <button type="button" className="soft-btn" data-testid="wh-history-refresh" onClick={load} disabled={loading}>
          <RefreshCw size={14} className={loading?"spin":""}/> Refresh
        </button>
      </div>
      <div style={{margin:"6px 0 4px"}}>
        <RefreshIndicator
          testId="wh-history-refresh-ind"
          lastUpdated={auto.lastUpdated}
          isRefreshing={auto.isRefreshing}
          error={auto.error}
          onRefresh={auto.refresh}
          compact
        />
      </div>

      {/* Summary */}
      <div className="wh-summary-grid" data-testid="wh-history-summary">
        <div className="wh-summary-tile wh-summary-primary">
          <span className="wh-summary-label">Total Pcs</span>
          <span className="wh-summary-value" data-testid="wh-history-total-pcs">{currency(s.total_pcs||0)}</span>
        </div>
        <div className="wh-summary-tile">
          <span className="wh-summary-label">Transaksi</span>
          <span className="wh-summary-value" data-testid="wh-history-trx">{s.trx_count||0}</span>
        </div>
        <div className="wh-summary-tile">
          <span className="wh-summary-label">Nota</span>
          <span className="wh-summary-value" data-testid="wh-history-orders">{(s.orders||[]).length}</span>
        </div>
      </div>

      {/* Aggregate breakdowns */}
      {s.total_pcs > 0 && (
        <div className="wh-breakdown-grid">
          <BreakdownCard title="Per Produk" icon={<TrendingUp size={14}/>} rows={s.per_product||[]} testId="wh-bd-product"/>
          <BreakdownCard title="Per Warna" rows={s.per_color||[]} testId="wh-bd-color"/>
          <BreakdownCard title="Per Size" rows={s.per_size||[]} testId="wh-bd-size"/>
          <BreakdownCard title="Per Tujuan" icon={<Truck size={14}/>} rows={s.per_destination||[]} testId="wh-bd-destination"/>
        </div>
      )}

      {/* Transactions */}
      {loading ? (
        <p className="muted small">Memuat riwayat…</p>
      ) : items.length === 0 ? (
        <div className="op-empty" data-testid="wh-history-empty">
          <div className="op-empty-icon"><ClipboardList size={30}/></div>
          <h4 className="op-empty-title">Belum ada transaksi</h4>
          <p className="op-empty-msg">Belum ada pengeluaran pada periode ini. Coba pilih rentang waktu lain atau lakukan pengeluaran dari tab <strong>Kebutuhan Real-time</strong>.</p>
        </div>
      ) : (
        <ul className="wh-tx-list" data-testid="wh-tx-list">
          {items.map((t, i) => (
            <li key={t.id || i} className={`wh-tx-item ${t.is_cancelled?"is-cancelled":""}`} data-testid={`wh-tx-${i}`}>
              <div className="wh-tx-main">
                <div className="wh-tx-head">
                  <span className="wh-tx-nota">{t.order_number}</span>
                  <strong className="wh-tx-customer">{t.customer_name || "—"}</strong>
                  {t.is_cancelled && <span className="wh-tx-cancelled-chip"><Ban size={11}/> DIBATALKAN</span>}
                </div>
                <div className="wh-tx-body">
                  <span className="wh-tx-product">{t.product_name}</span>
                  <span className="wh-tx-details muted small">
                    {t.color && `${t.color} · `}
                    {t.size && `${t.size} · `}
                    → <strong>{t.destination}</strong>
                  </span>
                  <span className="wh-tx-meta muted small">
                    {fmtDateTime(t.created_at)} · Oleh {t.operator_name || "—"}
                    {t.note && <> · <em>“{t.note}”</em></>}
                  </span>
                  {t.is_cancelled && (
                    <span className="wh-tx-cancel-info small">
                      Dibatalkan {fmtDateTime(t.cancelled_at)} oleh {t.cancelled_by || "—"}
                      {t.cancel_reason && <> · alasan: <em>{t.cancel_reason}</em></>}
                    </span>
                  )}
                </div>
              </div>
              <div className="wh-tx-side">
                <span className="wh-tx-qty" data-testid={`wh-tx-${i}-qty`}>{t.qty} pcs</span>
                {canCancel && !t.is_cancelled && (
                  <button
                    type="button"
                    className="text-btn small-btn wh-tx-cancel-btn"
                    data-testid={`wh-tx-${i}-cancel`}
                    onClick={()=>setCancelTarget(t)}
                  >
                    <Ban size={13}/> Batalkan
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {cancelTarget && (
        <CancelDispatchModal
          tx={cancelTarget}
          onClose={()=>setCancelTarget(null)}
          onDone={()=>{ setCancelTarget(null); load(); }}
        />
      )}
    </div>
  );
}

function BreakdownCard({ title, icon, rows, testId }) {
  const sortedRows = [...(rows||[])].sort((a,b) => (b.qty||0) - (a.qty||0)).slice(0, 8);
  const max = Math.max(1, ...sortedRows.map(r => r.qty||0));
  return (
    <div className="wh-breakdown-card" data-testid={testId}>
      <div className="wh-breakdown-head">{icon}<span>{title}</span></div>
      {sortedRows.length === 0 ? (
        <p className="muted small">—</p>
      ) : (
        <ul className="wh-breakdown-list">
          {sortedRows.map((r, i) => (
            <li key={`${r.key}-${i}`} className="wh-breakdown-row">
              <span className="wh-breakdown-label" title={r.key}>{r.key || "-"}</span>
              <div className="wh-breakdown-bar-wrap">
                <div className="wh-breakdown-bar" style={{width: `${(r.qty||0)/max*100}%`}}/>
              </div>
              <span className="wh-breakdown-qty">{currency(r.qty||0)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CancelDispatchModal({ tx, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) {
      notify.warning({ title: "Alasan wajib diisi", message: "Mohon jelaskan alasan pembatalan." });
      return;
    }
    setBusy(true);
    try {
      await axios.patch(`${API}/warehouse/issue/${tx.id}/cancel`, { reason: reason.trim() }, { withCredentials: true });
      notify.success({ title: "Transaksi dibatalkan", message: `${tx.qty} pcs ${tx.product_name} telah dibatalkan.` });
      onDone();
    } catch (e) {
      notify.error({ title: "Gagal membatalkan", message: e?.response?.data?.detail || e.message });
      setBusy(false);
    }
  };
  return (
    <div className="modal-overlay" data-testid="wh-cancel-overlay" onClick={onClose}>
      <div className="modal wh-cancel-modal" onClick={e=>e.stopPropagation()} data-testid="wh-cancel-modal">
        <div className="modal-head">
          <h3><Ban size={18} style={{verticalAlign:"-3px",marginRight:6,color:"#b91c1c"}}/>Batalkan Pengeluaran</h3>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Tutup"><X size={18}/></button>
        </div>
        <p className="muted small">
          Nota <strong>{tx.order_number}</strong> · {tx.product_name} · <strong>{tx.qty} pcs</strong> → {tx.destination}
        </p>
        <p className="wh-cancel-warn">
          <AlertTriangle size={14}/> Pembatalan akan mengembalikan qty ke daftar kebutuhan. Aksi ini tercatat di audit log.
        </p>
        <label className="field-label" style={{marginTop:12,display:"block"}}>Alasan pembatalan <span className="required">*</span></label>
        <textarea
          data-testid="wh-cancel-reason"
          value={reason}
          onChange={e=>setReason(e.target.value)}
          style={{width:"100%",marginTop:6,minHeight:80}}
          rows={3}
          placeholder="Mis. salah warna, salah nota, dikembalikan operator, dll."
          disabled={busy}
        />
        <div className="modal-foot" style={{marginTop:16}}>
          <button type="button" className="soft-btn" data-testid="wh-cancel-back" onClick={onClose} disabled={busy}>Kembali</button>
          <button type="button" className="danger" data-testid="wh-cancel-submit" onClick={submit} disabled={busy}>
            {busy ? "Membatalkan…" : "Konfirmasi Batal"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Iter 287f — Shopee Warehouse Section
// Iter 287h — Diexport supaya dapat dipakai dari ProductionQueue (halaman Rido)
// sebagai sub-source "Shopee" di dalam Pesanan Gudang.
// ============================================================================
export function ShopeeWarehouseSection({ user, hideCancelledToggle = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [openId, setOpenId] = useState(null);
  // Iter 287j — state untuk aksi langsung di kartu.
  const [markingId, setMarkingId] = useState(null);
  const [pickerJob, setPickerJob] = useState(null); // { job, mode: "preview"|"download"|"cetak" }
  // Iter 374 — Modal pilihan A5/A4 + konfirmasi tandai cetak (reuse cetak_mockup.js).
  const [sizeModal, setSizeModal] = useState(null); // { fileId, orderNumber, label, job? }
  const afterPrintRef = useRef(null);
  const [confirmPrintModal, setConfirmPrintModal] = useState(null); // { job }
  // Iter 374 — Modal per-file untuk pesanan multi-mockup. Mark setiap file terpisah;
  //   job-level mockup_printed_at di backend hanya set saat semua file printed.
  const [perFileMarkModal, setPerFileMarkModal] = useState(null); // { job }
  // Iter 374 — Filter chip Belum/Sudah Dicetak + pencarian lokal.
  const [printFilter, setPrintFilter] = useState("belum"); // "belum" | "sudah"
  const [searchQ, setSearchQ] = useState("");
  // Iter 287k — Preview via axios (cookie ikut) → tampilkan di modal in-app.
  // Menghindari window.open ke endpoint terproteksi yang gagal auth di tab baru.
  const [previewFile, setPreviewFile] = useState(null); // { file_id, filename, title, content_type }
  // Iter 287l — Unduh terautentikasi + pencatatan per-file.
  // Iter 287m — Optimistik: setelah fetch+trigger sukses, langsung tandai done.
  // Kegagalan pencatatan diretry di background, tidak mengembalikan tombol ke "Coba Lagi".
  const [dlBusyIds, setDlBusyIds] = useState(() => new Set());
  const [dlErrsById, setDlErrsById] = useState({});
  const [dlOptimisticIds, setDlOptimisticIds] = useState(() => new Set());
  // Iter 287v — Persetujuan/penolakan pengajuan dari desainer.
  const [rejectFor, setRejectFor] = useState(null);
  const handleApprovePending = async (job, pending) => {
    const label = pending.kind === "hold" ? "Tahan/Revisi" : "Batal";
    const msg = pending.kind === "hold"
      ? `Konfirmasi setuju Tahan/Revisi untuk ${job.order_number}? Anda menyatakan proses fisik SUDAH DIHENTIKAN. Kartu akan dibuka untuk revisi desainer.`
      : `Setujui BATAL untuk ${job.order_number}? Pekerjaan akan keluar dari antrean aktif dan tidak dapat dikembalikan.`;
    const ok = await notify.confirm({
      title: `Setujui ${label}?`,
      message: msg,
      primaryLabel: `Ya, setujui ${label}`,
      secondaryLabel: "Batal",
      tone: pending.kind === "hold" ? "warning" : "danger",
    });
    if (!ok) return;
    try {
      await axios.post(`${API}/shopee/${job.id}/pending-request/approve`, null, { withCredentials: true });
      await load();
      notify.success({ title: "Pengajuan disetujui", message: `${label} · ${job.order_number}` });
    } catch (e) {
      notify.error({ title: "Gagal menyetujui", message: e?.response?.data?.detail || e.message || "Kesalahan tidak diketahui." });
    }
  };
  const handleRejectPending = async (job, reason) => {
    try {
      await axios.post(`${API}/shopee/${job.id}/pending-request/reject`, { reason }, { withCredentials: true });
      setRejectFor(null);
      await load();
      notify.success({ title: "Pengajuan ditolak", message: `${job.order_number} — pekerjaan tetap terkunci.` });
    } catch (e) {
      notify.error({ title: "Gagal menolak", message: e?.response?.data?.detail || e.message || "Kesalahan tidak diketahui." });
      throw e;
    }
  };
  // Iter 287i — Rido: cancelled TIDAK di antrean aktif, tapi tampil sebagai notifikasi.
  // Selalu fetch termasuk cancelled agar dapat memberitahu pembatalan (7 hari terakhir).
  const alwaysIncludeCancelled = hideCancelledToggle;

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const inc = alwaysIncludeCancelled || includeCancelled;
      const r = await axios.get(`${API}/shopee/warehouse/queue`, { params: inc ? { include_cancelled: true } : {}, withCredentials: true });
      setItems(r.data.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Gagal memuat antrean Shopee");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeCancelled]);
  // Iter 396 — Fix signature: sebelum ini `useAutoRefresh(load, 30_000)` mengirim
  //   function sebagai options object → `onRefresh=undefined` → auto-refresh
  //   antrean Rido Shopee tidak pernah jalan. Diselaraskan dgn panggilan lain di
  //   file ini.
  useAutoRefresh({ enabled: true, intervalMs: 30000, onRefresh: load });

  // Iter 287j — mark processing dari kartu (endpoint sama dengan detail).
  const handleMarkProcessing = async (job) => {
    // Iter 287p — Ganti window.confirm/alert dgn notify (in-app).
    const ok = await notify.confirm({
      title: "Proses pesanan Shopee?",
      message: `Pesanan ${job.order_number} akan ditandai sedang diproses. Setelah ini, tombol Unduh aktif dan penarikan otomatis dari desainer akan ditolak.`,
      primaryLabel: "Proses Sekarang",
      secondaryLabel: "Batal",
    });
    if (!ok) return;
    setMarkingId(job.id);
    try {
      await axios.post(`${API}/shopee/${job.id}/warehouse-mark-processing`, null, { withCredentials: true });
      await load();
      notify.success({ title: "Berhasil", message: `Pesanan ${job.order_number} sedang diproses.` });
    } catch (e) {
      notify.error({ title: "Gagal menandai proses", message: e?.response?.data?.detail || e.message || "Terjadi kesalahan tidak diketahui." });
    } finally { setMarkingId(null); }
  };

  // Iter 374 — Panggil warehouse-mark-processing secara idempotent & silent.
  //   Dipakai sebelum unduh / tandai cetak agar user tidak perlu "Mulai Siapkan"
  //   eksplisit. Backend sudah idempotent (409 bila state berubah ke non-queued
  //   tanpa processing-ok). Error FIX / pending_request diteruskan ke pemanggil
  //   agar bisa ditampilkan di modal konteks (bukan toast global).
  const ensureProcessing = async (job) => {
    if (!job) return;
    const proc = job.warehouse_process_state || "queued";
    if (proc === "processing") return;
    await axios.post(`${API}/shopee/${job.id}/warehouse-mark-processing`, null, { withCredentials: true });
  };

  // Iter 287ae — Rido meneruskan pekerjaan Shopee ke tim Desain Bordir.
  const [forwardingId, setForwardingId] = useState(null);
  const handleForwardEmbroidery = async (job) => {
    if (forwardingId) return;
    const ok = await notify.confirm({
      title: "Kirim ke Desain Bordir?",
      message: `Pesanan ${job.order_number} akan diteruskan ke tim Desain Bordir. Snapshot mockup yang sama dengan Gudang akan dipakai.`,
      primaryLabel: "Kirim Sekarang",
      secondaryLabel: "Batal",
    });
    if (!ok) return;
    setForwardingId(job.id);
    try {
      await axios.post(`${API}/shopee/${job.id}/forward-embroidery`, { note: "" }, { withCredentials: true });
      await load();
      notify.success({ title: "Berhasil", message: "Pekerjaan diteruskan ke Desain Bordir." });
    } catch (e) {
      notify.error({ title: "Gagal meneruskan", message: e?.response?.data?.detail || e.message || "Terjadi kesalahan tidak diketahui." });
    } finally { setForwardingId(null); }
  };

  // Iter 323 — Rido menandai Mockup Shopee sudah DICETAK. Terpisah dari Preview/Unduh.
  //   Setelah ditandai, pesanan masuk antrean "Menunggu ACC" untuk Adit.
  // Iter 374 — Konfirmasi dialog UI dipindah ke `KonfirmasiCetakModal`; handler ini
  //   dipanggil setelah user klik "Ya, sudah dicetak". Auto-call ensureProcessing
  //   bila kartu masih queued (menghilangkan kebutuhan tombol "Mulai Siapkan").
  //   Error dilempar ke pemanggil agar modal bisa menampilkan pesan dan tidak
  //   menutup diri. Idempoten (klik ulang pada kartu sudah printed → return).
  const [markPrintedId, setMarkPrintedId] = useState(null);
  // Iter 374 — Terima opsional `fileId` untuk mark per-file. Snapshot entry
  //   (warehouse_sent_snapshot[].printed_at/by) akan diset; job-level
  //   mockup_printed_at hanya berubah ketika SEMUA file sudah ditandai.
  const handleMarkMockupPrinted = async (job, fileId) => {
    if (markPrintedId) return;
    if (!fileId && job?.mockup_printed_at) return; // idempotent di UI (whole-job)
    setMarkPrintedId(job.id + (fileId ? `::${fileId}` : ""));
    try {
      // Pastikan warehouse_process_state="processing" lebih dulu (silent, idempotent).
      await ensureProcessing(job);
      const body = fileId ? { file_id: fileId } : {};
      await axios.post(`${API}/shopee/${job.id}/mark-mockup-printed`, body, { withCredentials: true });
      await load();
      toast.success(fileId
        ? `✓ File mockup ditandai dicetak`
        : `✓ Mockup sudah dicetak — masuk Menunggu ACC Adit`);
    } catch (e) {
      setMarkPrintedId(null);
      throw new Error(e?.response?.data?.detail || e.message || "Gagal menandai mockup dicetak.");
    } finally { setMarkPrintedId(null); }
  };

  // Iter 287l — Unduh: fetch blob terautentikasi + catat ke backend.
  // Iter 287m — Dipisah: (1) fetch+trigger; kegagalan → "Coba Lagi". (2) record;
  // kegagalan hanya diretry di background, tak boleh membuat UI seolah unduh gagal.
  const handleDownloadFile = async (job, file) => {
    const fid = file.file_id;
    if (dlBusyIds.has(fid)) return;
    setDlBusyIds(prev => { const n = new Set(prev); n.add(fid); return n; });
    setDlErrsById(prev => { const n = {...prev}; delete n[fid]; return n; });
    try {
      await fetchAndTriggerShopeeDownload(file);
    } catch (e) {
      const msg = await parseAxiosErrMsg(e);
      setDlErrsById(prev => ({...prev, [fid]: msg}));
      setDlBusyIds(prev => { const n = new Set(prev); n.delete(fid); return n; });
      return;
    }
    // Fetch+trigger sukses → tandai optimistik + lepas busy. UI langsung "Unduh Ulang".
    setDlOptimisticIds(prev => { const n = new Set(prev); n.add(fid); return n; });
    setDlBusyIds(prev => { const n = new Set(prev); n.delete(fid); return n; });
    // Pencatatan di background dengan retry — tidak menahan UI.
    (async () => {
      const ok = await recordShopeeDownloadWithRetry(job.id, fid);
      // Refresh queue untuk memuat warehouse_downloads terbaru (dari server).
      // Bila pencatatan tak pernah sukses, snapshot tetap ada di optimistik lokal.
      try { await load(); } catch { /* ignore */ }
      if (!ok) {
        // eslint-disable-next-line no-console
        console.warn(`[Shopee] Pencatatan unduh gagal untuk file ${fid} — akan tetap tampil sebagai Sudah Diunduh di sesi ini.`);
      }
    })();
  };

  const activeItems = items.filter(x => x.warehouse_state === "sent");
  const cancelledItems = items.filter(x => x.warehouse_state === "cancelled");
  // Iter 332 — Antrean "Siapkan Barang" Rido Shopee hanya menampilkan kartu yang:
  //   1) warehouse_state=sent,
  //   2) status=FIX (bukan Menunggu FIX / BATAL),
  //   3) mockup belum dicetak (!mockup_printed_at) — kartu printed pindah ke tab Menunggu ACC.
  // Setara dgn tab Siapkan Barang Offline. Untuk admin view (!hideCancelledToggle) tetap
  // menampilkan semua (termasuk belum FIX) agar Owner bisa audit lengkap.
  const prepItems = useMemo(() => {
    if (!hideCancelledToggle) return activeItems; // admin
    return activeItems.filter(j => j.status === "FIX" && !j.mockup_printed_at && !finalCancelled(j));
  }, [activeItems, hideCancelledToggle]);
  // Iter 374 — Daftar "Sudah Dicetak" Shopee untuk Rido: FIX & sudah ditandai cetak.
  // Setara dgn tab Menunggu ACC Offline — tetap dirender di view yang sama lewat
  // filter chip. Admin view tidak di-filter (lihat semua).
  // Iter 375 — Hilangkan filter `!goods_acc_at` agar kartu edge-case (sudah ACC
  //   tapi masih berstatus `sent`, mis. race condition sebelum auto-complete)
  //   tetap muncul dgn label "Sudah ACC" dari data real. Backend normal auto
  //   men-set warehouse_state=completed setelah ACC → kartu hilang dgn sendirinya.
  const printedItems = useMemo(() => {
    if (!hideCancelledToggle) return [];
    return activeItems.filter(j => j.status === "FIX" && !!j.mockup_printed_at && !finalCancelled(j));
  }, [activeItems, hideCancelledToggle]);
  // Notifikasi Rido: pembatalan setelah dikirim, ambil yang recent (7 hari) dan
  // pernah benar-benar sampai gudang (punya warehouse_first_sent_at atau send_count>0).
  const cutoff = Date.now() - 7*86400*1000;
  const notifCancelled = cancelledItems.filter(c => {
    const t = c.warehouse_cancelled_at ? new Date(c.warehouse_cancelled_at).getTime() : 0;
    return t >= cutoff;
  }).concat(activeItems.filter(finalCancelled).map(c => ({ ...c, warehouse_cancelled_at: c.warehouse_cancelled_at || c.canceled_internal?.at,
    cancelled_by: c.cancelled_by || c.canceled_internal?.by?.name || (c.shopee_cancelled ? "Shopee" : ""),
    cancel_reason: c.cancel_reason || c.canceled_internal?.reason || (c.shopee_cancelled ? "Dibatalkan resmi di Shopee" : "") })));
  const activeCount = prepItems.length;
  const printedCount = printedItems.length;
  const cancelledCount = cancelledItems.length;

  // Iter 374 — Pencarian lokal (nota / akun / design title / PIC) + filter chip.
  const filterByQuery = (list) => {
    const q = (searchQ || "").trim().toLowerCase();
    if (!q) return list;
    return list.filter(j => {
      const hay = [j.order_number, j.customer_account, j.design_title,
                   j.pic_designer_name, j.mockup_printed_by, j.warehouse_sent_by]
                   .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  };

  // Iter 287ak — Urutan tampilan kartu antrean Shopee Gudang (client-side).
  // Iter 374 — Bila `printFilter === "sudah"` (view Rido), sumber = printedItems.
  const [sortBy, setSortBy] = useState("recent");
  const [printedSort, setPrintedSort] = useState("desc"); // Sudah Dicetak: desc = Terbaru Dicetak (default) | asc
  const [batchSel, setBatchSel] = useState(() => new Set()); // Iter 637 — pilihan cetak massal (ID pekerjaan unik)
  const lastPick = useRef(null);
  const toggleBatch = (id, e) => setBatchSel(prev => {
    const n = new Set(prev), on = !n.has(id);
    const ids = displayItems.filter(j => batchEligible(j, printFilter)).map(j => j.id);
    const a = ids.indexOf(lastPick.current), b = ids.indexOf(id);
    const range = e?.shiftKey && a >= 0 && b >= 0 ? ids.slice(Math.min(a, b), Math.max(a, b) + 1) : [id];
    range.forEach(x => (on ? n.add(x) : n.delete(x)));
    lastPick.current = id;
    return n;
  });
  useEffect(() => { setBatchSel(new Set()); }, [printFilter]);
  const displayItems = useMemo(() => {
    const baseList = !hideCancelledToggle
      ? items
      : (printFilter === "sudah" ? printedItems : prepItems);
    const list = filterByQuery(baseList.slice());
    if (hideCancelledToggle && printFilter === "sudah") return sortPrinted(list, printedSort);
    const ts = (j) => Date.parse(j.warehouse_sent_at || j.created_at || 0) || 0;
    if (sortBy === "recent" || !sortBy) {
      list.sort((a, b) => ts(b) - ts(a));
    } else if (sortBy === "waiting") {
      // Menunggu Cetak: sent+queued+FIX+no-pending dulu, lalu queued lain, lalu processing/on_hold/lainnya.
      const rank = (j) => {
        const proc = j.warehouse_process_state || "queued";
        const hasPending = j.pending_request && j.pending_request.status === "pending";
        if (j.warehouse_state === "sent" && proc !== "processing" && j.status === "FIX" && !hasPending) return 0;
        if (j.warehouse_state === "sent" && proc !== "processing") return 1;
        if (j.warehouse_state === "sent" && proc === "processing") return 2;
        return 3;
      };
      list.sort((a, b) => rank(a) - rank(b) || ts(b) - ts(a));
    } else if (sortBy === "hold") {
      // Ditahan / pengajuan menunggu dulu.
      const rank = (j) => {
        const hasPending = j.pending_request && j.pending_request.status === "pending";
        if (hasPending) return 0;
        if (j.warehouse_state === "on_hold") return 1;
        return 2;
      };
      list.sort((a, b) => rank(a) - rank(b) || ts(b) - ts(a));
    } else if (sortBy === "processing") {
      // Sedang Diproses dulu.
      const rank = (j) => {
        if (j.warehouse_state === "sent" && (j.warehouse_process_state || "queued") === "processing") return 0;
        return 1;
      };
      list.sort((a, b) => rank(a) - rank(b) || ts(b) - ts(a));
    }
    return list;
  }, [items, prepItems, printedItems, hideCancelledToggle, sortBy, printedSort, printFilter, searchQ]);
  // Scan/cari Rido: pesanan cocok yang tidak tampil di tab ini (sedang revisi/HOLD/beda tab) atau sudah direvisi setelah dicetak.
  const scanHidden = useMemo(() => {
    if (!hideCancelledToggle || !(searchQ || "").trim()) return [];
    const shown = new Set(displayItems.map(j => j.id));
    return filterByQuery(items).filter(j => !shown.has(j.id) || j.revised_after_print || j.revised_since_print);
  }, [items, displayItems, hideCancelledToggle, searchQ]); // eslint-disable-line react-hooks/exhaustive-deps

  return <section className={`wh-section shopee-theme${hideCancelledToggle ? " rido-desk" : ""}`} data-testid="wh-shopee-section">
    <ShopeeThemeStyles/>
    {!hideCancelledToggle && <StockCheckBoard/>}{/* Rido: tampil di bawah ubin Tanpa Mockup / Cetak Mockup */}
    {!hideCancelledToggle && <>
    <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8, marginBottom:12}}>
        <div style={{display:"flex", gap:8, alignItems:"center", flexWrap:"wrap"}}>
          <span className="wh-badge wh-badge-shopee shopee-badge">SHOPEE</span>
          {/* Iter 375 — Judul header mengikuti filter aktif untuk Rido.
              Admin view tetap pakai label total aktif seperti sebelumnya. */}
          {hideCancelledToggle ? (
            printFilter === "sudah" ? (
              <span data-testid="wh-shopee-count-active"><b>{printedCount}</b> Sudah Dicetak</span>
            ) : (
              <span data-testid="wh-shopee-count-active"><b>{activeCount}</b> Belum Dicetak</span>
            )
          ) : (
            <span data-testid="wh-shopee-count-active"><b>{activeCount}</b> Siapkan Barang</span>
          )}
          {!alwaysIncludeCancelled && cancelledCount > 0 && <span className="muted">· {cancelledCount} batal</span>}
        </div>
        <div style={{display:"flex", gap:6, alignItems:"center"}}>
          {/* Iter 287ak — Urutan prioritas. */}
          <select value={sortBy} onChange={e=>setSortBy(e.target.value)}
            style={{padding:"6px 8px", border:"1px solid #e2e8f0", borderRadius:6, fontSize:12}}
            title="Urutan tampilan kartu"
            data-testid="wh-shopee-sort">
            <option value="recent">↓ Terbaru</option>
            <option value="waiting">🖨 Belum Disiapkan</option>
            <option value="processing">⚙️ Sedang Disiapkan</option>
            <option value="hold">⏸ Ditahan / Pengajuan</option>
          </select>
          {!hideCancelledToggle && <label style={{display:"flex", alignItems:"center", gap:4, fontSize:12, cursor:"pointer"}}>
            <input type="checkbox" checked={includeCancelled} onChange={e=>setIncludeCancelled(e.target.checked)}
              data-testid="wh-shopee-include-cancelled"/> Tampilkan BATAL
          </label>}
          <button type="button" className="soft-btn small-btn" onClick={load}
            data-testid="wh-shopee-refresh"><RefreshCw size={14}/> Muat Ulang</button>
        </div>
      </div>
    </>}
    {hideCancelledToggle && <div className="rido-toolbar" data-testid="wh-shopee-print-filter">
      <div className="rido-print-filter" role="tablist" aria-label="Filter status cetak mockup Shopee">
        <button type="button" role="tab" aria-selected={printFilter === "belum"}
          className={`rido-print-chip ${printFilter === "belum" ? "active" : ""}`}
          onClick={() => setPrintFilter("belum")} data-testid="wh-shopee-chip-belum">
          Belum Dicetak <span className="rido-print-chip-count" data-testid="wh-shopee-count-active">{activeCount}</span>
        </button>
        <button type="button" role="tab" aria-selected={printFilter === "sudah"}
          className={`rido-print-chip ${printFilter === "sudah" ? "active" : ""}`}
          onClick={() => setPrintFilter("sudah")} data-testid="wh-shopee-chip-sudah">
          Sudah Dicetak <span className="rido-print-chip-count">{printedCount}</span>
        </button>
      </div>
      <div className="rido-tools">
        <input type="search" className="shopee-search-input" placeholder="Cari pesanan (nota / akun / desainer)"
          value={searchQ} onChange={e => setSearchQ(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter") { e.preventDefault(); setSearchQ(e.currentTarget.value); }
            if (e.key === "Escape") setSearchQ("");
          }}
          data-testid="wh-shopee-search"/>
        {searchQ.trim() && <span className="muted small" data-testid="wh-shopee-search-count">{displayItems.length} hasil</span>}
        {printFilter === "sudah" ? <select value={printedSort} onChange={e => setPrintedSort(e.target.value)} className="rido-sort"
            title="Urutan waktu cetak" aria-label="Urutkan waktu cetak" data-testid="wh-shopee-printed-sort">
            <option value="desc">Urutkan: Terbaru Dicetak</option>
            <option value="asc">Urutkan: Terlama Dicetak</option>
          </select> : <select value={sortBy} onChange={e=>setSortBy(e.target.value)} className="rido-sort"
            title="Urutan tampilan kartu" aria-label="Urutkan" data-testid="wh-shopee-sort">
            <option value="recent">Urutkan: Terbaru</option>
            <option value="waiting">Urutkan: Belum Disiapkan</option>
            <option value="processing">Urutkan: Sedang Disiapkan</option>
            <option value="hold">Urutkan: Ditahan / Pengajuan</option>
          </select>}
        <button type="button" className="soft-btn small-btn" onClick={load} disabled={loading} data-testid="wh-shopee-refresh">
          <RefreshCw size={14}/> Muat Ulang</button>
      </div>
    </div>}
    {err && <div className="warn-box" data-testid="wh-shopee-error">{err}</div>}
    {hideCancelledToggle && notifCancelled.length > 0 && <details className="rido-cancel-alert" data-testid="wh-shopee-notif-cancelled">
      <summary><b>{notifCancelled.length} pesanan dibatalkan</b> — jangan dicetak <span className="muted small" data-testid="wh-shopee-notif-cancelled-window">(7 hari terakhir)</span></summary>
      <ul>
        {notifCancelled.slice(0, 5).map(c => <li key={c.id} data-testid={`wh-shopee-notif-cancelled-item-${c.id}`}>
          <b>{c.order_number}</b> · {c.customer_account || "-"} · dibatalkan {fmtDateTime(c.warehouse_cancelled_at)}
          {" · "}Dibatalkan oleh: <b>{c.cancelled_by || "—"}</b>
          {c.cancel_reason && <> · {c.cancel_reason}</>}
          {c.mockup_printed_at && <> · <b style={{ color: "#b3261e" }} data-testid={`wh-shopee-notif-cancelled-printed-${c.id}`}>Sudah dicetak — jangan diproses</b></>}
        </li>)}
        {notifCancelled.length > 5 && <li className="muted">…dan {notifCancelled.length - 5} lainnya</li>}
      </ul>
    </details>}
    {loading && items.length === 0 && <div className="muted small">Memuat…</div>}
    <ScanFoundBanner jobs={scanHidden} onOpen={(j) => setOpenId(j.id)}
      onReprint={(j) => { setPrintFilter(j.mockup_printed_at ? "sudah" : "belum"); setSearchQ(j.order_number); }}/>
    {!loading && !err && !scanHidden.length && (hideCancelledToggle ? displayItems.length === 0 : activeCount === 0) && <div className="muted small rido-empty" data-testid="wh-shopee-empty">
      {hideCancelledToggle
        ? (searchQ.trim() ? "Tidak ada pesanan yang cocok dengan pencarian." : printFilter === "sudah" ? "Belum ada mockup yang dicetak." : "Semua mockup sudah dicetak. Pesanan baru muncul otomatis setelah desainer mengirim ke Gudang.")
        : <>Belum ada pesanan Shopee yang siap disiapkan. Kartu akan muncul otomatis setelah desainer menandai mockup <b>FIX</b> dan klik <b>Kirim ke Gudang</b>.</>}
    </div>}
    {hideCancelledToggle && <ShopeeBatchPrintBar jobs={displayItems} mode={printFilter} selected={batchSel} setSelected={setBatchSel}
      ensureProcessing={ensureProcessing} onDone={load}/>}
    <div className={hideCancelledToggle ? "rido-desk-grid" : undefined} data-testid="wh-shopee-grid"
      style={hideCancelledToggle ? undefined : {display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(320px, 1fr))", gap:16}}>
      {displayItems.map(job => <div key={job.id} className={`shopee-batch-cell${batchSel.has(job.id) ? " is-picked" : ""}`}>
        {hideCancelledToggle && <BatchSelectBox job={job} mode={printFilter} selected={batchSel} onToggle={toggleBatch}/>}
        <ShopeeWhCard job={job}
        onOpen={()=>setOpenId(job.id)}
        marking={markingId === job.id}
        forwardingEmb={forwardingId === job.id}
        markingPrinted={markPrintedId === job.id}
        printFilter={hideCancelledToggle ? printFilter : null}
        onMarkProcessing={handleMarkProcessing}
        onForwardEmbroidery={handleForwardEmbroidery}
        onMarkMockupPrinted={handleMarkMockupPrinted}
        onOpenPreview={(f)=>setPreviewFile(f)}
        onDownloadFile={handleDownloadFile}
        dlBusyIds={dlBusyIds}
        dlErrsById={dlErrsById}
        dlOptimisticIds={dlOptimisticIds}
        onApprovePending={handleApprovePending}
        onRejectPending={(j)=>setRejectFor(j)}
        onOpenDownloadPicker={(j, mode)=>setPickerJob({ job: j, mode })}
        onOpenCetakSize={(fileId, label, j)=>setSizeModal({ fileId, orderNumber: job.order_number, label, job: j || null })}
        onOpenConfirmPrint={(j)=>setConfirmPrintModal({ job: j })}
        onOpenPerFileMark={(j)=>setPerFileMarkModal({ job: j })}
        ensureProcessing={ensureProcessing}
      /></div>)}
    </div>
    {rejectFor && <ShopeeRejectPendingModal job={rejectFor}
      onClose={()=>setRejectFor(null)}
      onSubmit={handleRejectPending}/>}
    {openId && <ShopeeWhDetail jobId={openId} onClose={()=>{ setOpenId(null); load(); }}/>}
    {pickerJob && <ShopeeFilePickerModal job={pickerJob.job} mode={pickerJob.mode}
      onOpenPreview={(f)=>{ setPreviewFile(f); setPickerJob(null); }}
      onDownloadFile={handleDownloadFile}
      onOpenCetakSize={(fileId, label)=>{ setSizeModal({ fileId, orderNumber: pickerJob.job.order_number, label }); setPickerJob(null); }}
      onUnduhMockup={async (fileId) => { try { await unduhMockupShopee(fileId, pickerJob.job.order_number); } catch (err) { toast.error(err?.message || "Gagal mengunduh mockup."); } }}
      dlBusyIds={dlBusyIds}
      dlErrsById={dlErrsById}
      dlOptimisticIds={dlOptimisticIds}
      onClose={()=>setPickerJob(null)}/>}
    {previewFile && <ShopeeImagePreviewModal file={previewFile} onClose={()=>setPreviewFile(null)}/>}
    {/* Iter 374 — Modal pilihan A5/A4 (reuse Offline). onPrint & onDownload
        memakai fetcher Shopee (per fileId snapshot) agar status tidak otomatis
        berubah. Kertas fisik tetap A4; A5 = pojok kiri atas lembar A4. */}
    {sizeModal && (
      <CetakMockupSizeModal
        presets={["A5L", "A4R"]}
        orderNumber={sizeModal.orderNumber}
        title={sizeModal.label || `${mpOrderLabel(sizeModal.job)} ${sizeModal.orderNumber}`}
        onClose={()=>{
          const j = afterPrintRef.current;
          afterPrintRef.current = null;
          setSizeModal(null);
          if (j) setConfirmPrintModal({ job: j });  // Konfirmasi tetap wajib sebelum status berubah
        }}
        onPrint={async (paper) => {
          if (sizeModal.job && !sizeModal.job.is_simulation) {   // status BATAL Shopee terkini sebelum cetak
            const chk = (await axios.post(`${API}/shopee/mass-ship/print-check`, { job_ids: [sizeModal.job.id] }, { withCredentials: true })).data;
            const bad = (chk.cancelled || [])[0];   // hanya BATAL resmi yang melarang cetak
            if (bad) throw new Error(`${bad.order_number}: ${bad.reason}`);
            const unv = (chk.unverified || [])[0];
            if (unv) toast.warning(`${unv.order_number}: ${unv.reason}. Cetak tetap dilanjutkan.`);
          }
          if (paper === "A4R") {
            const sn = items.flatMap(x => x.warehouse_sent_snapshot || []).find(x => x.file_id === sizeModal.fileId);
            await cetakPdfA4P([{ fileId: sizeModal.fileId, orderNumber: sizeModal.orderNumber, paperFormat: sn?.paper_format || "" }]);
          } else await cetakMockupShopee(sizeModal.fileId, sizeModal.orderNumber, paper);
          const j = sizeModal.job;
          if (hideCancelledToggle && j && !j.mockup_printed_at && (j.warehouse_sent_snapshot || []).length === 1) afterPrintRef.current = j;
        }}
        onDownload={async () => {
          await unduhMockupShopee(sizeModal.fileId, sizeModal.orderNumber);
        }}
      />
    )}
    {/* Iter 374 — Modal konfirmasi tandai dicetak Shopee. Preview/unduh/dialog
        cetak TIDAK auto-tandai; hanya tombol "Ya, sudah dicetak" memanggil
        handleMarkMockupPrinted (yang silent ensureProcessing + idempotent). */}
    {confirmPrintModal && (
      <KonfirmasiCetakModal
        orderNumber={confirmPrintModal.job.order_number}
        scopeLabel={mpOrderLabel(confirmPrintModal.job)}
        pendingCount={1}
        totalCount={1}
        onCancel={()=>setConfirmPrintModal(null)}
        onConfirm={async ()=>{
          await handleMarkMockupPrinted(confirmPrintModal.job);
          setConfirmPrintModal(null);
        }}
      />
    )}
    {/* Iter 374 — Modal per-file untuk multi-mockup. Mark setiap file terpisah;
        pesanan baru masuk Sudah Dicetak ketika seluruh file ditandai (backend). */}
    {perFileMarkModal && (
      <ShopeeMarkPrintedPerFileModal
        job={items.find(x => x.id === perFileMarkModal.job.id) || perFileMarkModal.job}
        onClose={()=>setPerFileMarkModal(null)}
        onMarkFile={async (fileId) => {
          await handleMarkMockupPrinted(perFileMarkModal.job, fileId);
          // Refresh job state agar progress ter-update tanpa tutup modal.
          const fresh = (await axios.get(`${API}/shopee/warehouse/queue`, { withCredentials: true })).data;
          const freshJob = (fresh.items || []).find(j => j.id === perFileMarkModal.job.id);
          if (freshJob) {
            setPerFileMarkModal({ job: freshJob });
            if (freshJob.mockup_printed_at) {
              setPerFileMarkModal(null);
            }
          }
        }}
      />
    )}
  </section>;
}

// === END ShopeeWarehouseSection ===

// ============================================================================
// Iter 287ae — Bagian Shopee di tab tim Desain Bordir.
// Menampilkan pekerjaan Shopee yang sudah diteruskan Rido lewat "Kirim ke Desain
// Bordir". Read-only untuk data pekerjaan, tetapi tim bordir dapat Preview & Unduh
// mockup snapshot yang diteruskan. Sinkron on_hold/cancelled dari alur Rido.
// ============================================================================
export function ShopeeEmbroiderySection({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState("");
  const [previewFile, setPreviewFile] = useState(null);
  const [pickerJob, setPickerJob] = useState(null);
  const [busyStartId, setBusyStartId] = useState(null);
  const [busyDoneId, setBusyDoneId] = useState(null);
  const [pointsJob, setPointsJob] = useState(null);
  const [q, setQ] = useState("");
  // Iter 517 — Section tab (Antrean/Dikerjakan/Riwayat) + KPI counter ala Offline.
  //   Default: "antrean" supaya desainer melihat pekerjaan baru lebih dulu.
  const [section, setSection] = useState("antrean");
  // Iter 471/478 — Inflight guard: cegah dua request paralel. SELALU di-reset
  //   di `finally` (termasuk jalur timeout / abort) sehingga tidak pernah
  //   stuck permanen.
  const inflightRef = useRef(false);
  // Iter 480 — Track AbortController aktif supaya cleanup bisa membatalkan.
  const abortRef = useRef(null);

  const load = async (silent = false) => {
    if (inflightRef.current) return;
    inflightRef.current = true;
    if (silent) setRefreshing(true); else setLoading(true);
    if (!silent) setErr("");
    // Iter 478/480 — Timeout 15 detik + AbortController. Abort juga saat
    //   komponen unmount agar tidak ada pending request yang menahan state.
    const controller = new AbortController();
    const timer = setTimeout(() => { try { controller.abort(); } catch {} }, 15_000);
    abortRef.current = controller;
    try {
      // Iter 517 — include_history=true agar tab Riwayat dapat menampilkan
      //   design_done milik desainer login (filter per-operator di backend).
      const r = await axios.get(`${API}/shopee/embroidery/queue?include_history=true`, {
        withCredentials: true,
        timeout: 15_000,
        signal: controller.signal,
      });
      if (Array.isArray(r?.data?.items)) setItems(r.data.items);
      if (!silent) setErr("");
    } catch (e) {
      const isTimeout = e?.code === "ECONNABORTED" || e?.name === "CanceledError" || e?.message === "canceled";
      const detail = isTimeout
        ? "Permintaan timeout (>15 detik). Data terakhir dipertahankan."
        : (e?.response?.data?.detail || e?.message || "Gagal memuat (data terakhir dipertahankan).");
      setErr(silent ? `Auto-refresh gagal: ${detail}` : detail);
    } finally {
      clearTimeout(timer);
      if (abortRef.current === controller) abortRef.current = null;
      inflightRef.current = false;
      if (silent) setRefreshing(false); else setLoading(false);
    }
  };
  // Iter 480 — Cleanup: abort fetch + reset inflight saat unmount supaya
  //   StrictMode double-mount / navigasi cepat tidak membuat state stuck.
  useEffect(() => {
    load(false);
    return () => {
      try { abortRef.current?.abort(); } catch {}
      inflightRef.current = false;
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);
  // Iter 471 — Auto-refresh 60 detik SILENT (tidak flicker "Memuat…"). Hook
  //   pause saat tab hidden / form dirty / inflight. Fetch gagal: data lama
  //   dipertahankan (handled di load).
  useAutoRefresh({ enabled: true, intervalMs: 60_000, onRefresh: () => load(true) });

  // Iter 326 — Kerjakan: forwarded → designing.
  const handleStartDesigning = async (job) => {
    if (busyStartId) return;
    setBusyStartId(job.id);
    try {
      await axios.post(`${API}/shopee/${job.id}/bordir/start-designing`, null, { withCredentials: true });
      await load();
      notify.success({ title: "Mulai mengerjakan", message: `${job.order_number} · masuk status Sedang Dikerjakan.` });
    } catch (e) {
      notify.error({ title: "Gagal mulai mengerjakan", message: e?.response?.data?.detail || e.message || "Terjadi kesalahan." });
    } finally { setBusyStartId(null); }
  };
  // Iter 326 — Selesai: designing → design_done. Kartu keluar dari antrean ini,
  // otomatis masuk ke Antrean Operator Bordir.
  // Selesai Desain: isi titik per desain dulu (dialog), lalu designing → design_done.
  const handleDesignDone = (job) => { if (!busyDoneId) setPointsJob({ job, target: { source: "shopee", job_id: job.id } }); };
  const submitDesignDone = async (job, designs) => {
    setBusyDoneId(job.id);
    try {
      const r = await axios.post(`${API}/shopee/${job.id}/bordir/design-done`, { designs }, { withCredentials: true });
      // Iter 635 — sukses hanya bila backend mengonfirmasi kartu sudah di tahap Operator Bordir.
      if (!["design_done", "executing", "done"].includes(r?.data?.bordir_state)) throw new Error("Status kartu belum berpindah ke Antrean Operator Bordir. Silakan coba lagi.");
      setPointsJob(null);
      await load();
      notify.success({ title: "Berhasil dikirim", message: `${job.order_number} · ${designs.length} desain · ${designs.reduce((a, d) => a + d.titik, 0)} titik · masuk Antrean Operator Bordir.` });
    } finally { setBusyDoneId(null); }
  };

  // Iter 517 — Bucket items ke 3 kategori ala Offline:
  //   • Antrean  : bordir_state === "forwarded" (open-queue, siapa saja boleh ambil).
  //   • Sedang   : bordir_state === "designing" DAN bordir_designing_by === login.name
  //                (hanya pekerjaan milik desainer login — tidak campur antar operator).
  //   • Riwayat  : bordir_state === "design_done" DAN bordir_designing_by === login.name
  //                DAN bordir_design_done_at HARI INI (local date).
  const myName = (user?.name || "").trim();
  // Iter 595 — Tanggal "hari ini" memakai WIB (bukan UTC) & atribusi desainer memakai ID stabil dari backend
  //   (designing_mine / design_mine; data lama dicocokkan nama ternormalisasi). Riwayat tetap mencatat desain
  //   yang sudah lanjut ke Operator Bordir (executing/done).
  const todayWIB = new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 10);
  const bucketOf = (j) => {
    const bs = j.bordir_state;
    if (bs === "forwarded") return "antrean";
    if (bs === "designing") {
      if (!myName || j.designing_mine || (j.designing_mine === undefined && (j.bordir_designing_by || "").trim() === myName)) return "sedang";
      return "other";
    }
    if (j.bordir_design_done_at) {
      if (myName && !j.design_mine) return "other";
      if ((j.design_done_day_wib || "") !== todayWIB) return "other";
      return "riwayat";
    }
    return "other";
  };
  // Hitung KPI dari items (sebelum filter search) → angka selalu akurat.
  const counts = { antrean: 0, sedang: 0, riwayat: 0 };
  items.forEach(j => {
    if (!j || !j.id) return;
    const b = bucketOf(j);
    if (b === "antrean" || b === "sedang" || b === "riwayat") counts[b]++;
  });

  const filtered = items.filter(j => {
    // Iter 466 — Skip job korup (null/undefined/tanpa id) agar tidak crash downstream.
    if (!j || !j.id) return false;
    // Iter 517 — Filter per section.
    if (bucketOf(j) !== section) return false;
    if (!q.trim()) return true;
    const t = q.trim().toLowerCase();
    return (j.order_number || "").toLowerCase().includes(t)
      || (j.customer_account || "").toLowerCase().includes(t)
      || (j.design_title || "").toLowerCase().includes(t)
      || (j.pic_designer_name || "").toLowerCase().includes(t);
  });
  if (section === "antrean") filtered.sort(byShipDeadlineThenWait);

  return <section className="shopee-theme" data-theme-scope="shopee" data-testid="bordir-shopee-section">
    <ShopeeThemeStyles/>
    <div style={{display:"flex", gap:8, alignItems:"center", marginBottom:12, flexWrap:"wrap"}}>
      <h3 style={{margin:0, display:"flex", alignItems:"center", gap:8}}>
        <Sparkles size={18} style={{color:"var(--tf-shopee)"}}/> Desain Bordir · Shopee
        {myName && <span className="muted small" style={{fontWeight:400}}>· <strong>{myName}</strong></span>}
      </h3>
      <button type="button" className="soft-btn small-btn" onClick={() => load(false)} data-testid="bordir-shopee-refresh" disabled={refreshing || loading} title="Perbarui data sekarang">
        <RefreshCw size={12} className={refreshing || loading ? "spin" : ""}/>
        {refreshing && !loading ? "Menyegarkan…" : "Muat ulang"}
      </button>
      <div style={{marginLeft:"auto", position:"relative"}}>
        <Search size={12} style={{position:"absolute", left:8, top:"50%", transform:"translateY(-50%)", color:"#94a3b8"}}/>
        <input placeholder="Cari nomor/akun/judul/Operator" value={q} onChange={e=>setQ(e.target.value)}
          style={{padding:"6px 10px 6px 26px", border:"1px solid #cbd5e1", borderRadius:6, fontSize:12, minWidth:220}}
          data-testid="bordir-shopee-search"/>
      </div>
    </div>
    {/* Iter 517 — KPI 3 kolom ala Offline: Antrean · Dikerjakan · Selesai Hari Ini.
        Klik tile = pindah tab (sama perilaku dengan Offline). Reuse class
        `.op-stats-row` + `.op-stat` + `.op-stat-label` + `.op-stat-value` agar
        typography & spacing identik dengan tim Offline. */}
    <div className="op-stats-row" role="tablist" aria-label="Ringkasan pekerjaan Desain Bordir Shopee"
      data-testid="bordir-shopee-kpi">
      <button type="button" role="tab" aria-selected={section==="antrean"}
        className={`op-stat ${section==="antrean"?"active":""}`}
        onClick={()=>setSection("antrean")}
        data-testid="bordir-shopee-kpi-antrean">
        <span className="op-stat-label">Antrean</span>
        <strong className="op-stat-value op-stat-value-plain" data-testid="bordir-shopee-kpi-antrean-count">{counts.antrean}</strong>
        <span className="op-stat-sub">Belum diambil</span>
      </button>
      <button type="button" role="tab" aria-selected={section==="sedang"}
        className={`op-stat ${section==="sedang"?"active":""}`}
        onClick={()=>setSection("sedang")}
        data-testid="bordir-shopee-kpi-sedang">
        <span className="op-stat-label">Dikerjakan</span>
        <strong className="op-stat-value op-stat-value-active" data-testid="bordir-shopee-kpi-sedang-count">{counts.sedang}</strong>
        <span className="op-stat-sub">Sedang Anda kerjakan</span>
      </button>
      <button type="button" role="tab" aria-selected={section==="riwayat"}
        className={`op-stat ${section==="riwayat"?"active":""}`}
        onClick={()=>setSection("riwayat")}
        data-testid="bordir-shopee-kpi-riwayat">
        <span className="op-stat-label">Selesai Hari Ini</span>
        <strong className="op-stat-value op-stat-value-done" data-testid="bordir-shopee-kpi-riwayat-count">{counts.riwayat}</strong>
        <span className="op-stat-sub">Desain selesai hari ini</span>
      </button>
    </div>
    {/* Iter 542 — Diagram Produktivitas Harian untuk desainer bordir login.
        Iter 543 — Berevolusi menjadi LEADERBOARD (Wawa vs Rizal). Default
        Gabungan (all) sesuai mandat Owner — PIC dpt beralih ke Shopee/Offline. */}
    {err && <div className="warn-box small">{err}</div>}
    {/* Iter 471 — Skeleton "Memuat…" HANYA saat first-load (items masih kosong).
        Saat auto-refresh background, pertahankan list + tampilkan hint kecil. */}
    {loading && items.length === 0 && <div className="muted small" data-testid="bordir-shopee-loading">Memuat…</div>}
    {refreshing && items.length > 0 && <div className="muted small" style={{opacity:.65}} data-testid="bordir-shopee-refreshing">Menyegarkan…</div>}
    {!loading && !refreshing && filtered.length === 0 && !err && <div className="muted small" data-testid={`bordir-shopee-empty-${section}`}>
      {section === "antrean" && <>Belum ada antrean baru. Kartu akan muncul saat pekerjaan Shopee dengan proses <b>Bordir</b> atau <b>Bordir + Sablon DTF</b> diteruskan dari Gudang.</>}
      {section === "sedang" && <>Belum ada pekerjaan yang sedang Anda kerjakan. Klik <b>Kerjakan</b> pada kartu di tab Antrean untuk mulai.</>}
      {section === "riwayat" && <>Belum ada pekerjaan yang Anda selesaikan hari ini.</>}
    </div>}
    <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(320px, 1fr))", gap:16}}
      data-testid={`bordir-shopee-grid-${section}`}>
      {filtered.map(job => <ShopeeErrorBoundary key={job?.id || Math.random()}
        testId={`bordir-shopee-card-boundary-${job?.id || "x"}`}
        label={`pesanan ${job?.order_number || "(tanpa nomor)"}`}>
        <ShopeeBordirCard job={job}
          onOpenPreview={(f)=>setPreviewFile(f)}
          onOpenPicker={(j, mode)=>setPickerJob({ job: j, mode })}
          onStartDesigning={handleStartDesigning}
          onDesignDone={handleDesignDone}
          busyStart={busyStartId === job.id}
          busyDone={busyDoneId === job.id}/>
      </ShopeeErrorBoundary>)}
    </div>
    {pickerJob && <ShopeeFilePickerModal job={pickerJob.job} mode={pickerJob.mode}
      onOpenPreview={(f)=>{ setPreviewFile(f); setPickerJob(null); }}
      onDownloadFile={null}
      dlBusyIds={new Set()}
      dlErrsById={{}}
      dlOptimisticIds={new Set()}
      snapshotKey="bordir_forwarded_snapshot"
      onClose={()=>setPickerJob(null)}/>}
    {previewFile && <ShopeeImagePreviewModal file={previewFile} onClose={()=>setPreviewFile(null)}/>}
    {pointsJob && <DesignPointsDialog target={pointsJob.target} label={`${pointsJob.job.order_number} · ${pointsJob.job.design_title || ""}`}
      onSubmit={(designs) => submitDesignDone(pointsJob.job, designs)} onClose={() => setPointsJob(null)} />}
  </section>;
}


// ============================================================================
// Iter 326 — Antrean Operator Bordir · Shopee.
// Kartu yang desainnya SELESAI (bordir_state=design_done) masuk ke sini. Operator
// Bordir dapat memeriksa detail & Preview mockup. Tidak ada tombol Unduh.
// Tidak memengaruhi alur Offline.
// ============================================================================
export function ShopeeBordirOperatorSection({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState("");
  const [previewFile, setPreviewFile] = useState(null);
  const [pickerJob, setPickerJob] = useState(null);
  const [q, setQ] = useState("");
  // Iter 481 — Track busy state untuk tombol Kerjakan per-kartu.
  const [busyStartId, setBusyStartId] = useState(null);
  // Iter 483 — Track busy state untuk tombol Selesai per-kartu.
  const [busyDoneId, setBusyDoneId] = useState(null);
  const [titikJobId, setTitikJobId] = useState(null);  // Iter 617 — isi titik bila tidak terbaca otomatis
  // Iter 482 — Section switcher ala Operator Bordir Offline:
  //   antrean  = kartu bordir_state=design_done (siap diambil)
  //   sedang   = kartu bordir_state=executing   (sedang dikerjakan)
  //   riwayat  = kartu bordir_state=done        (sudah diselesaikan hari ini)
  const [opSection, setOpSection] = useState("antrean");
  // Iter 399 — Guard inflight: cegah dua request paralel (klik Muat Ulang +
  // auto-refresh berjalan bersamaan). Simpan hasil terakhir supaya UI tidak
  // "berkedip Memuat…" di tiap refresh — only kosongkan saat first-load.
  const inflightRef = useRef(false);
  // Iter 480 — Track AbortController untuk cleanup unmount.
  const abortRef = useRef(null);

  const load = async (silent = false) => {
    if (inflightRef.current) return;
    inflightRef.current = true;
    if (silent) setRefreshing(true); else { setLoading(true); setErr(""); }
    // Iter 478 — Timeout 15 detik + AbortController untuk cegah stuck "Memuat…"
    //   bila backend hang / jaringan putus.
    const controller = new AbortController();
    const timer = setTimeout(() => { try { controller.abort(); } catch {} }, 15_000);
    abortRef.current = controller;
    try {
      const r = await axios.get(`${API}/shopee/embroidery/operator-queue`, {
        withCredentials: true,
        params: { include_done: true },
        timeout: 15_000,
        signal: controller.signal,
      });
      if (Array.isArray(r?.data?.items)) setItems(r.data.items);
      if (!silent) setErr("");
    } catch (e) {
      const isTimeout = e?.code === "ECONNABORTED" || e?.name === "CanceledError" || e?.message === "canceled";
      const detail = isTimeout
        ? "Permintaan timeout (>15 detik). Data terakhir dipertahankan."
        : (e?.response?.data?.detail || e?.message || "Gagal memuat (data terakhir dipertahankan).");
      setErr(silent ? `Auto-refresh gagal: ${detail}` : detail);
    } finally {
      clearTimeout(timer);
      if (abortRef.current === controller) abortRef.current = null;
      setLoading(false); setRefreshing(false);
      inflightRef.current = false;
    }
  };
  // Iter 480 — Cleanup: abort fetch + reset inflight saat unmount.
  useEffect(() => {
    load(false);
    return () => {
      try { abortRef.current?.abort(); } catch {}
      inflightRef.current = false;
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);
  // Iter 399 — Auto-refresh wajib SILENT: jangan clear items + jangan tampilkan
  //   skeleton "Memuat…". Hanya indikator kecil "Menyegarkan…".
  useAutoRefresh({ enabled: true, intervalMs: 30_000, onRefresh: () => load(true) });
  // Iter 650 — Refresh otomatis saat sidik antrean Operator Bordir berubah (LiveQueueBar).
  useEffect(() => {
    const h = () => load(true);
    window.addEventListener("bordir-op-queue-changed", h);
    return () => window.removeEventListener("bordir-op-queue-changed", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = items.filter(j => {
    // Iter 466 — Skip job korup di operator queue.
    if (!j || !j.id) return false;
    if (!q.trim()) return true;
    const t = q.trim().toLowerCase();
    return (j.order_number || "").toLowerCase().includes(t)
      || (j.customer_account || "").toLowerCase().includes(t)
      || (j.design_title || "").toLowerCase().includes(t)
      || (j.pic_designer_name || "").toLowerCase().includes(t)
      || (j.bordir_design_done_by || "").toLowerCase().includes(t)
      || (j.bordir_execution_pic_name || "").toLowerCase().includes(t);
  });

  // Iter 482/483 — Hitung KPI ala Offline Operator Bordir dari items (bukan filtered)
  //   supaya counter tetap akurat saat user memakai search.
  //   • menunggu    = bordir_state design_done (siap diambil)
  //   • dikerjakan  = bordir_state executing
  //   • selesai_today = bordir_state done DAN bordir_executed_at di hari ini (Asia/Jakarta)
  //   • hampir / terlambat = 0 (Shopee tidak punya deadline).
  const kpi = useMemo(() => {
    let menunggu = 0, dikerjakan = 0, selesai_today = 0;
    // Hari ini di zona Asia/Jakarta (sv-SE → YYYY-MM-DD).
    const todayJakarta = new Date().toLocaleDateString("sv-SE", { timeZone: "Asia/Jakarta" });
    for (const j of items) {
      if (!j || !j.id) continue;
      const st = j.bordir_effective_state || j.bordir_state;
      if (st === "design_done") menunggu += 1;
      else if (st === "executing") dikerjakan += 1;
      else if (st === "done") {
        const ts = j.bordir_executed_at;
        if (ts) {
          try {
            const dayJakarta = new Date(ts).toLocaleDateString("sv-SE", { timeZone: "Asia/Jakarta" });
            if (dayJakarta === todayJakarta) selesai_today += 1;
          } catch { /* noop */ }
        }
      }
    }
    return { menunggu, dikerjakan, selesai_today, hampir: 0, terlambat: 0 };
  }, [items]);

  // Iter 482/483 — Split filtered items by section pakai bordir_state.
  //   Riwayat: hanya tampilkan "done" hari ini (Asia/Jakarta) agar tetap ringkas.
  const sectionItems = useMemo(() => {
    if (opSection === "antrean") {
      return filtered.filter(j => (j.bordir_effective_state || j.bordir_state) === "design_done");
    }
    if (opSection === "sedang") {   // hanya pekerjaan aktif milik operator yang login (pemegang / ditugaskan)
      return filtered.filter(j => (j.bordir_effective_state || j.bordir_state) === "executing"
        && !!teamRole(j.bordir_team, j.bordir_execution_pic_name || "", user?.name || ""));
    }
    if (opSection === "semua") {
      return filtered.filter(j => (j.bordir_effective_state || j.bordir_state) === "executing" || j.bordir_team?.waiting);
    }
    // Riwayat: done & diselesaikan hari ini.
    const todayJakarta = new Date().toLocaleDateString("sv-SE", { timeZone: "Asia/Jakarta" });
    return filtered.filter(j => {
      const st = j.bordir_effective_state || j.bordir_state;
      if (st !== "done") return false;
      const ts = j.bordir_executed_at;
      if (!ts) return false;
      try {
        return new Date(ts).toLocaleDateString("sv-SE", { timeZone: "Asia/Jakarta" }) === todayJakarta;
      } catch { return false; }
    });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [filtered, opSection]);

  // Iter 481 — Operator Bordir mengambil pekerjaan (bordir_state=design_done →
  //   executing). Backend CAS → atomic + idempoten; 409 kalau sudah diambil
  //   operator lain. Setelah sukses, silent reload supaya kartu update tanpa
  //   skeleton flicker.
  const handleOperatorStartExecuting = async (job) => {
    if (busyStartId) return;
    if (!job || !job.id) return;
    setBusyStartId(job.id);
    try {
      await axios.post(`${API}/shopee/${job.id}/bordir/operator-start`, null, { withCredentials: true });
      await load(true);
      notify.success({ title: "Pekerjaan Diambil", message: `${job.order_number} · ${job.customer_account || "-"}` });
    } catch (e) {
      notify.error({ title: "Gagal mengambil pekerjaan",
        message: e?.response?.data?.detail || e?.message || "Terjadi kesalahan tidak diketahui." });
      // Jika 409 (sudah diambil orang lain), refresh agar list sinkron.
      if (e?.response?.status === 409) try { await load(true); } catch {}
    } finally { setBusyStartId(null); }
  };

  // Iter 483 — Operator Bordir menyelesaikan pekerjaan (bordir_state=executing →
  //   done). Konfirmasi dulu (sama pola dengan handleDesignDone), lalu CAS
  //   atomic+idempoten di backend. Kartu akan keluar dari tab Dikerjakan dan
  //   masuk ke tab Riwayat setelah reload senyap.
  const [cancelJob, setCancelJob] = useState(null);
  const handleOperatorFinish = async (job) => {
    if (busyDoneId) return;
    if (!job || !job.id) return;
    const ok = await notify.confirm({
      title: "Tandai pekerjaan selesai?",
      message: `Pekerjaan bordir untuk ${job.order_number} dinyatakan SELESAI. Kartu akan keluar dari tab Dikerjakan dan masuk ke Riwayat hari ini.`,
      primaryLabel: "Ya, Selesai",
      secondaryLabel: "Batal",
      tone: "success",
    });
    if (!ok) return;
    setBusyDoneId(job.id);
    try {
      await axios.post(`${API}/shopee/${job.id}/bordir/operator-done`, null, { withCredentials: true });
      window.dispatchEvent(new Event("bordir-progress-saved"));
      await load(true);
      notify.success({ title: "Pekerjaan Selesai", message: `${job.order_number} · masuk Riwayat hari ini.` });
      try {  // Iter 617 — form titik hanya muncul bila titik/pcs tidak terbaca otomatis
        const f = await axios.get(`${API}/bordir-tasks/titik-form`, { params: { kind: "shopee", ref_id: job.id }, withCredentials: true });
        if (f.data?.needed) setTitikJobId(job.id);
      } catch {}
    } catch (e) {
      notify.error({ title: "Gagal menyelesaikan pekerjaan",
        message: e?.response?.data?.detail || e?.message || "Terjadi kesalahan tidak diketahui." });
      // Konflik state → refresh agar sinkron.
      if (e?.response?.status === 409) try { await load(true); } catch {}
    } finally { setBusyDoneId(null); }
  };

  return <section className="shopee-theme" data-theme-scope="shopee" data-testid="bordir-operator-shopee-section" style={{marginTop:24}}>
    <ShopeeThemeStyles/>
    {cancelJob && <BordirCancelDialog label={`Pesanan ${cancelJob.order_number}`} onClose={() => setCancelJob(null)}
      onSubmit={async (body) => {
        try { await axios.post(`${API}/shopee/${cancelJob.id}/bordir/operator-cancel`, body, { withCredentials: true }); }
        catch (e) { if (e?.response?.status === 409) { setCancelJob(null); notify.warning({ title: "Status pekerjaan berubah", message: e?.response?.data?.detail || "Muat ulang." }); await load(true); return; } throw e; }
        setCancelJob(null); await load(true);
        notify.success({ title: "Pekerjaan dibatalkan", message: `${cancelJob.order_number} kembali ke antrean dan dapat diambil operator lain.` });
      }}/>}
    {titikJobId && <TitikInputSheet kind="shopee" refId={titikJobId} onClose={() => setTitikJobId(null)} tid="bordir-op-shopee-titik"/>}
    {/* Iter 482 — Header ringkas: judul + total + tombol muat ulang. */}
    <div style={{display:"flex", gap:8, alignItems:"center", marginBottom:12, flexWrap:"wrap"}}>
      <h3 style={{margin:0, display:"flex", alignItems:"center", gap:8}}>
        <Layers size={18} style={{color:"var(--tf-shopee)"}}/> Antrean Operator Bordir · Shopee
        <span className="muted small" style={{fontWeight:400}}>({items.length})</span>
      </h3>
      <button type="button" className="soft-btn small-btn" onClick={() => load(false)} data-testid="bordir-op-shopee-refresh" disabled={refreshing || loading}>
        <RefreshCw size={12} style={refreshing ? {animation:"spin 1s linear infinite"} : undefined}/> Muat ulang
      </button>
    </div>

    {/* Iter 482 — KPI tiles ala Operator Offline (Antrean · Dikerjakan · Selesai hari ini).
        Hampir deadline / Terlambat ditampilkan sebagai meta ringkas (Shopee tanpa deadline → nilai 0). */}
    <div className="op-stats-row" data-testid="bordir-op-shopee-stats-row" role="group" aria-label="Statistik antrean Operator Bordir Shopee">
      <button type="button" className={`op-stat ${opSection==="antrean"?"active":""}`}
        onClick={()=>setOpSection("antrean")} data-testid="bordir-op-shopee-stat-antrean">
        <span className="op-stat-label">Antrean</span>
        <span className="op-stat-value op-stat-value-plain" data-testid="bordir-op-shopee-stat-antrean-value">{kpi.menunggu}</span>
      </button>
      <button type="button" className={`op-stat ${opSection==="sedang"?"active":""}`}
        onClick={()=>setOpSection("sedang")} data-testid="bordir-op-shopee-stat-dikerjakan">
        <span className="op-stat-label">Dikerjakan</span>
        <span className="op-stat-value op-stat-value-active" data-testid="bordir-op-shopee-stat-dikerjakan-value">{kpi.dikerjakan}</span>
      </button>
      <button type="button" className={`op-stat ${opSection==="riwayat"?"active":""}`}
        onClick={()=>setOpSection("riwayat")} data-testid="bordir-op-shopee-stat-selesai">
        <span className="op-stat-label">Selesai hari ini</span>
        <span className="op-stat-value op-stat-value-done" data-testid="bordir-op-shopee-stat-selesai-value">{kpi.selesai_today}</span>
      </button>
      <div className="op-stats-meta" data-testid="bordir-op-shopee-stats-meta" aria-live="polite" style={opSection==="riwayat"?{display:"none"}:undefined}>
        <span className={kpi.hampir>0?"op-stats-meta-warn":""}>Hampir deadline <b>{kpi.hampir}</b></span>
        <span className="op-stats-meta-dot" aria-hidden="true">·</span>
        <span className={kpi.terlambat>0?"op-stats-meta-late":""}>Terlambat <b>{kpi.terlambat}</b></span>
      </div>
    </div>

    {/* Iter 482 — Tab Antrean · Dikerjakan · Riwayat sama persis dengan Offline. */}
    <nav className="op-section-tabs op-section-tabs-equal" role="tablist" data-testid="bordir-op-shopee-section-tabs">
      <button type="button" role="tab" className={`op-section-tab ${opSection==="antrean"?"active":""}`}
        data-testid="bordir-op-shopee-tab-antrean" onClick={()=>setOpSection("antrean")}>
        <span>Antrean</span>
        <span className="op-section-tab-count" data-testid="bordir-op-shopee-tab-antrean-count">{kpi.menunggu}</span>
      </button>
      <button type="button" role="tab" className={`op-section-tab ${opSection==="sedang"?"active":""}`}
        data-testid="bordir-op-shopee-tab-sedang" onClick={()=>setOpSection("sedang")}>
        <span>Dikerjakan</span>
        <span className="op-section-tab-count" data-testid="bordir-op-shopee-tab-sedang-count">{kpi.dikerjakan}</span>
      </button>
      <button type="button" role="tab" className={`op-section-tab ${opSection==="semua"?"active":""}`}
        data-testid="bordir-op-shopee-tab-semua" onClick={()=>setOpSection("semua")}>
        <span>Semua Pekerjaan</span>
      </button>
      <button type="button" role="tab" className={`op-section-tab ${opSection==="riwayat"?"active":""}`}
        data-testid="bordir-op-shopee-tab-riwayat" onClick={()=>setOpSection("riwayat")}>
        <span>Riwayat</span>
      </button>
    </nav>

    {/* Iter 482 — Search ala Offline.
        Iter 511 — Tombol Scan Barcode (kamera) di sebelah kanan input, hanya tampil
        di tab Antrean (konsisten dengan Operator Bordir Offline). Hasil scan hanya
        meng-isi field pencarian — tidak mengubah status pekerjaan. Scanner fisik
        (USB keyboard emulation) tetap bekerja normal karena fokus input dibiarkan. */}
    <div className="op-search-wrap" data-testid="bordir-op-shopee-search-wrap">
      <Search size={16} className="op-search-icon" aria-hidden="true"/>
      <input
        type="search"
        className="op-search-input"
        placeholder="Cari nomor/akun/judul/Operator/desainer"
        value={q}
        onChange={e=>setQ(e.target.value)}
        data-testid="bordir-op-shopee-search"
        aria-label="Cari nomor atau akun"
      />
      {q && <button type="button" className="op-search-clear" onClick={()=>setQ("")}
        aria-label="Bersihkan pencarian" data-testid="bordir-op-shopee-search-clear">
        <X size={14}/>
      </button>}
      {/* Iter 511b — Scanner tersedia di semua tab (Antrean · Dikerjakan · Riwayat)
          agar operator dapat cepat memfilter kartu di tab manapun via barcode nota. */}
      <ShopeeBordirScanButton user={user} onFound={(num)=>setQ(num)}/>
    </div>

    {err && <div className="warn-box small" style={{marginTop:12}}>{err}</div>}
    {/* Iter 471 — Skeleton "Memuat…" HANYA saat first-load. Saat auto-refresh,
        pertahankan list dan tampilkan hint kecil "Menyegarkan…". */}
    {loading && items.length === 0 && <div className="muted small" data-testid="bordir-op-shopee-loading" style={{marginTop:12}}>Memuat…</div>}
    {refreshing && items.length > 0 && <div className="muted small" style={{opacity:.65, marginTop:12}} data-testid="bordir-op-shopee-refreshing">Menyegarkan…</div>}

    {user?.team === "Operator Bordir" && opSection !== "riwayat" && <ClaimLookup/>}
    {user?.team === "Operator Bordir" && opSection !== "riwayat" && <MyBordirTasks/>}
    {opSection === "riwayat" ? (
      <>
        {!loading && !refreshing && sectionItems.length === 0 && !err && (
          <div className="muted small" data-testid="bordir-op-shopee-empty-riwayat" style={{marginTop:16}}>
            Belum ada riwayat Operator Bordir Shopee untuk hari ini.
          </div>
        )}
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(320px, 1fr))", gap:16, marginTop:12}}>
          {sectionItems.map(job => <ShopeeErrorBoundary key={job?.id || Math.random()}
            testId={`bordir-op-shopee-card-boundary-${job?.id || "x"}`}
            label={`pesanan ${job?.order_number || "(tanpa nomor)"}`}>
            <ShopeeBordirOperatorCard job={job}
              user={user}
              onOpenPreview={(f)=>setPreviewFile(f)}
              onOpenPicker={(j, mode)=>setPickerJob({ job: j, mode })}
              onStartExecuting={handleOperatorStartExecuting}
              onFinishExecuting={handleOperatorFinish}
              onCancelExecuting={(j)=>setCancelJob(j)}
              busyStart={busyStartId === job.id}
              busyDone={busyDoneId === job.id}
              sectionMode={opSection} onChanged={() => load(true)}/>
          </ShopeeErrorBoundary>)}
        </div>
      </>
    ) : (
      <>
        {!loading && !refreshing && sectionItems.length === 0 && !err && (
          <div className="muted small" data-testid={`bordir-op-shopee-empty-${opSection}`} style={{marginTop:16}}>
            {opSection === "antrean"
              ? <>Belum ada kartu dari Desain Bordir. Kartu masuk otomatis setelah desainer menekan <b>Selesai</b> di antrean di atas.</>
              : "Belum ada pekerjaan yang sedang dikerjakan oleh Operator Bordir."}
          </div>
        )}
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(320px, 1fr))", gap:16, marginTop:12}}>
          {sectionItems.map(job => <ShopeeErrorBoundary key={job?.id || Math.random()}
            testId={`bordir-op-shopee-card-boundary-${job?.id || "x"}`}
            label={`pesanan ${job?.order_number || "(tanpa nomor)"}`}>
            <ShopeeBordirOperatorCard job={job}
              user={user}
              onOpenPreview={(f)=>setPreviewFile(f)}
              onOpenPicker={(j, mode)=>setPickerJob({ job: j, mode })}
              onStartExecuting={handleOperatorStartExecuting}
              onFinishExecuting={handleOperatorFinish}
              onCancelExecuting={(j)=>setCancelJob(j)}
              busyStart={busyStartId === job.id}
              busyDone={busyDoneId === job.id}
              sectionMode={opSection} onChanged={() => load(true)}/>
          </ShopeeErrorBoundary>)}
        </div>
      </>
    )}

    {pickerJob && <ShopeeFilePickerModal job={pickerJob.job} mode={pickerJob.mode}
      onOpenPreview={(f)=>{ setPreviewFile(f); setPickerJob(null); }}
      onDownloadFile={null}
      dlBusyIds={new Set()}
      dlErrsById={{}}
      dlOptimisticIds={new Set()}
      snapshotKey="bordir_forwarded_snapshot"
      onClose={()=>setPickerJob(null)}/>}
    {previewFile && <ShopeeImagePreviewModal file={previewFile} onClose={()=>setPreviewFile(null)}/>}
  </section>;
}

function ShopeeBordirOperatorCard({ job, user, onOpenPreview, onOpenPicker, onStartExecuting, onFinishExecuting, onCancelExecuting, busyStart, busyDone, sectionMode, onChanged }) {
  // Iter 511 — State lokal untuk modal Laporkan Kendala (dideklarasikan sebelum
  //   early-return guard agar aturan rules-of-hooks tidak terlanggar).
  const [reportOpen, setReportOpen] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);  // Multi-Operator Bordir Shopee (mekanisme Offline yang sama)
  const [finishDlg, setFinishDlg] = useState(false);
  const [claimSum] = useClaimSummary(job?.id ? [{ order_number: job.order_number, product_id: `shopee:${job.id}` }] : [],
    !!job?.id && (job.bordir_effective_state || job.bordir_state) === "executing" && job.bordir_execution_pic_name === user?.name);
  // Iter 466 — Guard: hindari crash pada job korup / tanpa id.
  if (!job || !job.id) return null;
  const snap = job.bordir_forwarded_snapshot || [];
  // Iter 484 — Semua file image di snapshot → URL thumbnail list. Primary =
  //   gambar pertama; sisanya jadi fallback bila primary 403/404/corrupt.
  const imageSnaps = snap.filter(s => (s.content_type || "").startsWith("image"));
  const thumbUrl = imageSnaps.length
    ? `${API}/shopee/files/${imageSnaps[0].file_id}/thumbnail?size=400`
    : null;
  const fallbackUrls = imageSnaps.slice(1).map(s => `${API}/shopee/files/${s.file_id}/thumbnail?size=400`);
  const firstImage = imageSnaps[0] || snap[0];
  const stop = (e) => e.stopPropagation();
  const doPreview = (e) => {
    stop(e);
    if (snap.length > 1) { onOpenPicker(job, "preview"); return; }
    const target = firstImage || snap[0];
    if (target && onOpenPreview) onOpenPreview({
      file_id: target.file_id, filename: target.filename,
      title: target.title, content_type: target.content_type,
    });
  };
  // Iter 481/483 — Logic tombol di kartu Operator Bordir Shopee:
  //   - Belum diambil (design_done)                   → tombol "Kerjakan"
  //   - Sedang dikerjakan oleh operator yg login      → tombol "Selesai"
  //   - Sedang dikerjakan oleh operator lain          → chip nama operator (tanpa tombol)
  //   - Sudah selesai (done)                           → chip "Selesai"
  const isOperatorTeam = user?.team === "Operator Bordir";
  const effState = job.bordir_effective_state || job.bordir_state;
  const isExecuting = effState === "executing";
  const isDone = effState === "done";
  const execPicName = job.bordir_execution_pic_name || "";
  const execByMe = isExecuting && execPicName && user?.name && execPicName.toLowerCase() === String(user.name).toLowerCase();
  const waiting = !!job.bordir_team?.waiting;
  const role = teamRole(job.bordir_team, execPicName, user?.name || "");
  const canTake = isOperatorTeam && !isExecuting && !isDone && effState === "design_done" && !waiting && sectionMode !== "semua";
  const canFinish = isOperatorTeam && execByMe && sectionMode !== "semua";
  // Border color: biru saat executing, hijau tua saat done, hijau terang saat siap.
  const borderColor = isDone ? "#047857" : (isExecuting ? "#1d4ed8" : "#15803d");
  return <div className="card shopee-card shopee-wh-card" data-accent={isDone ? "ready" : isExecuting ? "work" : "queue"} style={{padding:0, overflow:"hidden", borderLeft:`4px solid ${borderColor}`, display:"flex", flexDirection:"column"}}
    data-testid={`bordir-op-shopee-card-${job.id}`}>
    <ShopeeCancelTag job={job}/>
    <div className="shopee-wh-top">
      <ShopeeMockupThumb thumbUrl={thumbUrl} fallbackUrls={fallbackUrls} iconSize={28}
        testId={`bordir-op-shopee-thumb-${job.id}`}/>
      <div className="shopee-wh-info">
        <div className="shopee-wh-badges">
          <span className="shopee-badge shopee-wh-source-badge">SHOPEE</span>
          {isDone ? (
            <span className="shopee-status-badge" style={{background:"#d1fae5", color:"#047857"}}
              data-testid={`bordir-op-shopee-state-${job.id}`}>Selesai</span>
          ) : isExecuting ? (
            <span className="shopee-status-badge" style={{background:"#dbeafe", color:"#1d4ed8"}}
              data-testid={`bordir-op-shopee-state-${job.id}`}>Sedang Dikerjakan</span>
          ) : (
            <span className="shopee-status-badge" style={{background:"#dcfce7", color:"#166534"}}
              data-testid={`bordir-op-shopee-state-${job.id}`}>Siap Dibordir</span>
          )}
        </div>
        <div className="shopee-order-number" data-testid={`bordir-op-shopee-order-${job.id}`}>{job.order_number}</div>
        <BordirShipBy job={job}/>
<TokopediaAttachments orderNumber={job.order_number}/>
        {job.customer_account && <div className="shopee-account-line">
          <span className="shopee-account-label">Akun:</span> <b>{job.customer_account}</b>
        </div>}
        {job.design_title && <div className="shopee-design-title">{job.design_title}</div>}
        {job.process && <div className="shopee-process-chip">
          <Layers size={11} style={{verticalAlign:"-2px"}}/> {SHOPEE_PROCESS_LABEL[job.process] || job.process}
        </div>}
        <div className="shopee-wh-meta">
          Desainer: <b>{job.pic_designer_name}</b> · {job.job_type === "lembur" ? "Lembur" : "Reguler"}
        </div>
        {job.bordir_prior_progress && <div className="shopee-wh-meta" style={{color:"#b45309", fontWeight:700}} data-testid={`bordir-op-shopee-prior-${job.id}`}>
          {priorProgressText(job.bordir_prior_progress)}</div>}
        {job.bordir_design_done_at && <div className="shopee-wh-meta" data-testid={`bordir-op-shopee-done-info-${job.id}`} style={{color:"#15803d", fontWeight:700}}>
          ✅ Desain selesai {fmtDateTime(job.bordir_design_done_at)} · <b>{job.bordir_design_done_by || "-"}</b>
        </div>}
        {isExecuting && execPicName && <div className="shopee-wh-meta" data-testid={`bordir-op-shopee-exec-info-${job.id}`} style={{color:"#1d4ed8", fontWeight:700}}>
          🧵 Operator: <b>{execPicName}</b>{job.bordir_execution_started_at ? ` · ${fmtDateTime(job.bordir_execution_started_at)}` : ""}
        </div>}
        {isDone && job.bordir_executed_by_name && <div className="shopee-wh-meta" data-testid={`bordir-op-shopee-finished-info-${job.id}`} style={{color:"#047857", fontWeight:700}}>
          🏁 Diselesaikan oleh <b>{job.bordir_executed_by_name}</b>{job.bordir_executed_at ? ` · ${fmtDateTime(job.bordir_executed_at)}` : ""}
        </div>}
      </div>
    </div>
    <div className="shopee-wh-actions" onClick={e => e.stopPropagation()}>
      <button type="button" className="soft-btn shopee-wh-btn shopee-wh-btn-preview"
        onClick={doPreview} disabled={!snap.length}
        data-testid={`bordir-op-shopee-preview-${job.id}`}>
        <ImgIcon size={14}/> Preview
      </button>
      {canTake && <button type="button" className="primary shopee-wh-btn"
        onClick={(e)=>{ stop(e); onStartExecuting && onStartExecuting(job); }}
        disabled={!!busyStart}
        data-testid={`bordir-op-shopee-kerjakan-${job.id}`}>
        <CheckCircle2 size={14}/> {busyStart ? "Memproses…" : "Kerjakan"}
      </button>}
      {canFinish && <button type="button" className="primary shopee-wh-btn"
        onClick={(e)=>{ stop(e); if (needsFinishForm(claimSum[`${job.order_number}::shopee:${job.id}`], job.bordir_team, user?.name || "")) { setFinishDlg(true); return; } onFinishExecuting && onFinishExecuting(job); }}
        disabled={!!busyDone}
        style={{background:"#047857", borderColor:"#047857"}}
        data-testid={`bordir-op-shopee-selesai-${job.id}`}>
        <CheckCircle2 size={14}/> {busyDone ? "Menyimpan…" : "Selesai"}
      </button>}
      {isExecuting && execByMe && isOperatorTeam && onCancelExecuting && <button type="button" className="soft-btn shopee-wh-btn"
        onClick={(e)=>{ stop(e); onCancelExecuting(job); }} disabled={!!busyDone} style={{color:"#b91c1c", borderColor:"#fca5a5"}}
        data-testid={`bordir-op-shopee-batalkan-${job.id}`}>Batalkan Pekerjaan</button>}
      {isExecuting && !execByMe && execPicName && <span className="shopee-wh-btn" style={{background:"#dbeafe", color:"#1d4ed8", textAlign:"center", fontWeight:700, padding:"8px 10px", borderRadius:6}}
        data-testid={`bordir-op-shopee-exec-by-other-${job.id}`}>
        {execPicName}
      </span>}
    </div>
    {isOperatorTeam && (isExecuting || waiting) && <TeamStatus team={job.bordir_team} holderName={isExecuting ? execPicName : ""} testid={`bordir-op-shopee-team-${job.id}`}/>}
    {isOperatorTeam && waiting && sectionMode !== "semua" && <div className="shopee-wh-actions" onClick={e => e.stopPropagation()} style={{paddingTop:0}}>
      <ResumeScanButton operator={user} user={user} onChanged={onChanged} testid={`bordir-op-shopee-resume-${job.id}`}/></div>}
    {isOperatorTeam && isExecuting && sectionMode === "sedang" && <BordirTeamActions orderNumber={job.order_number} productId={`shopee:${job.id}`} team={job.bordir_team}
      holderName={execPicName} meName={user?.name || ""} label={`${job.order_number} · ${job.design_title || ""}`} onChanged={onChanged} testid={`shopee-${job.id}`}/>}
    {finishDlg && <FinishWithClaim orderNumber={job.order_number} productId={`shopee:${job.id}`} label={`${job.order_number} · ${job.design_title || ""}`}
      onFinish={() => onFinishExecuting && onFinishExecuting(job)} onClose={() => { setFinishDlg(false); onChanged && onChanged(); }}/>}
    {isExecuting && job.bordir_split_used && (isOperatorTeam || !user?.team) && <div className="shopee-wh-actions" onClick={e => e.stopPropagation()} style={{paddingTop:0}}>
      <button type="button" className="soft-btn shopee-wh-btn" onClick={(e)=>{ stop(e); setSplitOpen(v => !v); }} aria-expanded={splitOpen}
        data-testid={`bordir-op-shopee-split-${job.id}`}>
        <Layers size={14}/> {execByMe ? (splitOpen ? "Tutup Bagi Pekerjaan" : "Bagi Pekerjaan") : (splitOpen ? "Tutup Tim Bordir" : "Lihat Tim Bordir")}
      </button>
    </div>}
    {splitOpen && isExecuting && <div onClick={e => e.stopPropagation()} style={{padding:"0 10px 10px"}}>
      <BordirSplitPanel orderNumber={job.order_number} productId={`shopee:${job.id}`}/>
    </div>}
    {/* Iter 511 — Laporkan Kendala (Shopee).
        Hanya aktif untuk pekerjaan yang berhak ditangani operator:
          • design_done (siap diambil) → canTake
          • executing oleh operator yg login → canFinish
        Supervisor (admin/owner) di luar scope — mereka pakai halaman audit terpisah.
        Workflow bordir TIDAK diubah oleh aksi ini. */}
    {(canTake || canFinish) && (
      <div className="shopee-wh-actions" onClick={e => e.stopPropagation()}
        style={{paddingTop:0, borderTop:"1px dashed #e2e8f0"}}>
        <button type="button" className="soft-btn shopee-wh-btn"
          onClick={(e)=>{ stop(e); setReportOpen(true); }}
          data-testid={`bordir-op-shopee-report-${job.id}`}
          style={{color:"#b91c1c", borderColor:"#fecaca"}}>
          <AlertTriangle size={14}/> Laporkan Kendala
        </button>
      </div>
    )}
    {reportOpen && <ShopeeReportKendalaModal job={job}
      actorName={user?.name || "—"}
      onClose={()=>setReportOpen(false)}/>}
  </div>;
}

// ============================================================================
// Iter 511 — Scanner Barcode (Shopee Operator Bordir)
// Reuse ScanMockupModal existing. Hasil scan hanya mengisi field pencarian via
// onFound(num). Scanner otomatis stop ketika modal ditutup. Scanner fisik (USB
// keyboard emulation) tetap bekerja karena input pencarian menerima keypress
// native tanpa diintercept.
// ============================================================================
function ShopeeBordirScanButton({ user, onFound }) {
  const [open, setOpen] = useState(false);
  const handleOpenOrder = React.useCallback((num) => {
    if (num && onFound) onFound(num);
    setOpen(false);
  }, [onFound]);
  return (
    <>
      <button type="button" className="op-scan-barcode-btn" onClick={()=>setOpen(true)}
        data-testid="bordir-op-shopee-scan-btn" aria-label="Scan Cari Barang">
        <Camera size={14}/> <span>Scan Cari Barang</span>
      </button>
      <ScanMockupModal
        open={open}
        onClose={()=>setOpen(false)}
        user={user}
        onOpenOrder={handleOpenOrder}
        searchOnly
      />
    </>
  );
}

// ============================================================================
// Iter 511 — Modal Laporkan Kendala (Shopee)
// Konsisten dengan Offline. POST ke /api/shopee/{job_id}/issues yang menulis
// ke koleksi `product_issues` (source="shopee") agar Owner/Admin tetap bisa
// memantau lintas sumber via /api/issues.
// Guard double-click: setBusy sepanjang request + backend idempotent 60 detik.
// ============================================================================
function ShopeeReportKendalaModal({ job, actorName, onClose }) {
  const [category, setCategory] = useState("Mesin / Alat");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (busy) return;
    const trimmed = note.trim();
    if (!trimmed) {
      notify.warning({ title: "Catatan wajib diisi", message: "Mohon jelaskan detail kendala yang ditemukan." });
      return;
    }
    setBusy(true);
    let backendOk = false;
    let idempotent = false;
    try {
      const r = await axios.post(
        `${API}/shopee/${encodeURIComponent(job.id)}/issues`,
        { category, note: trimmed, stage: "Bordir Shopee" },
        { withCredentials: true }
      );
      backendOk = true;
      idempotent = !!r?.data?.idempotent_hit;
    } catch (e) {
      // Fallback localStorage supaya tidak hilang bila offline.
      // eslint-disable-next-line no-console
      console.warn("Report kendala Shopee gagal:", e?.response?.data?.detail || e?.message);
    }
    try {
      const key = "tam_issue_reports_shopee_v1";
      const now = new Date().toISOString();
      const list = JSON.parse(localStorage.getItem(key) || "[]");
      list.push({
        at: now,
        source: "shopee",
        job_id: job.id,
        order_number: job.order_number,
        design_title: job.design_title || "",
        category, note: trimmed,
        reported_by: actorName,
        synced: backendOk,
      });
      localStorage.setItem(key, JSON.stringify(list.slice(-200)));
    } catch { /* ignore storage errors */ }
    setBusy(false);
    onClose();
    if (backendOk) {
      notify.success({
        title: idempotent ? "Laporan sudah tercatat" : "Kendala tercatat",
        message: idempotent
          ? "Laporan serupa baru saja terkirim dan sudah masuk ke pemantauan Owner."
          : `Laporan "${category}" untuk ${job.order_number} sudah dicatat ke server dan akan diteruskan ke admin.`,
      });
    } else {
      notify.warning({
        title: "Tersimpan offline",
        message: "Server tidak dapat dijangkau. Laporan disimpan lokal dan akan disinkron nanti.",
      });
    }
  };
  return (
    <div className="modal-overlay" data-testid="bordir-op-shopee-report-overlay" onClick={onClose}>
      <div className="modal op-report-modal" onClick={e=>e.stopPropagation()} data-testid="bordir-op-shopee-report-modal">
        <div className="modal-head">
          <h3><AlertTriangle size={18} style={{verticalAlign:"-3px",marginRight:6,color:"#b91c1c"}}/>Laporkan Kendala</h3>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Tutup"><X size={18}/></button>
        </div>
        <p className="muted small" style={{marginTop:4}}>
          Nota: <strong>{job.order_number}</strong>
          {job.design_title ? <> · Judul: <strong>{job.design_title}</strong></> : null}
          {" "}· Tahap: <strong>Bordir Shopee</strong>
        </p>
        <label className="field-label" style={{marginTop:14,display:"block"}}>Kategori kendala</label>
        <select data-testid="bordir-op-shopee-report-category" value={category} onChange={e=>setCategory(e.target.value)} style={{width:"100%",marginTop:6}} disabled={busy}>
          <option>Bahan</option>
          <option>Warna</option>
          <option>Ukuran</option>
          <option>Desain / Mockup</option>
          <option>Mesin / Alat</option>
          <option>Kekurangan Barang</option>
          <option>Lainnya</option>
        </select>
        <label className="field-label" style={{marginTop:14,display:"block"}}>Detail kendala <span className="required">*</span></label>
        <textarea
          data-testid="bordir-op-shopee-report-note"
          value={note}
          onChange={e=>setNote(e.target.value)}
          style={{width:"100%",marginTop:6,minHeight:88}}
          rows={4}
          placeholder="Jelaskan kendala secara singkat, mis. benang putus, mesin macet, dst."
          disabled={busy}
        />
        <div className="modal-foot" style={{marginTop:16}}>
          <button type="button" className="soft-btn" data-testid="bordir-op-shopee-report-cancel" onClick={onClose} disabled={busy}>Batal</button>
          <button type="button" className="primary" data-testid="bordir-op-shopee-report-submit" onClick={submit} disabled={busy}>
            {busy ? "Menyimpan…" : "Kirim Laporan"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Iter 323 — Antrean "Menunggu ACC" Shopee (section terpisah untuk Adit / Rido / Owner).
// Operator ACC-only (Adit) hanya melihat section ini — tidak mendapat akses stok,
// Preview/Unduh file, maupun kewenangan Rido lainnya.
// ============================================================================
export function ShopeeAccWaitingSection({ user, operator }) {
  const [queue, setQueue] = useState({ waiting: [], done: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("waiting"); // waiting | done
  const [busyId, setBusyId] = useState(null);
  const [q, setQ] = useState("");
  const isAccOnly = !!operator?.is_acc_only;
  // Iter 474 — Inflight guard: cegah double fetch (auto-refresh + klik manual).
  const inflightRef = useRef(false);

  const load = async (silent = false) => {
    if (inflightRef.current) return;
    inflightRef.current = true;
    if (silent) setRefreshing(true); else { setLoading(true); setErr(""); }
    // Iter 478 — Timeout 15 detik + AbortController untuk cegah stuck "Memuat…".
    const controller = new AbortController();
    const timer = setTimeout(() => { try { controller.abort(); } catch {} }, 15_000);
    try {
      const r = await axios.get(`${API}/shopee/warehouse/acc-queue`, {
        withCredentials: true,
        timeout: 15_000,
        signal: controller.signal,
      });
      // Iter 474 — Hanya overwrite queue kalau waiting/done benar-benar array.
      //   Response malformed JANGAN mengganti data lama → counter tidak jadi 0.
      const waiting = r?.data?.waiting;
      const done = r?.data?.done;
      if (Array.isArray(waiting) || Array.isArray(done)) {
        setQueue({
          waiting: Array.isArray(waiting) ? waiting : [],
          done: Array.isArray(done) ? done : [],
        });
      }
      if (!silent) setErr("");
    } catch (e) {
      const isTimeout = e?.code === "ECONNABORTED" || e?.name === "CanceledError" || e?.message === "canceled";
      const detail = isTimeout
        ? "Permintaan timeout (>15 detik). Data terakhir dipertahankan."
        : (e?.response?.data?.detail || "Gagal memuat antrean ACC Shopee (data terakhir dipertahankan).");
      setErr(silent ? `Auto-refresh gagal: ${detail}` : detail);
    } finally {
      clearTimeout(timer);
      inflightRef.current = false;
      if (silent) setRefreshing(false); else setLoading(false);
    }
  };
  useEffect(() => { load(false); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  // Iter 474 — Signature `useAutoRefresh` SEBELUMNYA salah: `(load, 30_000)` →
  //   hook terima `enabled=load` (truthy fn) & `intervalMs=30_000` sebagai deps.
  //   `onRefresh` undefined → auto-refresh TIDAK BERJALAN. Diperbaiki ke object
  //   form + silent + inflight guard.
  useAutoRefresh({ enabled: true, intervalMs: 30_000, onRefresh: () => load(true) });
  // Iter 646 — Sinkron dengan Scan Cepat ACC tanpa refresh manual.
  useEffect(() => {
    const h = () => load(true);
    window.addEventListener("acc-fast-done", h);
    return () => window.removeEventListener("acc-fast-done", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Iter 595 — Pisahkan "ACC Berhasil" dari "Berhasil Diteruskan ke Desain Bordir"; gagal → alasan + Coba Lagi (idempoten).
  const reportBordirForward = async (job, fwd) => {
    const who = `${job.order_number} · ${job.customer_account || "-"}`;
    if (!fwd || fwd.state === "not_required") { notify.success({ title: "ACC Berhasil", message: who }); return; }
    if (fwd.state === "forwarded") { notify.success({ title: "ACC Berhasil · Berhasil Diteruskan ke Desain Bordir", message: who }); return; }
    const again = await notify.confirm({ title: "ACC Berhasil, tetapi GAGAL diteruskan ke Desain Bordir",
      message: `${who}\nAlasan: ${fwd.reason || "tidak diketahui"}. ACC tetap tersimpan. Coba lagi tidak akan menggandakan pekerjaan.`,
      primaryLabel: "Coba Lagi", secondaryLabel: "Nanti" });
    if (again) await retryBordirForward(job);
  };
  const retryBordirForward = async (job) => {
    try {
      const r = await axios.post(`${API}/shopee/${job.id}/bordir/retry-forward`, null, { withCredentials: true });
      await load(true);
      await reportBordirForward(job, r.data?.bordir_forward);
      const cut = r.data?.acc_stock_cut;
      if (cut?.message && !["cut", "manual_done"].includes(cut.status)) notify.warning({ title: cut.label || "Perlu Potong Manual", message: cut.message });
    } catch (e) {
      notify.error({ title: "Gagal mencoba ulang penerusan", message: e?.response?.data?.detail || e.message || "Silakan coba lagi." });
    }
  };

  const handleAccGoods = async (job) => {
    if (busyId) return;
    if (job.goods_acc_at) return;
    const unrec = !!job.print_unrecorded;
    if (unrec && !job.acc_eligible) {
      notify.error({ title: "ACC diblokir", message: (job.acc_block_reasons || []).join("; ") || "Syarat ACC belum lengkap." });
      return;
    }
    const ok = await notify.confirm(unrec ? {
      title: "Status Cetak Belum Tercatat — konfirmasi pemeriksaan",
      message: `Pesanan ${job.order_number} belum memiliki catatan cetak mockup. Dengan melanjutkan, Anda menyatakan BARANG FISIK SUDAH DIPERIKSA sesuai mockup aktif. Catatan cetak tidak akan diisi otomatis.`,
      primaryLabel: "Ya, Fisik Sudah Diperiksa — ACC",
      secondaryLabel: "Batal",
      tone: "warning",
    } : {
      title: "ACC barang siap?",
      message: `Anda menyatakan BARANG FISIK untuk pesanan ${job.order_number} sudah DISIAPKAN sesuai mockup. Setelah ACC, pelaksana & waktu akan dicatat. Stok tidak dikurangi di tahap ini.`,
      primaryLabel: "Ya, ACC Barang Siap",
      secondaryLabel: "Batal",
      tone: "success",
    });
    if (!ok) return;
    setBusyId(job.id);
    try {
      const body = unrec ? { confirm_unrecorded_print: true, physical_checked: true } : null;
      const r = await axios.post(`${API}/shopee/${job.id}/acc-goods`, body, { withCredentials: true });
      // Iter 475 — Setelah response sukses, refetch SILENT (tanpa skeleton
      // "Memuat…") agar kartu langsung keluar dari antrean tanpa flicker.
      // Iter 646 — Refresh antrean di latar belakang; hasil ACC langsung ditampilkan.
      load(true);
      await reportBordirForward(job, r.data?.bordir_forward);
    } catch (e) {
      notify.error({ title: "Gagal ACC Barang", message: e?.response?.data?.detail || e.message || "Terjadi kesalahan tidak diketahui." });
    } finally { setBusyId(null); }
  };

  const list = (tab === "waiting" ? queue.waiting : queue.done);
  const filtered = list.filter(j => {
    const t = q.trim().toLowerCase();
    if (!t) return true;
    return (j.order_number || "").toLowerCase().includes(t)
      || (j.customer_account || "").toLowerCase().includes(t)
      || (j.design_title || "").toLowerCase().includes(t)
      || (j.pic_designer_name || "").toLowerCase().includes(t)
      || (j.mockup_printed_by || "").toLowerCase().includes(t);
  });

  return <section className="wh-section shopee-theme" data-testid="wh-shopee-acc-section">
    <ShopeeThemeStyles/>
    <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8, marginBottom:12}}>
      <div style={{display:"flex", gap:8, alignItems:"center", flexWrap:"wrap"}}>
        <span className="wh-badge wh-badge-shopee shopee-badge">SHOPEE</span>
        <h2 style={{margin:0, fontSize:16, fontWeight:700}}>
          {isAccOnly ? "Penyiapan Barang — ACC · Shopee" : "Menunggu ACC · Shopee"}
        </h2>
      </div>
      <div style={{display:"flex", gap:6, alignItems:"center"}}>
        <button type="button" className="soft-btn small-btn" onClick={()=>load(false)}
          data-testid="wh-shopee-acc-refresh" disabled={loading || refreshing} title="Perbarui data sekarang">
          <RefreshCw size={14} className={loading || refreshing ? "spin" : ""}/>
          {refreshing && !loading ? "Memuat ulang…" : "Muat Ulang"}
        </button>
      </div>
    </div>
    <div className="muted small" style={{marginBottom:10}}>
      {isAccOnly
        ? "Periksa fisik barang untuk mockup Shopee yang sudah dicetak Rido, lalu tekan ACC Barang Siap. Anda tidak dapat mencetak mockup, mengakses stok, atau memindahkan tahap."
        : "Rido menandai 'Mockup Dicetak' lebih dulu di tab Shopee. Setelah itu kartu muncul di sini untuk diperiksa fisik oleh Adit."}
    </div>
    <nav className="riwayat-range" role="tablist" aria-label="Status ACC Barang Shopee" data-testid="wh-shopee-acc-tabs" style={{marginBottom:10}}>
      <button type="button" role="tab" aria-selected={tab==="waiting"}
        className={`riwayat-range-btn ${tab==="waiting"?"active":""}`}
        onClick={()=>setTab("waiting")} data-testid="wh-shopee-acc-tab-waiting">
        Menunggu ACC <strong style={{marginLeft:4}}>{queue.waiting.length}</strong>
      </button>
      <button type="button" role="tab" aria-selected={tab==="done"}
        className={`riwayat-range-btn ${tab==="done"?"active":""}`}
        onClick={()=>setTab("done")} data-testid="wh-shopee-acc-tab-done">
        Sudah di-ACC <strong style={{marginLeft:4}}>{queue.done.length}</strong>
      </button>
      <div style={{marginLeft:"auto", position:"relative"}}>
        <Search size={12} style={{position:"absolute", left:8, top:"50%", transform:"translateY(-50%)", color:"#94a3b8"}}/>
        <input placeholder="Cari nomor/akun/judul/Operator" value={q} onChange={e=>setQ(e.target.value)}
          style={{padding:"6px 10px 6px 26px", border:"1px solid #cbd5e1", borderRadius:6, fontSize:12, minWidth:220}}
          data-testid="wh-shopee-acc-search"/>
      </div>
    </nav>
    {err && <div className="warn-box" data-testid="wh-shopee-acc-error">{err}</div>}
    {/* Iter 474 — Skeleton "Memuat…" HANYA saat first-load. Saat auto-refresh
        background, pertahankan list + hint kecil "Menyegarkan…". */}
    {loading && (queue.waiting.length === 0 && queue.done.length === 0) && <div className="muted small" data-testid="wh-shopee-acc-loading">Memuat…</div>}
    {refreshing && (queue.waiting.length > 0 || queue.done.length > 0) && <div className="muted small" style={{opacity:.65}} data-testid="wh-shopee-acc-refreshing">Menyegarkan…</div>}
    {!loading && !refreshing && filtered.length === 0 && !err && <div className="muted small" data-testid="wh-shopee-acc-empty">
      {tab === "waiting"
        ? "Belum ada antrean Menunggu ACC Shopee. Kartu muncul otomatis saat Rido menandai mockup sudah dicetak."
        : "Belum ada riwayat ACC Shopee."}
    </div>}
    <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(320px, 1fr))", gap:12}}>
      {filtered.map(job => <ShopeeAccWaitingCard key={job.id} job={job}
        onAcc={handleAccGoods} onRetry={retryBordirForward} busy={busyId === job.id} tab={tab}
        canAct={isAccOnly}/>)}
    </div>
  </section>;
}

function ShopeeAccWaitingCard({ job, onAcc, onRetry, busy, tab, canAct }) {
  const printed = !!job.mockup_printed_at;
  const accd = !!job.goods_acc_at;
  const snap = job.warehouse_sent_snapshot || [];
  // Thumbnail untuk konteks visual — Adit melihat gambar mockup untuk membandingkan
  // dengan fisik, tapi tidak dapat Preview/Unduh file (tombol tidak ditawarkan).
  let thumbUrl = null;
  for (const s of snap) {
    if ((s.content_type || "").startsWith("image")) { thumbUrl = `${API}/shopee/files/${s.file_id}/thumbnail?size=400`; break; }
  }
  const badge = accd
    ? { t: "SUDAH ACC", c: "#166534", bg: "#dcfce7" }
    : { t: "MENUNGGU ACC", c: "#92400e", bg: "#fef3c7" };
  return <div className="card shopee-card shopee-wh-card" style={{padding:0, overflow:"hidden", borderLeft:`4px solid ${badge.c}`, display:"flex", flexDirection:"column"}}
    data-testid={`wh-shopee-acc-card-${job.id}`}>
    <ShopeeCancelTag job={job}/>
    <div className="shopee-wh-top">
      <ShopeeMockupThumb thumbUrl={thumbUrl} iconSize={28}
        testId={`wh-shopee-acc-thumb-${job.id}`}
        onClickPreview={(url) => { try { window.open(url, "_blank", "noopener,noreferrer"); } catch { /* ignore */ } }}/>
      <div className="shopee-wh-info">
        <div className="shopee-wh-badges">
          <span className="shopee-badge shopee-wh-source-badge">SHOPEE</span>
          <span className="shopee-status-badge" style={{background:badge.bg, color:badge.c}}
            data-testid={`wh-shopee-acc-state-${job.id}`}>{badge.t}</span>
        </div>
        {job.customer_account && <div className="shopee-wh-account-primary" data-testid={`wh-shopee-acc-account-${job.id}`}>
          {job.customer_account}
        </div>}
        <div className="shopee-order-number" data-testid={`wh-shopee-acc-order-${job.id}`}>{job.order_number}</div>
<TokopediaAttachments orderNumber={job.order_number}/>
        {job.design_title && <div className="shopee-design-title">{job.design_title}</div>}
        {job.process && <div className="shopee-process-chip">
          <Layers size={11} style={{verticalAlign:"-2px"}}/> {SHOPEE_PROCESS_LABEL[job.process] || job.process}
        </div>}
        <div className="shopee-wh-meta">
          PIC Desainer: <b>{job.pic_designer_name}</b> · {job.job_type === "lembur" ? "Lembur" : "Reguler"}
        </div>
        {printed && <div className="shopee-wh-meta" data-testid={`wh-shopee-acc-printed-${job.id}`} style={{color:"#065f46", fontWeight:600}}>
          🖨 Mockup dicetak {fmtDateTime(job.mockup_printed_at)} · <b>{job.mockup_printed_by || "-"}</b>
        </div>}
        {!printed && (job.print_unrecorded || job.goods_acc_print_unrecorded) && <div className="shopee-wh-meta" data-testid={`wh-shopee-acc-print-unrecorded-${job.id}`} style={{color:"#b45309", fontWeight:700}}>
          Status Cetak Belum Tercatat
        </div>}
        {job.order_job_count > 1 && <div className="shopee-wh-meta" data-testid={`wh-shopee-acc-multi-${job.id}`}>
          Nota ini berisi {job.order_job_count} pekerjaan — periksa semua item
        </div>}
        {!accd && job.print_unrecorded && !job.acc_eligible && <div className="shopee-wh-meta" data-testid={`wh-shopee-acc-blocked-${job.id}`} style={{color:"#b91c1c", fontWeight:600}}>
          ACC diblokir: {(job.acc_block_reasons || []).join("; ")}
        </div>}
        {accd && <div className="shopee-wh-meta" data-testid={`wh-shopee-acc-done-${job.id}`} style={{color:"#15803d", fontWeight:700}}>
          ✅ Barang di-ACC {fmtDateTime(job.goods_acc_at)} · <b>{job.goods_acc_by || "-"}</b>
        </div>}
        {accd && job.bordir_state && <div className="shopee-wh-meta" data-testid={`wh-shopee-bordir-forwarded-${job.id}`}>➡️ Sudah diteruskan ke Desain Bordir</div>}
        {accd && !job.bordir_state && job.bordir_forward_status?.state === "failed" && (
          <div className="shopee-wh-meta" data-testid={`wh-shopee-bordir-forward-failed-${job.id}`} style={{color:"#b45309", fontWeight:700}}>
            ⚠️ Gagal diteruskan ke Desain Bordir: {job.bordir_forward_status.reason}
            {canAct && onRetry && <button type="button" className="soft-btn small-btn" style={{marginLeft:8}} disabled={busy}
              onClick={e => { e.stopPropagation(); onRetry(job); }} data-testid={`wh-shopee-bordir-retry-${job.id}`}>Coba Lagi</button>}
          </div>)}
      </div>
    </div>
    {tab === "waiting" && !accd && <div className="shopee-wh-actions" onClick={e=>e.stopPropagation()}>
      {canAct ? (
        <button type="button" className="primary shopee-wh-btn"
          style={{gridColumn:"1 / -1", background:"#15803d", borderColor:"#15803d"}}
          onClick={() => onAcc && onAcc(job)}
          disabled={busy || (job.print_unrecorded && !job.acc_eligible)}
          data-testid={`wh-shopee-acc-btn-${job.id}`}>
          {busy ? "Memproses…" : (job.print_unrecorded && !job.acc_eligible) ? "ACC Diblokir" : "ACC Barang Siap"}
        </button>
      ) : (
        <span className="shopee-wh-passive" data-testid={`wh-shopee-acc-passive-${job.id}`}
          style={{gridColumn:"1 / -1", display:"inline-flex", alignItems:"center", justifyContent:"center",
                   gap:6, padding:"10px 14px", borderRadius:999, background:"rgba(234,88,12,.1)",
                   color:"#fde68a", fontSize:12, fontWeight:600,
                   border:"1px dashed rgba(234,88,12,.45)"}}>
          ⏳ Menunggu pemeriksaan Adit
        </span>
      )}
    </div>}
  </div>;
}


function ShopeeBordirCard({ job, onOpenPreview, onOpenPicker, onStartDesigning, onDesignDone, busyStart, busyDone }) {
  const [more, setMore] = useState(false);
  // Iter 466 — Guard: `job` null/undefined atau tanpa `id` jangan dirender
  //   (ErrorBoundary luar akan menangkap, tapi guard eksplisit lebih ramah).
  if (!job || !job.id) return null;
  const eff = job.bordir_effective_state || "forwarded";
  // Iter 517 — Tambah state "design_done" untuk tab Riwayat (desainer login).
  const badge = eff === "on_hold" ? { t: "DITAHAN", c: "#92400e", bg: "#fef3c7" }
    : eff === "cancelled" ? { t: "BATAL", c: "#FFFFFF", bg: "#DC2626" }
    : eff === "designing" ? { t: "Sedang Didesain · belum dikirim", c: "#1e40af", bg: "#dbeafe" }
    : eff === "design_done" ? { t: "Sudah masuk antrean operator", c: "#166534", bg: "#dcfce7" }
    : eff === "executing" ? { t: `Sedang dikerjakan operator${job.bordir_execution_pic_name ? ` · ${job.bordir_execution_pic_name}` : ""}`, c: "#1e40af", bg: "#dbeafe" }
    : eff === "done" ? { t: `Selesai bordir${job.bordir_executed_by_name ? ` · ${job.bordir_executed_by_name}` : ""}`, c: "#FFFFFF", bg: "#15803d" }
    : { t: "Siap Dikerjakan", c: "#166534", bg: "#dcfce7" };
  const sentAt = job.bordir_design_done_at ? new Date(job.bordir_design_done_at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
  const snap = job.bordir_forwarded_snapshot || [];
  let thumbUrl = null;
  for (const s of snap) {
    if ((s.content_type || "").startsWith("image")) { thumbUrl = `${API}/shopee/files/${s.file_id}/thumbnail?size=400`; break; }
  }
  const firstImage = snap.find(s => (s.content_type || "").startsWith("image"));
  const stop = (e) => e.stopPropagation();
  const doPreview = (e) => {
    stop(e);
    if (snap.length > 1) { onOpenPicker(job, "preview"); return; }
    const target = firstImage || snap[0];
    if (target && onOpenPreview) onOpenPreview({
      file_id: target.file_id, filename: target.filename,
      title: target.title, content_type: target.content_type,
    });
  };
  // Iter 326 — Preview tetap bisa dibuka saat on_hold/cancelled; aksi Kerjakan/Selesai dinonaktifkan.
  // Iter 517 — design_done = Riwayat: aksi juga dinonaktifkan (sudah selesai).
  const canAct = eff === "forwarded" || eff === "designing";
  const toggle = () => setMore(v => !v);
  return <div className="card shopee-card shopee-wh-card dbc-card" role="button" tabIndex={0} aria-expanded={more}
    onClick={toggle} onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); toggle(); } }}
    style={{padding:0, overflow:"hidden", borderLeft:`4px solid ${badge.bg}`, opacity: eff === "cancelled" ? 0.75 : 1, display:"flex", flexDirection:"column", cursor:"pointer"}}
    data-state={eff} data-testid={`bordir-shopee-card-${job.id}`}>
    <ShopeeCancelTag job={job}/>
    <div className="shopee-wh-top">
      <ShopeeMockupThumb thumbUrl={thumbUrl} iconSize={28}
        testId={`bordir-shopee-thumb-${job.id}`}/>
      <div className="shopee-wh-info">
        <div className="shopee-wh-badges">
          <span className="shopee-status-badge" style={{background:badge.bg, color:badge.c}}
            data-testid={`bordir-shopee-state-${job.id}`}>{badge.t}</span>
          <span className="dbc-more" aria-hidden="true" data-testid={`bordir-shopee-more-${job.id}`}>{more ? "Tutup" : "Detail"}</span>
        </div>
        <div className="shopee-order-number" data-testid={`bordir-shopee-order-${job.id}`}>{job.order_number}</div>
        <BordirShipBy job={job}/>
<TokopediaAttachments orderNumber={job.order_number}/>
        {job.design_title && <div className="shopee-design-title">{job.design_title}</div>}
        {job.customer_account && <div className="shopee-account-line"><b>{job.customer_account}</b></div>}
      </div>
    </div>
    {more && <dl className="phub-detail" onClick={stop} data-testid={`bordir-shopee-detail-${job.id}`}>
      <dt>Pesanan</dt><dd>{job.order_number} · ID {String(job.id).slice(0, 8)}</dd>
      <dt>Akun</dt><dd>{job.customer_account || "-"}</dd>
      {job.process && <><dt>Proses</dt><dd>{SHOPEE_PROCESS_LABEL[job.process] || job.process}</dd></>}
      <dt>Jenis</dt><dd>{job.job_type === "lembur" ? "Lembur" : "Reguler"}</dd>
      <dt>Mockup</dt><dd><div className="shopee-wh-meta" style={{margin:0}}>Desainer Shopee: <b>{job.pic_designer_name || "-"}</b></div></dd>
      {job.bordir_designing_at && <><dt>Dikerjakan</dt><dd data-testid={`bordir-shopee-designing-info-${job.id}`}>Desain Bordir: <b>{job.bordir_designing_by || "-"}</b> · mulai {fmtDateTime(job.bordir_designing_at)}</dd></>}
      {sentAt && <><dt>Dikirim</dt><dd data-testid={`bordir-shopee-sent-${job.id}`}>{sentAt}{job.bordir_design_done_by ? ` · ${job.bordir_design_done_by}` : ""}</dd></>}
      {eff === "design_done" && job.bordir_design_done_at && <><dt>Selesai</dt><dd data-testid={`bordir-shopee-done-info-${job.id}`}>{fmtDateTime(job.bordir_design_done_at)} · diteruskan ke Operator Bordir</dd></>}
    </dl>}
    {eff === "on_hold" && <div className="shopee-hold-strip">
      <b>DITAHAN</b> — menunggu revisi ulang dari desainer (sinkron dari Rido).
    </div>}
    {eff === "cancelled" && <div className="shopee-hold-strip" style={{background:"#fee2e2", color:"#991b1b"}}>
      <b>DIBATALKAN</b> — pekerjaan tidak dilanjutkan (sinkron dari Rido).
    </div>}
    <div className="shopee-wh-actions dbc-actions" onClick={e => e.stopPropagation()}>
      <button type="button" className="soft-btn shopee-wh-btn shopee-wh-btn-preview"
        onClick={doPreview} disabled={!snap.length}
        data-testid={`bordir-shopee-preview-${job.id}`}>
        <ImgIcon size={14}/> Lihat Gambar
      </button>
      {eff === "forwarded" && <button type="button" className="primary shopee-wh-btn dbc-big"
        onClick={(e) => { stop(e); if (!busyStart && canAct && onStartDesigning) onStartDesigning(job); }}
        disabled={busyStart || !canAct}
        style={{background:"#1d4ed8", borderColor:"#1d4ed8"}}
        data-testid={`bordir-shopee-start-${job.id}`}>
        {busyStart ? "Memproses…" : "MULAI DESAIN"}
      </button>}
      {eff === "designing" && <button type="button" className="primary shopee-wh-btn dbc-big"
        onClick={(e) => { stop(e); if (!busyDone && canAct && onDesignDone) onDesignDone(job); }}
        disabled={busyDone || !canAct}
        style={{background:"#15803d", borderColor:"#15803d"}}
        data-testid={`bordir-shopee-done-${job.id}`}>
        {busyDone ? "Memproses…" : "SELESAI DESAIN"}
      </button>}
      {eff === "design_done" && (
        <span className="muted small" data-testid={`bordir-shopee-history-note-${job.id}`}
          style={{alignSelf:"center", textAlign:"center"}}>
          Pekerjaan ini sudah diteruskan ke Operator Bordir.
        </span>
      )}
      {!canAct && eff !== "design_done" && <div className="shopee-wh-action-note muted">Tombol dikunci selama pekerjaan ditahan/dibatalkan.</div>}
    </div>
  </div>;
}


// Iter 287i — Tema oranye scoped untuk modul & tab Shopee.
// Tidak menyentuh warna status desain (FIX/REVISI/BATAL) dan tidak mengubah tampilan
// Offline maupun branding TAMFARIS utama.
function ShopeeThemeStyles() {
  return <style>{`
    .shopee-theme .shopee-badge { background:var(--tf-shopee) !important; color:#fff !important; padding:3px 8px; border-radius:6px; font-size:11px; font-weight:700; letter-spacing:0.5px; }
  `}</style>;
}

// Iter 287v — Modal alasan penolakan pengajuan dari desainer.
function ShopeeRejectPendingModal({ job, onClose, onSubmit }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  return <div style={{position:"fixed", inset:0, background:"rgba(15,23,42,.55)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:12}}
    onClick={e=>{ if (e.target === e.currentTarget && !busy) onClose(); }}
    data-testid="wh-shopee-reject-modal">
    <div className="card shopee-theme" style={{maxWidth:520, width:"100%", padding:16}}>
      <ShopeeThemeStyles/>
      <h3 style={{margin:"0 0 8px 0", fontSize:15, fontWeight:700}}>Tolak pengajuan · {job.order_number}</h3>
      <div className="muted small" style={{marginBottom:6}}>
        Pekerjaan akan tetap terkunci dengan status proses sebelumnya. Wajib isi alasan penolakan.
      </div>
      <textarea rows={4} value={reason} onChange={e=>setReason(e.target.value)}
        placeholder="Contoh: sudah dicetak sebagian, tidak bisa dihentikan."
        style={{width:"100%", padding:"8px 10px", border:"1px solid #cbd5e1", borderRadius:6, fontSize:13, resize:"vertical"}}
        data-testid="wh-shopee-reject-reason"/>
      <div style={{display:"flex", gap:8, justifyContent:"flex-end", marginTop:10}}>
        <button className="soft-btn small-btn" onClick={onClose} disabled={busy}
          data-testid="wh-shopee-reject-cancel">Batal</button>
        <button className="primary small-btn tf-btn-danger"
          disabled={busy || reason.trim().length < 3}
          onClick={async ()=>{
            setBusy(true);
            try { await onSubmit(job, reason.trim()); }
            catch { /* keep open */ }
            finally { setBusy(false); }
          }}
          data-testid="wh-shopee-reject-submit">
          {busy ? "Menolak…" : "Tolak Pengajuan"}
        </button>
      </div>
    </div>
  </div>;
}

// ============================================================================
// Iter 374 — ShopeeMarkPrintedPerFileModal
// Modal konfirmasi tandai dicetak PER-FILE untuk pesanan Shopee multi-mockup.
// Menampilkan tiap file di `warehouse_sent_snapshot` dengan status printed_at.
// Memanggil `onMarkFile(fileId)` yang dilempar ke parent (POST per-file).
// Progress ditampilkan dengan "N/M sudah dicetak". Setelah semua file ditandai,
// pesanan otomatis masuk "Sudah Dicetak" di backend (dicek dari field
// mockup_printed_at yang di-set backend saat semua file selesai).
// ============================================================================
function ShopeeMarkPrintedPerFileModal({ job, onClose, onMarkFile }) {
  const snap = job.warehouse_sent_snapshot || [];
  const [busyFid, setBusyFid] = useState(null);
  const [err, setErr] = useState("");
  const printedCount = snap.filter(s => !!s.printed_at).length;
  const totalCount = snap.length;
  const allDone = printedCount === totalCount && totalCount > 0;
  const doMark = async (fileId) => {
    if (busyFid) return;
    setBusyFid(fileId);
    setErr("");
    try {
      await onMarkFile(fileId);
    } catch (e) {
      setErr(e?.message || "Gagal menandai file.");
    } finally {
      setBusyFid(null);
    }
  };
  const doMarkAll = async () => {
    if (busyFid) return;
    const remaining = snap.filter(s => !s.printed_at);
    for (const s of remaining) {
      setBusyFid(s.file_id);
      try {
        await onMarkFile(s.file_id);
      } catch (e) {
        setErr(e?.message || "Gagal menandai sebagian file.");
        setBusyFid(null);
        return;
      }
    }
    setBusyFid(null);
  };
  return (
    <div className="cm-backdrop" role="dialog" aria-modal="true" aria-label="Konfirmasi tandai mockup per-file"
      onClick={busyFid ? undefined : onClose} data-testid="shopee-mark-perfile-modal">
      <div className="cm-panel" onClick={e => e.stopPropagation()}>
        <header className="cm-head">
          <h3>Tandai Mockup Dicetak — {job.order_number}</h3>
          <button type="button" className="cm-close" onClick={onClose} aria-label="Tutup" data-testid="shopee-mark-perfile-close">
            <X size={16}/>
          </button>
        </header>
        <div className="cm-body">
          <p className="cm-hint">
            {mpOrderLabel(job)} <b>{job.order_number}</b>: {printedCount} dari {totalCount} file mockup sudah ditandai.
            Pesanan masuk <b>Sudah Dicetak</b> hanya setelah seluruh file ditandai. Setiap file dicatat terpisah.
          </p>
          <div style={{display:"flex", flexDirection:"column", gap:6}}>
            {snap.map(s => {
              const done = !!s.printed_at;
              const busy = busyFid === s.file_id;
              return <div key={s.file_id} className="shopee-perfile-row" data-testid={`shopee-mark-perfile-row-${s.file_id}`}>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontWeight:700, fontSize:13, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                    {s.title || s.filename}
                  </div>
                  <div className="muted small" style={{fontSize:11}}>
                    v{s.version} · {s.filename}
                    {done && <> · <b style={{color:"#15803d"}}>Dicetak oleh {s.printed_by || "-"}</b></>}
                  </div>
                </div>
                {done ? (
                  <span className="chip chip-safe" style={{fontSize:11}} data-testid={`shopee-mark-perfile-done-${s.file_id}`}>
                    <CheckCircle size={11}/> Sudah dicetak
                  </span>
                ) : (
                  <button type="button"
                    className="primary small-btn"
                    onClick={() => doMark(s.file_id)}
                    disabled={!!busyFid}
                    data-testid={`shopee-mark-perfile-mark-${s.file_id}`}>
                    🖨 {busy ? "Menandai…" : "Tandai Dicetak"}
                  </button>
                )}
              </div>;
            })}
          </div>
          {err && (
            <div className="cm-err" data-testid="shopee-mark-perfile-err">
              <AlertCircle size={14}/> <span>{err}</span>
            </div>
          )}
        </div>
        <footer className="cm-foot">
          <button type="button" className="cm-btn cm-btn-ghost" onClick={onClose} disabled={!!busyFid}
            data-testid="shopee-mark-perfile-cancel">
            {allDone ? "Tutup" : "Batal"}
          </button>
          {!allDone && snap.some(s => !s.printed_at) && (
            <button type="button" className="cm-btn cm-btn-primary" onClick={doMarkAll} disabled={!!busyFid}
              data-testid="shopee-mark-perfile-all">
              <FileText size={14}/> Tandai Semua Sisa
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}


function ShopeeWhCard({ job, onOpen, onMarkProcessing, onForwardEmbroidery, forwardingEmb, markingPrinted, onMarkMockupPrinted, onOpenDownloadPicker, onOpenPreview, onDownloadFile, dlBusyIds, dlErrsById, dlOptimisticIds, marking, onApprovePending, onRejectPending, onOpenCetakSize, onOpenConfirmPrint, onOpenPerFileMark, ensureProcessing, printFilter }) {
  const state = job.warehouse_state;
  const pstate = job.warehouse_process_state || "queued";
  const isProcessing = state === "sent" && pstate === "processing";
  const isQueued = state === "sent" && pstate === "queued";
  const isOnHold = state === "on_hold";
  const designerUpdated = !!job.warehouse_designer_updated_at;
  const pendingReq = job.pending_request && job.pending_request.status === "pending" ? job.pending_request : null;
  const allDl = isAllDownloaded(job, dlOptimisticIds);
  // Iter 287ao — Badge status sesuai kondisi aktual pekerjaan.
  //  • cancelled → BATAL
  //  • on_hold → DITAHAN
  //  • sent+processing → Diproses Gudang
  //  • sent+queued + desain FIX & tidak ada perubahan desainer tertunda → Menunggu Gudang
  //  • sent+queued + desain belum FIX / baru di-edit desainer → Menunggu FIX
  //  Tidak pernah pakai "Antri cetak" lagi.
  let badge;
  if (state === "cancelled") badge = {t:"BATAL", c:"#7f1d1d", bg:"#fee2e2"};
  else if (isOnHold) badge = {t:"DITAHAN", c:"#92400e", bg:"#fef3c7"};
  else if (isProcessing) badge = {t:"Diproses Gudang", c:"#7c3aed", bg:"#ede9fe"};
  else if (isQueued) {
    const needsFix = job.status !== "FIX" || designerUpdated;
    badge = needsFix
      ? {t:"Menunggu FIX", c:"#92400e", bg:"#fef3c7"}
      : {t:"Menunggu Gudang", c:"#065f46", bg:"#d1fae5"};
  } else {
    badge = {t:"-", c:"#334155", bg:"#e2e8f0"};
  }
  // Iter 375 — Mode "Sudah Dicetak": info PIC desainer & pengirim dipindah ke
  //   modal Detail agar kartu padat (sesuai pola AccBarangCard Offline).
  //   Info cetak di-render oleh ShopeeWhCardActions sebagai banner hijau,
  //   jadi baris "🖨 Mockup dicetak" di dalam shopee-wh-info disembunyikan.
  const isPrintedView = printFilter === "sudah" || !!job.mockup_printed_at;
  const desk = printFilter === "belum" || printFilter === "sudah";  // tampilan Rido (desktop ringkas)
  // Thumbnail dari snapshot yang dikirim (bukan mockup terbaru — mencegah pakai mockup lama saat revisi).
  const snap = job.warehouse_sent_snapshot || [];
  let thumbUrl = null;
  for (const s of snap) {
    if ((s.content_type || "").startsWith("image")) { thumbUrl = `${API}/shopee/files/${s.file_id}/thumbnail?size=${desk ? 200 : 400}`; break; }
  }
  return <div className={`card shopee-card shopee-card-clickable shopee-wh-card${desk ? " rdc" : ""}`} style={{padding:0, overflow:"hidden", borderLeft: desk ? undefined : `4px solid ${badge.c}`, opacity: state==="cancelled"?0.7:1, display:"flex", flexDirection:"column"}}
    onClick={onOpen}
    onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
    role="button"
    tabIndex={0}
    data-testid={`wh-shopee-card-${job.id}`}>
    <ShopeeCancelTag job={job}/>
    <div className="shopee-wh-top">
      <ShopeeMockupThumb thumbUrl={thumbUrl} iconSize={28}
        testId={`wh-shopee-thumb-${job.id}`}/>
      {desk ? <RidoCardInfo job={job} badge={badge} allDl={allDl} designerUpdated={designerUpdated && isQueued}/> :
      <div className="shopee-wh-info">
        <div className="shopee-wh-badges">
          <span className="shopee-badge shopee-wh-source-badge">SHOPEE</span>
          <span className="shopee-status-badge" style={{background:badge.bg, color:badge.c}}
            data-testid={`wh-shopee-state-${job.id}`}>{badge.t}</span>
        </div>
        {/* Iter 287ao — Akun pembeli paling jelas & tebal di atas, nomor pesanan di bawahnya. */}
        {job.customer_account && <div className="shopee-wh-account-primary" data-testid={`wh-shopee-card-account-${job.id}`}>
          {job.customer_account}
        </div>}
        <div className="shopee-order-number" data-testid={`wh-shopee-card-order-${job.id}`}>{job.order_number}
          {desk && <> <PaperBadge job={job}/></>}</div>
<TokopediaAttachments orderNumber={job.order_number}/>
        <TokopediaDeadline orderNumber={job.order_number}/>
        {allDl && <div className="shopee-wh-alldl-wrap">
          <span className="shopee-wh-alldl"
            data-testid={`wh-shopee-all-downloaded-${job.id}`}>SUDAH DIUNDUH</span>
        </div>}
        {!desk && job.design_title && <div className="shopee-design-title">{job.design_title}</div>}
        {!desk && job.process && <div className="shopee-process-chip" data-testid={`wh-shopee-process-${job.id}`}>
          <Layers size={11} style={{verticalAlign:"-2px"}}/> {SHOPEE_PROCESS_LABEL[job.process] || job.process}
        </div>}
        <div className="shopee-wh-meta">
          Desainer: <b>{job.pic_designer_name}</b>{!desk && <> · {job.job_type === "lembur" ? "Lembur" : "Reguler"}</>}
        </div>
        {!desk && !isPrintedView && <div className="shopee-wh-meta">
          Dikirim {fmtDateTime(job.warehouse_sent_at)} · <b>{job.warehouse_sent_by}</b>
          {job.warehouse_send_count > 1 && <> · pengiriman ke-{job.warehouse_send_count}</>}
        </div>}
        {!desk && job.bordir_state === "forwarded" && <div className="shopee-wh-forwarded-wrap">
          <span className="shopee-wh-forwarded"
            data-testid={`wh-shopee-forwarded-${job.id}`}>
            <Sparkles size={11} style={{verticalAlign:"-2px"}}/> {job.bordir_forwarded_auto ? "Otomatis diteruskan ke Desain Bordir" : "Sudah diteruskan ke Desain Bordir"} · {job.bordir_forwarded_by_name || "-"}
          </span>
        </div>}
        {/* Iter 323 — Jejak Mockup Dicetak (Rido) & ACC Barang Siap (Adit/Rido).
            Iter 375 — Pola Offline AccBarangCard: baris "🖨 Mockup dicetak oleh X · time"
            tetap di dalam body kartu (ringkas, muted). Chip pemeriksaan (⏳ Menunggu
            ACC / ✅ Sudah ACC) dipindah ke area aksi sekunder di ShopeeWhCardActions. */}
        {job.mockup_printed_at && !desk && <div className="shopee-wh-meta" data-testid={`wh-shopee-printed-info-${job.id}`}
          style={{color:"#065f46", fontWeight:600}}>
          🖨 Mockup dicetak oleh <b>{job.mockup_printed_by || "—"}</b> · {fmtDateTime(job.mockup_printed_at)}
          {job.goods_acc_at && <> · ACC oleh <b>{job.goods_acc_by || "—"}</b> · {fmtDateTime(job.goods_acc_at)}</>}
        </div>}
        {designerUpdated && <div className="shopee-wh-designer-updated-wrap">
          <span className="shopee-wh-designer-updated"
            data-testid={`wh-shopee-designer-updated-${job.id}`}>Diperbarui oleh desainer{job.mockup_printed_at ? " — cetak ulang versi revisi" : ""}</span>
        </div>}
      </div>}
    </div>
    {/* Iter 287v — Pengajuan aktif: banner menonjol + tombol Setujui/Tolak (Gudang). */}
    {pendingReq && <div className="shopee-pending-strip"
      onClick={e => e.stopPropagation()}
      data-testid={`wh-shopee-pending-${job.id}`}>
      <div style={{fontSize:13, fontWeight:700}}>
        Pengajuan {pendingReq.kind === "hold" ? "Tahan / Revisi" : "Batal"} dari desainer
      </div>
      <div className="muted small" style={{marginTop:2, fontSize:12}}>
        {pendingReq.requested_by_name} · <b>Alasan:</b> {pendingReq.reason}
        {pendingReq.details && <> · <b>Detail:</b> {pendingReq.details}</>}
      </div>
      <div style={{display:"flex", gap:6, marginTop:8, flexWrap:"wrap"}}>
        <button type="button" className="primary small-btn"
          style={{background:"#059669", borderColor:"#059669"}}
          onClick={() => onApprovePending && onApprovePending(job, pendingReq)}
          data-testid={`wh-shopee-approve-${job.id}`}>Setujui</button>
        <button type="button" className="soft-btn small-btn"
          style={{color:"#991b1b", borderColor:"#fecaca", background:"#fef2f2"}}
          onClick={() => onRejectPending && onRejectPending(job, pendingReq)}
          data-testid={`wh-shopee-reject-${job.id}`}>Tolak</button>
      </div>
    </div>}
    {job.hold?.active && <div className="shopee-hold-strip" onClick={e => e.stopPropagation()}
      style={{background:"#fef3c7", color:"#92400e", fontWeight:700}} data-testid={`wh-shopee-hold-badge-${job.id}`}>
      HOLD — tidak dapat dicetak resi sampai HOLD dilepas Tim Mockup{job.hold.reason ? ` (alasan: ${job.hold.reason})` : ""}.
    </div>}
    {/* Iter 287v — Kartu DITAHAN: hentikan aksi lanjutan, tunggu revisi ulang desainer. */}
    {isOnHold && <div className="shopee-hold-strip"
      onClick={e => e.stopPropagation()}
      data-testid={`wh-shopee-on-hold-${job.id}`}>
      <b>DITAHAN</b> — menunggu desainer mengirim revisi ulang.
    </div>}
    {/* Iter 374 — Baris aksi kartu Shopee Rido: Cetak Mockup / Unduh Mockup /
        Tandai Mockup Dicetak + Preview. Tombol "Mulai Siapkan" dihapus — Rido
        tidak perlu klaim eksplisit. Backend tetap mewajibkan processing state;
        helper `ensureProcessing` otomatis mem-POST warehouse-mark-processing
        secara silent sebelum unduh / tandai cetak. */}
    {["sent", "on_hold"].includes(state) && <WhNeedsPanel jobId={job.id}/>}
    {state === "sent" && !pendingReq && <div className="shopee-wh-actions"
      onClick={e => e.stopPropagation()}>
      <ShopeeWhCardActions
        job={job}
        onOpen={onOpen}
        isQueued={isQueued}
        marking={marking}
        forwardingEmb={forwardingEmb}
        markingPrinted={markingPrinted}
        printFilter={printFilter}
        onMarkProcessing={onMarkProcessing}
        onForwardEmbroidery={onForwardEmbroidery}
        onMarkMockupPrinted={onMarkMockupPrinted}
        onOpenPreview={onOpenPreview}
        onOpenDownloadPicker={onOpenDownloadPicker}
        onDownloadFile={onDownloadFile}
        onOpenCetakSize={onOpenCetakSize}
        onOpenConfirmPrint={onOpenConfirmPrint}
        onOpenPerFileMark={onOpenPerFileMark}
        ensureProcessing={ensureProcessing}
        dlBusyIds={dlBusyIds}
        dlErrsById={dlErrsById}
        dlOptimisticIds={dlOptimisticIds}
      />
    </div>}
  </div>;
}

// Iter 287j — Aksi tombol di kartu Shopee (tanpa buka detail).
// Iter 374 — Alur Cetak Mockup Shopee disamakan dengan Offline:
//   [Preview] [Cetak Mockup] [Unduh Mockup] [Tandai Mockup Dicetak]
// Tombol "Mulai Siapkan" DIHAPUS; processing state di-set otomatis saat Unduh
// atau Tandai (via helper ensureProcessing). Preview tetap tersedia tanpa gate.
function ShopeeWhCardActions({ job, onOpen, isQueued, marking, forwardingEmb, markingPrinted, onMarkProcessing, onForwardEmbroidery, onMarkMockupPrinted, onOpenDownloadPicker, onOpenPreview, onDownloadFile, onOpenCetakSize, onOpenConfirmPrint, onOpenPerFileMark, ensureProcessing, dlBusyIds, dlErrsById, dlOptimisticIds, printFilter }) {
  const snap = job.warehouse_sent_snapshot || [];
  const firstImage = snap.find(s => (s.content_type || "").startsWith("image"));
  const dlMap = downloadsMapOf(job);
  const optHas = (fid) => !!(dlOptimisticIds && dlOptimisticIds.has && dlOptimisticIds.has(fid));
  const singleFile = snap.length === 1 ? snap[0] : null;
  const singleFid = singleFile?.file_id;
  const singleBusy = singleFid ? (dlBusyIds?.has?.(singleFid) || false) : false;
  const stop = (e) => { e.stopPropagation(); };
  const notFix = job.status !== "FIX";
  const designerUpdated = !!job.warehouse_designer_updated_at;
  const shpCancel = !!(job.shopee_cancelled || job.shopee_in_cancel);
  const blocked = notFix || designerUpdated || shpCancel;
  const printed = !!job.mockup_printed_at;
  const busyMark = !!markingPrinted;
  // Iter 375 — Deteksi mode "sudah dicetak" untuk Rido: tampilkan info ACC Gudang
  //   & tombol sekunder (Preview / Unduh / Cetak Ulang) sesuai pola Offline.
  const isPrintedView = printFilter === "sudah" || printed;
  const accd = !!job.goods_acc_at;
  // Iter 375 — Progress per-file untuk partial print di tab Belum Dicetak.
  const printedFiles = snap.filter(s => !!s.printed_at).length;
  const totalFiles = snap.length;
  const partialProgress = !printed && printedFiles > 0 && totalFiles > 0;

  // Preview — selalu diperbolehkan (backend tidak gate preview).
  const doPreview = (e) => {
    stop(e);
    if (snap.length > 1) {
      onOpenDownloadPicker(job, "preview");
      return;
    }
    const target = firstImage || snap[0];
    if (target && onOpenPreview) onOpenPreview({
      file_id: target.file_id, filename: target.filename,
      title: target.title, content_type: target.content_type,
    });
  };

  // Cetak Mockup / Cetak Ulang — buka modal A5/A4. Multi file → picker; single → direct.
  //   TIDAK memanggil ensureProcessing: preview endpoint tidak butuh processing,
  //   dan tombol cetak harus murni visual (tidak mengubah status penyiapan).
  // Iter 375 — Saat `printed`, tombol diaktifkan sebagai "Cetak Ulang" tanpa
  //   mereset status/menyentuh endpoint mark-printed (hanya buka dialog cetak).
  const doCetak = (e) => {
    stop(e);
    if (snap.length > 1) {
      onOpenDownloadPicker(job, "cetak");
      return;
    }
    const target = firstImage || snap[0];
    if (target && onOpenCetakSize) onOpenCetakSize(target.file_id, `${mpOrderLabel(job)} ${job.order_number}`, job);
  };

  // Unduh Mockup — pakai preview endpoint (barcode-stamped, tanpa gate processing).
  //   Memisahkan akses file dari klaim penyiapan: Rido dapat mengunduh mockup
  //   visual kapan saja selama kartu FIX & snapshot aktif, tanpa mengubah
  //   warehouse_process_state. File raw yang butuh processing tetap diakses
  //   lewat tombol Unduh di detail (untuk Bordir/Sablon).
  const doDownload = async (e) => {
    stop(e);
    if (blocked) return;
    if (snap.length > 1) { onOpenDownloadPicker(job, "unduh-mockup"); return; }
    const target = firstImage || snap[0];
    if (!target) return;
    try {
      await unduhMockupShopee(target.file_id, job.order_number);
    } catch (err) {
      toast.error(err?.message || "Gagal mengunduh mockup.");
    }
  };

  // Tandai Mockup Dicetak — multi file buka per-file modal; single file via konfirmasi.
  const doMarkPrint = (e) => {
    stop(e);
    if (blocked || printed || busyMark) return;
    if (snap.length > 1 && onOpenPerFileMark) {
      onOpenPerFileMark(job);
      return;
    }
    if (onOpenConfirmPrint) onOpenConfirmPrint(job);
  };

  // ==========================================================================
  // Iter 375 — Mode "Sudah Dicetak" (Rido view): chip status pemeriksaan ACC
  //   Gudang (⏳ Menunggu / ✅ Sudah) + aksi sekunder ringkas (Preview / Unduh /
  //   Cetak Ulang). Tombol penandaan diganti info "Mockup sudah dicetak" yg
  //   sudah tertulis di body kartu (lihat baris jejak Mockup Dicetak).
  //   Pola mengikuti AccBarangCard Offline.
  // ==========================================================================
  const isSablonProc = job.process === "sablon_dtf" || job.process === "bordir_sablon_dtf";
  // Iter 548 — Jejak jalur paralel DTF untuk Rido/Adit (hanya proses sablon).
  //   Tidak membuat Gudang menunggu Ibnu — hanya info progres.
  const dtfLabel = (() => {
    const st = job.sablon_state;
    if (st === "done") return `✅ DTF selesai${job.sablon_done_by_name ? ` (${job.sablon_done_by_name})` : ""}`;
    if (st === "in_progress") return `🖨️ DTF dikerjakan${job.sablon_started_by_name ? ` (${job.sablon_started_by_name})` : ""}`;
    if (st === "forwarded") return "⏳ DTF menunggu operator";
    return "⏳ DTF belum diteruskan";
  })();
  const pressLabel = (() => {
    const st = job.press_state;
    if (st === "done") return `✅ Press selesai${job.press_done_by_name ? ` (${job.press_done_by_name})` : ""}`;
    if (st === "in_progress") return `🔥 Press dikerjakan${job.press_started_by_name ? ` (${job.press_started_by_name})` : ""}`;
    if (st === "ready") return "✅ Siap Press (menunggu Haikal)";
    return "⏳ Press belum siap";
  })();
  if (printFilter === "belum" || printFilter === "sudah") {
    const items = [
      !printed && { key: "mark", label: busyMark ? "Menandai…" : (partialProgress ? `Tandai Sudah Dicetak (${printedFiles}/${totalFiles})` : "Tandai Sudah Dicetak"), onSelect: () => doMarkPrint({ stopPropagation() {} }), disabled: blocked || busyMark },
      { key: "dl", label: singleBusy ? "Mengunduh…" : "Unduh Mockup", onSelect: () => doDownload({ stopPropagation() {} }), disabled: blocked || singleBusy || !snap.length },
      { key: "detail", label: "Lihat Detail", onSelect: () => onOpen && onOpen() },
    ].filter(Boolean);
    return <div className="rido-actions">
      <button type="button" className="soft-btn shopee-wh-btn rido-btn-print" onClick={doCetak} disabled={blocked || !snap.length}
        title={blocked ? "Menunggu desainer menandai FIX" : printed ? "Cetak ulang (status tidak berubah)" : "Pilih A5 / A4 lalu cetak — konfirmasi muncul setelahnya"}
        data-testid={printed ? `wh-shopee-card-reprint-${job.id}` : `wh-shopee-card-cetak-${job.id}`}>
        <Printer size={15}/> {printed ? "Cetak Ulang" : "Cetak"}
      </button>
      <button type="button" className="soft-btn shopee-wh-btn" onClick={doPreview} data-testid={`wh-shopee-card-preview-${job.id}`}>
        <ImgIcon size={15}/> Pratinjau
      </button>
      <RidoMoreMenu items={items} testId={`wh-shopee-card-more-${job.id}`}/>
      {printed && <span className={`rido-acc ${accd ? "ok" : ""}`} data-testid={accd ? `wh-shopee-card-acc-done-${job.id}` : `wh-shopee-card-acc-waiting-${job.id}`}
        title={accd ? `ACC oleh ${job.goods_acc_by || "—"} · ${fmtDateTime(job.goods_acc_at)}` : `Dicetak oleh ${job.mockup_printed_by || "—"} · ${fmtDateTime(job.mockup_printed_at)}`}>
        {accd ? "✅ Sudah ACC" : "⏳ Menunggu ACC"}</span>}
      {(job.revised_after_print || job.revised_since_print) && <span className="rido-note" style={{ color: "#b3261e", fontWeight: 700 }} data-testid={`wh-shopee-card-revised-${job.id}`}>{MSG_REVISED}</span>}
      {printed && printFilter === "sudah" && <span className="rido-note" data-testid={`wh-shopee-card-printed-at-${job.id}`}>
        {fmtPrintedWib(job.mockup_printed_at) || "Waktu cetak tidak tercatat"}</span>}
      {printed && job.mockup_reprint_count > 0 && <span className="rido-note" style={{ color: "#9a5b00", fontWeight: 600 }} data-testid={`wh-shopee-card-reprinted-${job.id}`}>
        Dicetak ulang ×{job.mockup_reprint_count}</span>}
      {printed && isSablonProc && <span className="rido-note" data-testid={`wh-shopee-dtf-track-${job.id}`}>{dtfLabel} · {pressLabel}</span>}
      {partialProgress && <span className="rido-note" data-testid={`wh-shopee-card-partial-${job.id}`}>{printedFiles}/{totalFiles} file dicetak</span>}
      {blocked && <span className="rido-note" data-testid={`wh-shopee-card-blocked-note-${job.id}`}>Menunggu desainer menandai FIX.</span>}
    </div>;
  }

  if (isPrintedView && printed) {
    return <>
      {/* Chip status pemeriksaan — pola Offline AccBarangCard. */}
      {accd ? (
        <div className="shopee-wh-acc-chip shopee-wh-acc-done"
          data-testid={`wh-shopee-card-acc-done-${job.id}`}>
          ✅ Sudah ACC oleh <b>{job.goods_acc_by || "—"}</b> · {fmtDateTime(job.goods_acc_at)}
        </div>
      ) : (
        <div className="shopee-wh-acc-chip shopee-wh-acc-waiting"
          data-testid={`wh-shopee-card-acc-waiting-${job.id}`}>
          ⏳ Menunggu ACC Gudang
        </div>
      )}
      {/* Iter 548 — Jejak jalur paralel DTF Ibnu + Press Haikal (sablon only). */}
      {isSablonProc && (
        <div className="shopee-wh-dtf-track" data-testid={`wh-shopee-dtf-track-${job.id}`}>
          <span className="shopee-wh-dtf-chip" data-testid={`wh-shopee-dtf-state-${job.id}`}>{dtfLabel}</span>
          <span className="shopee-wh-dtf-chip" data-testid={`wh-shopee-press-state-${job.id}`}>{pressLabel}</span>
        </div>
      )}
      {/* Aksi sekunder ringkas — turun baris di HP agar label tidak terpotong. */}
      <div className="shopee-wh-secondary-row">
        <button type="button" className="shopee-wh-secondary-btn"
          onClick={doPreview}
          data-testid={`wh-shopee-card-preview-${job.id}`}>
          <ImgIcon size={13}/> Pratinjau
        </button>
        <button type="button" className="shopee-wh-secondary-btn"
          onClick={doDownload}
          disabled={blocked || singleBusy || !snap.length}
          title={blocked ? "Menunggu desainer menandai FIX" : "Unduh file mockup ke perangkat ini"}
          data-testid={`wh-shopee-card-unduh-${job.id}`}>
          <Download size={13}/> {singleBusy ? "Mengunduh…" : "Unduh"}
        </button>
        <button type="button" className="shopee-wh-secondary-btn"
          onClick={doCetak}
          disabled={blocked || !snap.length}
          title={blocked ? "Menunggu desainer menandai FIX" : "Cetak ulang mockup (tidak mengubah status)"}
          data-testid={`wh-shopee-card-reprint-${job.id}`}>
          <Printer size={13}/> Cetak Ulang
        </button>
      </div>
    </>;
  }

  return <>
    <button type="button" className="soft-btn shopee-wh-btn shopee-wh-btn-preview"
      onClick={doPreview}
      data-testid={`wh-shopee-card-preview-${job.id}`}>
      <ImgIcon size={14}/> Pratinjau
    </button>
    <button type="button" className="soft-btn shopee-wh-btn"
      onClick={doCetak}
      disabled={blocked || printed || !snap.length}
      title={shpCancel ? "DIBATALKAN SHOPEE — JANGAN PRODUKSI" : blocked ? "Menunggu desainer menandai FIX" : (printed ? "Mockup sudah dicetak" : "Pilih A5 / A4 lalu buka dialog cetak")}
      data-testid={`wh-shopee-card-cetak-${job.id}`}>
      <Printer size={14}/> Cetak Mockup
    </button>
    <button type="button" className="soft-btn shopee-wh-btn"
      onClick={doDownload}
      disabled={blocked || singleBusy || !snap.length}
      title={blocked ? "Menunggu desainer menandai FIX" : "Unduh file mockup ke perangkat ini"}
      data-testid={`wh-shopee-card-unduh-${job.id}`}>
      <Download size={14}/> {singleBusy ? "Mengunduh…" : "Unduh Mockup"}
    </button>
    {printed ? (
      <span className="soft-btn shopee-wh-btn shopee-wh-btn-print shopee-wh-btn-print-done"
        data-testid={`wh-shopee-card-printed-done-${job.id}`}
        title={`Dicetak oleh ${job.mockup_printed_by || "-"}`}>
        🖨 Mockup Sudah Dicetak
      </span>
    ) : (
      <button type="button"
        className="primary shopee-wh-btn shopee-wh-btn-print"
        onClick={doMarkPrint}
        disabled={blocked || busyMark}
        title={blocked ? "Menunggu desainer menandai FIX" : "Buka konfirmasi — mockup fisik sudah selesai dicetak"}
        data-testid={`wh-shopee-card-mark-printed-${job.id}`}>
        🖨 {busyMark ? "Menandai…" : (partialProgress ? `Tandai Mockup (${printedFiles}/${totalFiles})` : "Tandai Mockup Dicetak")}
      </button>
    )}
    {/* Iter 375 — Info progres parsial di tab Belum Dicetak (per-file). */}
    {partialProgress && (
      <div className="shopee-wh-partial-note" data-testid={`wh-shopee-card-partial-${job.id}`}>
        🖨 {printedFiles}/{totalFiles} mockup sudah dicetak — tandai sisanya untuk masuk Sudah Dicetak.
      </div>
    )}
    {blocked && <div className="shopee-wh-action-note" data-testid={`wh-shopee-card-blocked-note-${job.id}`}>
      Menunggu desainer menandai FIX.
    </div>}
    {/* Iter 287ae — Badge pasif "Sudah Diteruskan ke Desain Bordir". */}
    {(() => {
      const proc = (job.process || "").trim();
      if (!SHOPEE_PROCESSES_WITH_EMBROIDERY.has(proc)) return null;
      if (job.bordir_state !== "forwarded") return null;
      return <span className="shopee-wh-forwarded" style={{gridColumn:"1 / -1"}}
        data-testid={`wh-shopee-card-forwarded-badge-${job.id}`}>
        <Sparkles size={11} style={{verticalAlign:"-2px"}}/> {job.bordir_forwarded_auto ? "Otomatis diteruskan ke Desain Bordir" : "Sudah diteruskan ke Desain Bordir"} · {job.bordir_forwarded_by_name || "-"}
      </span>;
    })()}
  </>;
}

function ShopeeWhDetail({ jobId, onClose }) {
  const [job, setJob] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  // Iter 287k — preview via modal in-app (auth cookie ikut).
  const [previewFile, setPreviewFile] = useState(null);
  // Iter 287l — Unduh terautentikasi per file (state lokal untuk modal ini).
  // Iter 287m — Optimistik: setelah fetch+trigger sukses langsung tandai; pencatatan retry background.
  const [dlBusyIds, setDlBusyIds] = useState(() => new Set());
  const [dlErrsById, setDlErrsById] = useState({});
  const [dlOptimisticIds, setDlOptimisticIds] = useState(() => new Set());
  const load = async () => {
    try {
      // Pakai queue endpoint yang mengembalikan snapshot lengkap; ambil single by id.
      const r = await axios.get(`${API}/shopee/warehouse/queue`, { params:{ include_cancelled: true }, withCredentials: true });
      const found = (r.data.items || []).find(j => j.id === jobId);
      if (!found) setErr("Kartu tidak ditemukan atau sudah dihapus dari antrean");
      else setJob(found);
    } catch (e) { setErr(e?.response?.data?.detail || "Gagal memuat detail"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [jobId]);
  const markProcessing = async () => {
    // Iter 287p — Ganti window.confirm dgn notify.confirm.
    const ok = await notify.confirm({
      title: "Mulai proses kartu ini?",
      message: `Kartu ${job?.order_number} akan ditandai sedang diproses gudang. Setelah ini, desainer tidak lagi bisa menariknya kembali otomatis.`,
      primaryLabel: "Mulai Proses",
      secondaryLabel: "Batal",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await axios.post(`${API}/shopee/${jobId}/warehouse-mark-processing`, null, { withCredentials: true });
      await load();
      notify.success({ title: "Berhasil", message: `Kartu ${job?.order_number} sedang diproses.` });
    } catch (e) { setErr(e?.response?.data?.detail || "Gagal menandai proses"); }
    finally { setBusy(false); }
  };
  const handleDownloadFile = async (file) => {
    const fid = file.file_id;
    if (dlBusyIds.has(fid)) return;
    setDlBusyIds(prev => { const n = new Set(prev); n.add(fid); return n; });
    setDlErrsById(prev => { const n = {...prev}; delete n[fid]; return n; });
    try {
      await fetchAndTriggerShopeeDownload(file);
    } catch (e) {
      const msg = await parseAxiosErrMsg(e);
      setDlErrsById(prev => ({...prev, [fid]: msg}));
      setDlBusyIds(prev => { const n = new Set(prev); n.delete(fid); return n; });
      return;
    }
    setDlOptimisticIds(prev => { const n = new Set(prev); n.add(fid); return n; });
    setDlBusyIds(prev => { const n = new Set(prev); n.delete(fid); return n; });
    (async () => {
      await recordShopeeDownloadWithRetry(jobId, fid);
      try { await load(); } catch { /* ignore */ }
    })();
  };
  return <>
  <div style={{position:"fixed", inset:0, background:"rgba(15,23,42,.55)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:12}}
    onClick={e=>{ if (e.target === e.currentTarget) onClose(); }}
    data-testid="wh-shopee-detail-modal">
    <div className="card shopee-theme" style={{maxWidth:900, width:"100%", maxHeight:"92vh", overflowY:"auto", padding:16}}>
      <ShopeeThemeStyles/>
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12, gap:8, flexWrap:"wrap"}}>
        <h3 style={{margin:0, fontSize:16, fontWeight:700}}>
          <span className="shopee-badge" style={{fontSize:10, marginRight:6, verticalAlign:"3px"}}>SHOPEE</span>
          Kartu Cetak {job?.order_number || "…"}
        </h3>
        <div style={{display:"flex", gap:6}}>
          {job && job.warehouse_state === "sent" && (job.warehouse_process_state || "queued") === "queued" && (
            <button className="soft-btn small-btn" onClick={markProcessing} disabled={busy}
              style={{color:"#7c3aed", borderColor:"#ddd6fe"}}
              data-testid="wh-shopee-mark-processing">Mulai Proses</button>
          )}
          <button className="soft-btn small-btn" onClick={onClose} data-testid="wh-shopee-detail-close">Tutup</button>
        </div>
      </div>
      {err && <div className="warn-box small">{err}</div>}
      {job && <div style={{display:"flex", flexDirection:"column", gap:10}}>
        <div className="card" style={{padding:10}}>
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))", gap:8, fontSize:13}}>
            <div><div className="muted small">Nomor Pesanan</div><div style={{fontWeight:600}}>{job.order_number}</div></div>
            <div><div className="muted small">Nama Akun</div><div style={{fontWeight:600}}>{job.customer_account || "-"}</div></div>
            <div><div className="muted small">Judul Desain</div><div style={{fontWeight:600}}>{job.design_title || "-"}</div></div>
            <div><div className="muted small">Desainer</div><div style={{fontWeight:600}}>{job.pic_designer_name || "-"}</div></div>
            <div><div className="muted small">Jenis</div><div style={{fontWeight:600}}>{job.job_type === "lembur" ? "Lembur" : "Reguler"}</div></div>
            <div><div className="muted small">Waktu Kirim</div><div style={{fontWeight:600}}>{fmtDateTime(job.warehouse_sent_at)}</div></div>
            <div><div className="muted small">Pengirim</div><div style={{fontWeight:600}}>{job.warehouse_sent_by || "-"}</div></div>
            <div><div className="muted small">Pengiriman ke</div><div style={{fontWeight:600}}>{job.warehouse_send_count || 1}</div></div>
          </div>
          {job.description && <div style={{marginTop:8, padding:"6px 8px", background:"#f8fafc", borderRadius:6, fontSize:12}}>
            <div className="muted small" style={{marginBottom:2}}>Keterangan</div>{job.description}
          </div>}
          {job.warehouse_process_state === "processing" && <div style={{marginTop:8, padding:"6px 10px", background:"#ede9fe", border:"1px solid #7c3aed", borderRadius:6, fontSize:12, color:"#5b21b6"}}>
            <b>Sedang diproses gudang.</b> Dimulai {fmtDateTime(job.warehouse_processing_started_at)} oleh {job.warehouse_processing_started_by}.
          </div>}
          {job.warehouse_state === "cancelled" && <div style={{marginTop:8, padding:"6px 10px", background:"#fee2e2", border:"1px solid #ef4444", borderRadius:6, fontSize:12, color:"#7f1d1d"}}>
            <b>Pekerjaan BATAL.</b> {job.cancel_reason || ""} — kartu ini tidak dihitung sebagai antrean aktif.
          </div>}
        </div>
        <div className="card" style={{padding:10}}>
          <div style={{fontWeight:700, marginBottom:8, display:"flex", justifyContent:"space-between", alignItems:"center", gap:8, flexWrap:"wrap"}}>
            <span>Mockup yang dikirim ({(job.warehouse_sent_snapshot||[]).length}){isStaleSnapshot(job) ? " — versi lama" : ""}</span>
            {isAllDownloaded(job, dlOptimisticIds) && <span style={{background:"#ecfccb", color:"#3f6212", padding:"2px 8px", borderRadius:6, fontSize:11, fontWeight:700, border:"1px solid #bef264"}}
              data-testid={`wh-shopee-detail-all-downloaded`}>SUDAH DIUNDUH</span>}
          </div>
          {isStaleSnapshot(job) && <StaleWarning testid="wh-shopee-detail-stale"/>}
          {(() => {
            const dlActive = (job.warehouse_process_state || "queued") !== "queued";
            const dlMap = downloadsMapOf(job);
            return <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(200px, 1fr))", gap:10}}>
              {(job.warehouse_sent_snapshot||[]).map(s => {
                const isImg = (s.content_type||"").startsWith("image");
                const url = `${API}/shopee/files/${s.file_id}/preview`;
                const fBusy = dlBusyIds.has(s.file_id);
                const fErr = dlErrsById[s.file_id] || "";
                const rec = dlMap[s.file_id];
                const fDone = !!rec || dlOptimisticIds.has(s.file_id);
                let dlLabel = fDone ? "Unduh Ulang" : "Unduh";
                if (fBusy) dlLabel = "Mengunduh…";
                else if (fErr) dlLabel = "Coba Lagi";
                return <div key={s.file_id} className="card" style={{padding:0, overflow:"hidden"}} data-testid={`wh-shopee-mockup-${s.file_id}`}>
                  <div style={{aspectRatio:"1", background:"var(--tf-neutral-bg)", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden"}}>
                    {isImg
                      ? <img src={url} alt={s.title} style={{width:"100%", height:"100%", objectFit:"cover"}}/>
                      : <div className="muted small" style={{padding:12, textAlign:"center"}}>Bukan gambar<br/>({s.filename})</div>}
                  </div>
                  <div style={{padding:8}}>
                    <div style={{fontWeight:700, fontSize:13, display:"flex", alignItems:"center", gap:6, flexWrap:"wrap"}}>
                      <span>{s.title || "-"}</span>
                      {fDone && <span style={{background:"#ecfccb", color:"#3f6212", padding:"1px 6px", borderRadius:4, fontSize:10, fontWeight:700, border:"1px solid #bef264"}}
                        data-testid={`wh-shopee-file-downloaded-${s.file_id}`}>Sudah Diunduh</span>}
                    </div>
                    {isStaleSnapshot(job) && <div style={{fontSize:11, fontWeight:800, color:"#b3261e"}} data-testid={`wh-shopee-file-stale-${s.file_id}`}>{STALE_FILE_LABEL}</div>}
                    <div className="muted small" style={{fontSize:11}}>Versi {s.version} · {s.filename}</div>
                    {fDone && rec && <div className="muted small" style={{fontSize:10}}>
                      {fmtDateTime(rec.downloaded_at)} · {rec.downloaded_by_name || "-"}
                    </div>}
                    {fDone && !rec && <div className="muted small" style={{fontSize:10}}>
                      Baru saja · pencatatan sedang tersinkron…
                    </div>}
                    {fErr && <div className="warn-box small" style={{margin:"4px 0", padding:"4px 8px", fontSize:11}}
                      data-testid={`wh-shopee-download-err-${s.file_id}`}>{fErr}</div>}
                    <div style={{display:"flex", gap:4, marginTop:6, flexWrap:"wrap"}}>
                      {isImg && <button type="button"
                        onClick={() => setPreviewFile({
                          file_id: s.file_id, filename: s.filename, stale: isStaleSnapshot(job),
                          title: s.title, content_type: s.content_type,
                        })}
                        className="soft-btn small-btn" data-testid={`wh-shopee-preview-${s.file_id}`}
                        style={{color:"var(--tf-neutral)", borderColor:"var(--tf-neutral-border)", background:"var(--tf-surface)", cursor:"pointer"}}>Pratinjau</button>}
                      {dlActive
                        ? <button type="button"
                            onClick={() => !fBusy && handleDownloadFile(s)}
                            disabled={fBusy}
                            className="soft-btn small-btn" data-testid={`wh-shopee-download-${s.file_id}`}
                            style={{
                              color: fErr ? "var(--tf-danger)" : "var(--tf-neutral)",
                              borderColor: fErr ? "var(--tf-danger-border)" : "var(--tf-neutral-border)",
                              background: fErr ? "#fef2f2" : "#fff",
                              cursor: fBusy ? "progress" : "pointer",
                              opacity: fBusy ? 0.75 : 1,
                            }}><Download size={12}/> {dlLabel}</button>
                        : <button type="button" className="soft-btn small-btn" disabled
                            title="Gunakan tombol Unduh / Tandai Mockup Dicetak di kartu — unduhan akan terbuka otomatis."
                            style={{opacity:0.55, cursor:"not-allowed"}}
                            data-testid={`wh-shopee-download-disabled-${s.file_id}`}><Download size={12}/> Unduh</button>}
                    </div>
                  </div>
                </div>;
              })}
            </div>;
          })()}
        </div>
      </div>}
    </div>
  </div>
  {previewFile && <ShopeeImagePreviewModal file={previewFile} onClose={()=>setPreviewFile(null)}/>}
  </>;
}


// Iter 287j — Modal pilih file untuk Preview/Unduh dari kartu (mockup > 1).
function ShopeeFilePickerModal({ job, mode, onClose, onOpenPreview, onDownloadFile, onOpenCetakSize, onUnduhMockup, dlBusyIds, dlErrsById, dlOptimisticIds, snapshotKey }) {
  const snap = job[snapshotKey || "warehouse_sent_snapshot"] || [];
  const isDownload = mode === "download";
  const isCetak = mode === "cetak";
  const isUnduhMockup = mode === "unduh-mockup";
  const titleAction = isCetak ? "Cetak"
    : (isUnduhMockup ? "Unduh"
      : (isDownload ? "Unduh" : "Pratinjau"));
  const dlMap = downloadsMapOf(job);
  const optHas = (fid) => !!(dlOptimisticIds && dlOptimisticIds.has && dlOptimisticIds.has(fid));
  return <div style={{position:"fixed", inset:0, background:"rgba(15,23,42,.55)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:12}}
    onClick={e=>{ if (e.target === e.currentTarget) onClose(); }}
    data-testid={`wh-shopee-picker-${mode}`}>
    <div className="card shopee-theme" style={{maxWidth:520, width:"100%", padding:16}}>
      <ShopeeThemeStyles/>
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12}}>
        <h3 style={{margin:0, fontSize:15, fontWeight:700}}>
          {titleAction} mockup — {job.order_number}
        </h3>
        <button className="soft-btn small-btn" onClick={onClose} data-testid="wh-shopee-picker-close">Tutup</button>
      </div>
      {isCetak && <div className="muted small" style={{marginBottom:8, fontSize:12}}>
        Pilih file yang akan dicetak. Setelah dipilih, dialog pilihan A5/A4 akan terbuka. Setiap file bisa dicetak terpisah; menandai dicetak dilakukan per pesanan.
      </div>}
      <div style={{display:"flex", flexDirection:"column", gap:6}}>
        {snap.map(s => {
          // Iter 374 — mode "cetak" → tombol opens CetakMockupSizeModal untuk file ini.
          if (isCetak) {
            return <button key={s.file_id} type="button"
              onClick={() => { if (onOpenCetakSize) { onOpenCetakSize(s.file_id, `${s.title || s.filename}`); onClose(); } }}
              className="soft-btn"
              style={{display:"flex", gap:8, alignItems:"center", padding:"8px 10px", borderColor:"var(--tf-neutral-border)", background:"var(--tf-surface)", cursor:"pointer", textAlign:"left"}}
              data-testid={`wh-shopee-picker-cetak-item-${s.file_id}`}>
              <Printer size={14} color="currentColor"/>
              <span style={{flex:1, minWidth:0, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis"}}>
                <b>{s.title || s.filename}</b>
                <span className="muted small" style={{marginLeft:6}}>v{s.version} · {s.filename}</span>
              </span>
              <span style={{fontSize:11, fontWeight:700, color:"var(--tf-neutral)"}}>Cetak</span>
            </button>;
          }
          // Iter 374 — mode "unduh-mockup" → pakai preview endpoint (barcode-stamped),
          //   tanpa processing-gate. Memisahkan Unduh Mockup dari claim status.
          if (isUnduhMockup) {
            return <button key={s.file_id} type="button"
              onClick={async () => {
                if (onUnduhMockup) { await onUnduhMockup(s.file_id); onClose(); }
              }}
              className="soft-btn"
              style={{display:"flex", gap:8, alignItems:"center", padding:"8px 10px", borderColor:"var(--tf-neutral-border)", background:"var(--tf-surface)", cursor:"pointer", textAlign:"left"}}
              data-testid={`wh-shopee-picker-unduh-mockup-item-${s.file_id}`}>
              <Download size={14} color="currentColor"/>
              <span style={{flex:1, minWidth:0, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis"}}>
                <b>{s.title || s.filename}</b>
                <span className="muted small" style={{marginLeft:6}}>v{s.version} · {s.filename}</span>
              </span>
              <span style={{fontSize:11, fontWeight:700, color:"var(--tf-neutral)"}}>Unduh</span>
            </button>;
          }
          // Iter 287k — Preview lewat modal in-app (fetch axios) supaya cookie auth ikut.
          if (!isDownload) {
            return <button key={s.file_id} type="button"
              onClick={() => onOpenPreview && onOpenPreview({
                file_id: s.file_id, filename: s.filename,
                title: s.title, content_type: s.content_type,
              })}
              className="soft-btn"
              style={{display:"flex", gap:8, alignItems:"center", padding:"8px 10px", borderColor:"var(--tf-neutral-border)", background:"var(--tf-surface)", cursor:"pointer", textAlign:"left"}}
              data-testid={`wh-shopee-picker-item-${s.file_id}`}>
              <ImgIcon size={14} color="currentColor"/>
              <span style={{flex:1, minWidth:0, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis"}}>
                <b>{s.title || s.filename}</b>
                <span className="muted small" style={{marginLeft:6}}>v{s.version} · {s.filename}</span>
              </span>
            </button>;
          }
          // Iter 287l — Unduh via helper terautentikasi + tampilkan status per file.
          const busy = dlBusyIds?.has?.(s.file_id) || false;
          const err = dlErrsById?.[s.file_id] || "";
          const rec = dlMap[s.file_id];
          const done = !!rec || optHas(s.file_id);
          let label = done ? "Unduh Ulang" : "Unduh";
          if (busy) label = "Mengunduh…";
          else if (err) label = "Coba Lagi";
          return <div key={s.file_id} style={{display:"flex", flexDirection:"column", gap:2}}>
            <button type="button"
              onClick={() => !busy && onDownloadFile && onDownloadFile(job, s)}
              disabled={busy}
              className="soft-btn"
              style={{display:"flex", gap:8, alignItems:"center", padding:"8px 10px",
                borderColor: err ? "var(--tf-danger-border)" : "var(--tf-neutral-border)",
                background: err ? "#fef2f2" : "#fff",
                color: err ? "var(--tf-danger)" : "var(--tf-neutral)",
                cursor: busy ? "progress" : "pointer",
                opacity: busy ? 0.75 : 1,
                textAlign:"left"}}
              data-testid={`wh-shopee-picker-item-${s.file_id}`}>
              <Download size={14}/>
              <span style={{flex:1, minWidth:0, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis"}}>
                <b>{s.title || s.filename}</b>
                <span className="muted small" style={{marginLeft:6}}>v{s.version} · {s.filename}</span>
              </span>
              <span style={{fontSize:11, fontWeight:700}}>{label}</span>
            </button>
            {done && !err && <div className="muted small" style={{fontSize:10, paddingLeft:10}} data-testid={`wh-shopee-picker-done-${s.file_id}`}>
              <span style={{background:"#ecfccb", color:"#3f6212", padding:"1px 5px", borderRadius:4, border:"1px solid #bef264", marginRight:6}}>Sudah Diunduh</span>
              {rec ? <>{fmtDateTime(rec.downloaded_at)} · {rec.downloaded_by_name || "-"}</> : <>Baru saja · pencatatan sedang tersinkron…</>}
            </div>}
            {err && <div className="warn-box small" style={{margin:"4px 0 0 10px", padding:"4px 8px", fontSize:11}} data-testid={`wh-shopee-picker-err-${s.file_id}`}>{err}</div>}
          </div>;
        })}
      </div>
      <div className="muted small" style={{marginTop:8}}>
        {isDownload
          ? "Unduhan menggunakan sesi login Anda. Browser tidak dapat memastikan file selesai tersimpan di perangkat."
          : "Klik nama file untuk membuka preview."}
      </div>
    </div>
  </div>;
}


// Iter 287k — Modal preview mockup Shopee dengan fetch terautentikasi (cookie operator ikut).
// Tidak membuka URL API terlindungi via window.open. Mendukung image; non-image tampil pesan.
function ShopeeImagePreviewModal({ file, onClose }) {
  const [blobUrl, setBlobUrl] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const isImg = (file.content_type || "").startsWith("image");
  useEffect(() => {
    let cancelled = false;
    let currentUrl = "";
    (async () => {
      try {
        const r = await axios.get(`${API}/shopee/files/${file.file_id}/preview`,
          { responseType: "blob", withCredentials: true });
        if (cancelled) return;
        currentUrl = URL.createObjectURL(r.data);
        setBlobUrl(currentUrl);
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        let msg = "";
        // Payload berupa Blob saat responseType="blob"; parse ke teks bila perlu.
        try {
          if (e?.response?.data instanceof Blob) {
            const t = await e.response.data.text();
            try { msg = JSON.parse(t)?.detail || t; } catch { msg = t; }
          } else {
            msg = e?.response?.data?.detail || "";
          }
        } catch { /* ignore */ }
        if (status === 401) setErr("Sesi Anda kedaluwarsa. Silakan login ulang sebagai operator Gudang lalu coba lagi.");
        else if (status === 403) setErr(msg || "Akses ditolak. Pastikan Anda login sebagai operator Gudang aktif.");
        else setErr(msg || "Gagal memuat preview.");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; if (currentUrl) URL.revokeObjectURL(currentUrl); };
  }, [file.file_id]);

  return <div style={{position:"fixed", inset:0, background:"rgba(15,23,42,.65)", zIndex:1100, display:"flex", alignItems:"center", justifyContent:"center", padding:12}}
    onClick={e=>{ if (e.target === e.currentTarget) onClose(); }}
    data-testid="wh-shopee-preview-modal">
    <div className="card shopee-theme" style={{maxWidth:960, width:"100%", maxHeight:"92vh", overflow:"hidden", padding:12, display:"flex", flexDirection:"column"}}>
      <ShopeeThemeStyles/>
      {file.stale && <StaleWarning testid="wh-shopee-preview-stale-msg"/>}
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", gap:8, marginBottom:10, flexWrap:"wrap"}}>
        <div style={{minWidth:0, flex:1}}>
          <div style={{fontWeight:700, fontSize:14, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis"}}>
            <span className="shopee-badge" style={{fontSize:10, marginRight:6}}>SHOPEE</span>
            {file.title || file.filename}
          </div>
          <div className="muted small" style={{fontSize:11, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis"}}>{file.filename}</div>
          {file.stale && <div style={{fontSize:12, fontWeight:800, color:"#b3261e"}} data-testid="wh-shopee-preview-stale">{STALE_FILE_LABEL}</div>}
        </div>
        <button className="soft-btn small-btn" onClick={onClose} data-testid="wh-shopee-preview-close"
          style={{color:"var(--tf-neutral)", borderColor:"var(--tf-neutral-border)"}}>Tutup</button>
      </div>
      <div style={{flex:1, minHeight:0, overflow:"auto", display:"flex", alignItems:"center", justifyContent:"center", background:"var(--tf-neutral-bg)", borderRadius:6, padding:8}}>
        {loading && <div className="muted small" data-testid="wh-shopee-preview-loading">Memuat pratinjau…</div>}
        {!loading && err && <div className="warn-box small" data-testid="wh-shopee-preview-error" style={{margin:0}}>{err}</div>}
        {!loading && !err && blobUrl && isImg && <img src={blobUrl} alt={file.title || file.filename}
          style={{maxWidth:"100%", maxHeight:"78vh", objectFit:"contain"}}
          data-testid="wh-shopee-preview-image"/>}
        {!loading && !err && blobUrl && !isImg && <div className="muted small" style={{padding:16, textAlign:"center"}} data-testid="wh-shopee-preview-nonimage">
          File ini bukan gambar ({file.filename}). Gunakan tombol Unduh setelah pesanan diproses.
        </div>}
      </div>
    </div>
  </div>;
}

